import { useState, useCallback, useEffect, useRef, useMemo, createContext, useContext } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import {
  ReactFlow,
  MiniMap,
  Controls,
  Background,
  useNodesState,
  useEdgesState,
  MarkerType,
  Handle,
  Position,
  BaseEdge,
  EdgeLabelRenderer,
  getSmoothStepPath,
  getNodesBounds,
  getViewportForBounds,
  NodeResizeControl,
  ConnectionMode,
  useConnection,
  useInternalNode,
  useReactFlow,
  useStoreApi,
} from '@xyflow/react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import ELK from 'elkjs/lib/elk.bundled.js';
import '@xyflow/react/dist/style.css';
import { ChevronLeft, AlertCircle, Trash2, Plus, Edit2, Check, FileDown, FileImage, ChevronDown, Loader, CheckCircle, Shield, X, Eye, EyeOff, Maximize2, Minimize2, ArrowUp, ArrowDown } from 'lucide-react';
import toast from 'react-hot-toast';
import axiosInstance from '../api/axios';
import SystemMultiSelect from './SystemMultiSelect';
import DocumentCatalogSelect from './DocumentCatalogSelect';
import { getRiskCategoryLabel } from '../constants/riskCategories';
import { toPng } from 'html-to-image';
import jsPDF from 'jspdf';
import { deepEqual } from 'fast-equals';
import * as yup from 'yup';
import { API_ENDPOINTS } from '../api/endpoints';
import { buildChangedPatch, normalizeFormForPatch } from '../request/risks/functional.js';
import { useUpdateRisk } from '../hooks/risks/useRisks.ts';
import { defaultRiskFormValues } from '../features/risks/components/RiskFormFields.tsx';
import { RISK_TYPE_OPTIONS } from '../features/risks/constants.ts';
import { riskManualEditSchema } from '../features/risks/schema.ts';
import { RISK_LEVEL_SELECT_OPTIONS } from '../features/risks/riskDraftSelectMaps.ts';
import { resolveDisplayOnlyRiskIdentifier, resolveMetaBarSystemRiskId, formatRiskSourceLabel } from '../features/risks/utils/riskDetailsUtils.ts';
import FlowchartCatalogRisksSidePanel from './flowchart/FlowchartCatalogRisksSidePanel';
import { useAuditYear } from '../contexts/AuditYearContext.tsx';
import FlowchartSubprocessLinksSidePanel from './flowchart/FlowchartSubprocessLinksSidePanel';
import FlowchartSubprocessLinkOverlay from './flowchart/FlowchartSubprocessLinkOverlay';
import { buildFlowchartSvg, downloadSvg } from '../features/flowchart/export/exportFlowchartSvg.ts';
import { NODE_HEIGHT, NODE_WIDTH, SWIMLANE_HEIGHT } from '../features/flowchart/flowchartDimensions.ts';
import FlowchartLegend from './flowchart/FlowchartLegend';
import { FlowchartNodeHoverCard } from './flowchart/FlowchartNodeHoverCard.jsx';
import { fetchFlowchartSubprocessLinks } from '../request/flowchart/subprocessLinks.api';
import { TENANT_PATHS } from '../routing/paths.ts';
import { soxProcessFlowchartPath } from '../routing/processNavigation.ts';
import { MODULE_KEYS } from '../constants/moduleAccess.ts';
import { useRequireModuleAccess } from '../hooks/useRequireModuleAccess.ts';
import ModuleAccessDeniedDialog from './shared/ModuleAccessDeniedDialog.tsx';
import { Button } from './common';
import {
  SOX_DASHBOARD_STATUS_OPTIONS,
  normalizeSoxDashboardProcessStatus,
} from './sox-dashboard/soxStatusSelectPresets';
import {
  getFloatingEdgeParamsForNodes,
  getOvalVisualRect,
} from '../features/flowchart/floatingEdgeGeometry.ts';
import { isConnectionAllowed } from '../features/flowchart/connectionRules.ts';
import { useFlowchartQueries } from '../hooks/flowchart/useFlowchartQueries';
import { useFlowchartMutations } from '../hooks/flowchart/useFlowchartMutations';
import { updateFlowchartSwimlaneLayout, updateFlowchartConnectorLayout } from '../request/flowchart/flowchart.api';
import { useFlowchartUndoController, reattachCanvasHandlers } from '../features/flowchart/undo';
import { resolve403ErrorMessage } from '../request/getApiErrorMessage.ts';
import { Tooltip, TooltipProvider } from '../admin/components/shared/Tooltip.tsx';
import { DeleteModal } from '../shared/common/index.ts';
import {
  queryKeys,
  SOX_CYCLES_QUERY_KEY,
  cycleProcessesQueryKey,
  processesQueryKey,
} from '../queryKeys.ts';

const SWIMLANE_MIN_HEIGHT = 120;
const SWIMLANE_MAX_HEIGHT = 800;
const SWIMLANE_LAYOUT_SAVE_DEBOUNCE_MS = 700;

// computeSwimlaneGeometry returns one entry per lane with its effective
// height (from the user-resized map, falling back to SWIMLANE_HEIGHT) and
// cumulative yStart/yEnd. Used by both the layout pass and the spawn-
// position helpers so every consumer sees the same lane bounds.
const computeSwimlaneGeometry = (dynamicSwimlanes, heightsByActor) => {
  let cursor = 0;
  return dynamicSwimlanes.map((swimlane) => {
    const raw = heightsByActor ? heightsByActor[swimlane.actor] : undefined;
    const height = Number.isFinite(raw) && raw > 0 ? raw : SWIMLANE_HEIGHT;
    const entry = {
      ...swimlane,
      height,
      yStart: cursor,
      yEnd: cursor + height,
    };
    cursor += height;
    return entry;
  });
};

// Merge the currently-present actors into the established lane display order:
// keep the prior order for actors that still exist, then append any brand-new
// actors at the end. This is the single source of truth for lane ordering, so a
// swimlane created from any entry point (header dropdown, add-node, edit-node)
// always appears last and stays there across re-layouts.
const buildLaneOrder = (presentActors, prevOrder) => {
  // Dedupe while preserving first-appearance order.
  const present = [...new Set((presentActors || []).filter((actor) => actor && actor.trim() !== ''))];
  const presentSet = new Set(present);
  const kept = (prevOrder || []).filter((actor) => presentSet.has(actor));
  const keptSet = new Set(kept);
  const appended = present.filter((actor) => !keptSet.has(actor));
  return [...kept, ...appended];
};

const CONNECTOR_LABEL_MAX_LENGTH = 50;
const SWIMLANE_NAME_MAX_LENGTH = 250;
const NODE_SPAWN_HORIZONTAL_STEP = NODE_WIDTH + 40;
const NODE_SPAWN_VERTICAL_PADDING = 24;
const SOX_STATUS_VALUES = new Set(SOX_DASHBOARD_STATUS_OPTIONS.map((option) => option.value));

// --- Easy-connect handles (SCRUM-460) -------------------------------------
// The full-node target handle invisibly covers the whole node so a connection
// can be dropped anywhere on it (drops are resolved geometrically by React
// Flow, so it never needs pointer events and cannot block the node's buttons).
// The source affordance is the center EasyConnectSource handle (see below).
// Keep easy-connect handles UNDER the node action buttons (risk badge, delete,
// mapped controls use z-40/z-50) so those stay clickable.
const EASY_CONNECT_HANDLE_Z_INDEX = 30;

const getEasyConnectTargetHandleStyle = (isConnectionTarget, bounds, borderRadius = '8px') => ({
  position: 'absolute',
  left: `${bounds?.x ?? 0}px`,
  top: `${bounds?.y ?? 0}px`,
  width: bounds ? `${bounds.width}px` : '100%',
  height: bounds ? `${bounds.height}px` : '100%',
  transform: 'none',
  // Faint highlight while a connection drag could end on this node.
  background: isConnectionTarget ? 'rgba(59, 130, 246, 0.08)' : 'transparent',
  border: isConnectionTarget ? '2px dashed rgba(59, 130, 246, 0.55)' : 'none',
  borderRadius,
  opacity: 1,
  // CRITICAL: while a connection drag is in progress this handle must receive
  // the pointer, so a drop ANYWHERE on the node completes the connection
  // (React Flow resolves the drop via elementFromPoint; with pointer-events
  // off it falls back to proximity, which only catches drops within
  // connectionRadius of the node CENTER — users' drops on the rest of the
  // node silently failed). Outside a drag it stays inert so it never blocks
  // node buttons, dragging, or double-click.
  pointerEvents: isConnectionTarget ? 'all' : 'none',
  zIndex: EASY_CONNECT_HANDLE_Z_INDEX - 1,
});

// Selector for useConnection: true while a connection drag from another node
// is in progress (i.e. this node is a potential drop target).
const makeConnectionTargetSelector = (nodeId) => (connection) =>
  connection.inProgress && connection.fromNode?.id !== nodeId;

// Delivers the waypoint-change handler to CustomEdge without threading it
// through edge.data (every setEdges path would otherwise have to remember to
// re-attach it — the version loader didn't, which silently disabled editing).
const WaypointActionsContext = createContext(null);

// Broadcasts the swimlane currently under a dragged node (and whether dropping
// there is valid) so each SwimlaneNode can highlight itself green (valid drop,
// fully inside the lane) or red (invalid — node is straddling a lane border).
// Value shape: { actorKey: string, status: 'valid' | 'invalid' } | null.
const SwimlaneDragHighlightContext = createContext(null);
const normalizeActorKey = (actor) => String(actor || '').trim().toLowerCase();

// --- Easy-connect: one big CENTER source handle (meeting 2026-06-26) -------
// The connect affordance is the node's whole CENTER: press anywhere in the
// middle and drag to another node to draw a connector. It is a single source
// Handle inset from the node's edges by EASY_CONNECT_EDGE_MARGIN, so the
// surviving border "ring" stays plain node body — grab the ring to MOVE the
// node, click it to SELECT. Double-click anywhere opens the node details.
//
// This replaces the four small edge dots users had to aim at (the central
// hover affordance/tooltip that used to block them is gone too). The handle
// has NO id, so the edge it produces is handle-less and routes as a clean
// floating connector (CustomEdge re-anchors both ends as nodes move). A
// connection can still be DROPPED anywhere on a node via the full-node target
// drop zone below.
// Fraction of the node occupied by the center connect zone. The rest of the
// node body stays a normal draggable surface, so grabbing anywhere OUTSIDE
// the center still MOVES the node (dragging is what triggers swimlane
// auto-extend, so the body must stay grabbable).
const EASY_CONNECT_ZONE_FRACTION = 0.55;
// Above the node body/text but below the corner action buttons (risk badge /
// delete / mapped-control chips use z-40/z-50) so those stay clickable.
const EASY_CONNECT_SOURCE_Z_INDEX = 33;

const getEasyConnectSourceStyle = (bounds) => {
  const rect = bounds || { x: 0, y: 0, width: NODE_WIDTH, height: NODE_HEIGHT };
  const w = rect.width * EASY_CONNECT_ZONE_FRACTION;
  const h = rect.height * EASY_CONNECT_ZONE_FRACTION;
  return {
    position: 'absolute',
    left: `${rect.x + (rect.width - w) / 2}px`,
    top: `${rect.y + (rect.height - h) / 2}px`,
    width: `${w}px`,
    height: `${h}px`,
    transform: 'none',
    minWidth: 0,
    minHeight: 0,
    background: 'transparent',
    border: 'none',
    borderRadius: bounds ? '9999px' : '8px',
    cursor: 'crosshair',
    zIndex: EASY_CONNECT_SOURCE_Z_INDEX,
  };
};

// The center source handle. No id => the produced edge stays handle-less and
// routes as a floating connector. `position` is cosmetic here (it only steers
// the drag-preview direction) because the absolute style controls placement.
// A plain CLICK on the zone (press + release without dragging) selects the
// node, so the center behaves like node body for selection too.
function EasyConnectSource({ nodeId, bounds }) {
  const store = useStoreApi();
  const onClick = useCallback((e) => {
    e.stopPropagation();
    const { addSelectedNodes } = store.getState();
    addSelectedNodes([nodeId]);
  }, [store, nodeId]);
  return (
    <Handle
      type="source"
      position={Position.Right}
      className="easy-connect-source"
      style={getEasyConnectSourceStyle(bounds)}
      onClick={onClick}
    />
  );
}

// A "+" badge that fades in on node hover to advertise the center connect zone.
// pointer-events:none so it never intercepts the drag started by the handle
// underneath it.
function ConnectHint({ bounds }) {
  const cx = bounds ? bounds.x + bounds.width / 2 : NODE_WIDTH / 2;
  const cy = bounds ? bounds.y + bounds.height / 2 : NODE_HEIGHT / 2;
  return (
    <div
      className="opacity-0 group-hover:opacity-100 transition-opacity duration-150"
      style={{
        position: 'absolute',
        left: `${cx}px`,
        top: `${cy}px`,
        transform: 'translate(-50%, -50%)',
        pointerEvents: 'none',
        zIndex: EASY_CONNECT_SOURCE_Z_INDEX + 1,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: 30,
        height: 30,
        borderRadius: '9999px',
        background: 'rgba(59, 130, 246, 0.14)',
        border: '1.5px solid #3b82f6',
        color: '#2563eb',
        boxShadow: '0 1px 3px rgba(0, 0, 0, 0.12)',
      }}
    >
      <Plus className="h-4 w-4" strokeWidth={3} />
    </div>
  );
}

// --- Connector waypoint geometry (Neha feedback: store/edit waypoints) -----
const wpDist = (a, b) => Math.hypot(b.x - a.x, b.y - a.y);
const wpNormalize = (dx, dy) => {
  const len = Math.hypot(dx, dy) || 1;
  return { x: dx / len, y: dy / len };
};
// Build a smooth path through an ordered list of points, rounding each interior
// corner. `points` is [source, ...waypoints, target] in flow coordinates.
const buildWaypointPath = (points, radius = 10) => {
  if (!Array.isArray(points) || points.length < 2) return '';
  if (points.length === 2) return `M ${points[0].x},${points[0].y} L ${points[1].x},${points[1].y}`;
  let d = `M ${points[0].x},${points[0].y}`;
  for (let i = 1; i < points.length - 1; i++) {
    const prev = points[i - 1];
    const curr = points[i];
    const next = points[i + 1];
    const r = Math.min(radius, wpDist(prev, curr) / 2, wpDist(curr, next) / 2);
    const v1 = wpNormalize(curr.x - prev.x, curr.y - prev.y);
    const v2 = wpNormalize(next.x - curr.x, next.y - curr.y);
    const p1 = { x: curr.x - v1.x * r, y: curr.y - v1.y * r };
    const p2 = { x: curr.x + v2.x * r, y: curr.y + v2.y * r };
    d += ` L ${p1.x},${p1.y} Q ${curr.x},${curr.y} ${p2.x},${p2.y}`;
  }
  const last = points[points.length - 1];
  d += ` L ${last.x},${last.y}`;
  return d;
};
// Index in `waypoints` at which to insert a new point clicked at `click`
// (flow coords), chosen by the closest segment of [source,...waypoints,target].
const waypointInsertIndex = (source, waypoints, target, click) => {
  const pts = [source, ...waypoints, target];
  let best = 0;
  let bestDist = Infinity;
  for (let i = 0; i < pts.length - 1; i++) {
    const mid = { x: (pts[i].x + pts[i + 1].x) / 2, y: (pts[i].y + pts[i + 1].y) / 2 };
    const d = wpDist(mid, click);
    if (d < bestDist) {
      bestDist = d;
      best = i; // insert after segment-start index i => waypoints index i
    }
  }
  return best;
};

const doesNodeOverlap = (candidateRect, nodeRect) => {
  return !(
    candidateRect.x + candidateRect.width <= nodeRect.x ||
    candidateRect.x >= nodeRect.x + nodeRect.width ||
    candidateRect.y + candidateRect.height <= nodeRect.y ||
    candidateRect.y >= nodeRect.y + nodeRect.height
  );
};

const getAvailableNodeSpawnPosition = ({ desiredPosition, existingNodes, constrainToSwimlane }) => {
  const processNodes = existingNodes.filter((node) => node.type !== 'swimlane');
  if (processNodes.length === 0) {
    return desiredPosition;
  }

  const targetY = typeof constrainToSwimlane?.y === 'number' ? constrainToSwimlane.y : desiredPosition.y;
  const candidateY = Math.max(0, targetY);
  const verticalMin = candidateY - NODE_SPAWN_VERTICAL_PADDING;
  const verticalMax = candidateY + NODE_HEIGHT + NODE_SPAWN_VERTICAL_PADDING;

  const collidesAt = (x) => {
    const candidateRect = { x, y: candidateY, width: NODE_WIDTH, height: NODE_HEIGHT };
    return processNodes.some((node) => {
      const nodeX = Number.isFinite(node.position?.x) ? node.position.x : 0;
      const nodeY = Number.isFinite(node.position?.y) ? node.position.y : 0;
      const nodeRect = { x: nodeX, y: nodeY, width: NODE_WIDTH, height: NODE_HEIGHT };

      if (nodeRect.y > verticalMax || nodeRect.y + nodeRect.height < verticalMin) {
        return false;
      }

      return doesNodeOverlap(candidateRect, nodeRect);
    });
  };

  const normalizedDesiredX = Math.max(0, desiredPosition.x);
  if (!collidesAt(normalizedDesiredX)) {
    return { x: normalizedDesiredX, y: candidateY };
  }

  for (let step = 1; step <= 80; step += 1) {
    const rightX = normalizedDesiredX + (step * NODE_SPAWN_HORIZONTAL_STEP);
    if (!collidesAt(rightX)) {
      return { x: rightX, y: candidateY };
    }

    const leftX = Math.max(0, normalizedDesiredX - (step * NODE_SPAWN_HORIZONTAL_STEP));
    if (!collidesAt(leftX)) {
      return { x: leftX, y: candidateY };
    }
  }

  return { x: normalizedDesiredX, y: candidateY };
};

const NODE_DETAILS_SCHEMA = yup.object({
  nodeType: yup
    .string()
    .max(100, 'Node type cannot be more than 100 characters')
    .oneOf(['start', 'process', 'decision', 'end'], 'Invalid node type')
    .required('Node type is required'),
  nodeDescription: yup
    .string()
    .trim()
    .max(500, 'Description cannot be more than 500 characters')
    .required('Description is required'),
  actor: yup
    .string()
    .trim()
    .max(SWIMLANE_NAME_MAX_LENGTH, `Actor cannot be more than ${SWIMLANE_NAME_MAX_LENGTH} characters`)
    .required('Actor is required'),
  system: yup
    .string()
    .trim()
    .max(100, 'System cannot be more than 100 characters')
    .optional(),
  soxControl: yup.string().nullable(),
  document: yup.string().nullable(),
  keyControl: yup.boolean().nullable(),
});

const normalizeNodeEditField = (value) => String(value ?? '').trim();

const normalizeNodeSystemField = (value) => {
  const parts = String(value ?? '')
    .split(/[,/]/)
    .map((part) => part.trim())
    .filter(Boolean);
  return [...new Set(parts)].sort().join(', ');
};

const reactFlowTypeFromNodeType = (nodeType) => {
  if (nodeType === 'decision') return 'decision';
  if (nodeType === 'start' || nodeType === 'end') return 'oval';
  return 'rectangle';
};

const nodeEditFormMatchesNode = (form, node) => {
  if (!form || !node) return false;
  const nodeData = node.data || {};
  const formNodeType = normalizeNodeEditField(form.nodeType || 'process');
  const existingNodeType = normalizeNodeEditField(nodeData.nodeType || 'process');
  // Compare against version-scoped systems[] (same source as the form seed),
  // not shared flowchart_nodes.system text — that text can already match after
  // another version's edit and would incorrectly skip save.
  const existingSystemsText = systemTextForEditForm(nodeData.systems);

  return (
    formNodeType === existingNodeType &&
    normalizeNodeEditField(form.nodeDescription) === normalizeNodeEditField(nodeData.nodeDescription) &&
    normalizeNodeEditField(form.actor) === normalizeNodeEditField(nodeData.actor) &&
    normalizeNodeSystemField(form.system) === normalizeNodeSystemField(existingSystemsText) &&
    normalizeNodeEditField(form.soxControl) === normalizeNodeEditField(nodeData.soxControl) &&
    documentsForCompare(form.documents) === documentsForCompare(nodeData.documents) &&
    Boolean(form.keyControl) === Boolean(nodeData.keyControl) &&
    node.type === reactFlowTypeFromNodeType(formNodeType)
  );
};

const closeNodeEditModal = (setters) => {
  setters.setIsEditModalOpen(false);
  setters.setEditingNode(null);
  setters.setIsAddingNode(false);
  setters.setIsAddingNewActor(false);
  setters.setNodeFormErrors({});
};

const generateConnectorId = () => `connector_${Date.now()}`;
const getEdgeMarkers = (isBidirectional = false) => {
  const marker = {
    type: MarkerType.ArrowClosed,
    width: 20,
    height: 20,
    color: '#000000',
  };

  return {
    markerEnd: marker,
    markerStart: isBidirectional ? marker : undefined,
  };
};

const sanitizeFlowchartForSave = (flowchartData) => ({
  ...flowchartData,
  nodes: (flowchartData?.nodes || []).map((node) => {
    const { x, y, measured, selected, dragging, ...rest } = node;
    return {
      ...rest,
      // Backend requires strict booleans for these fields.
      keyControl: rest.keyControl === true,
      keySystem: rest.keySystem === true,
    };
  }),
});

const UUID_LIKE_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const isUuidLike = (value) => UUID_LIKE_RE.test(String(value ?? '').trim());

const getRiskRecordId = (risk) => {
  // Prefer DB UUID fields only — never treat display RSK_… as the record id.
  for (const candidate of [risk?.id, risk?.riskId]) {
    const trimmed = String(candidate ?? '').trim();
    if (trimmed && isUuidLike(trimmed)) return trimmed;
  }
  return null;
};

const pickDisplayRiskCode = (risk) => {
  if (!risk || typeof risk !== 'object') return '';
  const clientRef = String(risk.clientRiskId ?? risk.client_risk_id ?? '').trim();
  if (clientRef) return clientRef;
  const snake = String(risk.risk_id ?? '').trim();
  if (snake && !isUuidLike(snake)) return snake;
  const camel = String(risk.riskId ?? '').trim();
  if (camel && !isUuidLike(camel)) return camel;
  return '';
};

const mergeRiskEntryPreserveDisplay = (existing, incoming) => {
  const recordId = getRiskRecordId(incoming) ?? getRiskRecordId(existing);
  const displayCode = pickDisplayRiskCode(existing) || pickDisplayRiskCode(incoming);
  const clientRef = String(
    existing?.clientRiskId ?? existing?.client_risk_id ?? incoming?.clientRiskId ?? incoming?.client_risk_id ?? ''
  ).trim();
  const merged = {
    ...existing,
    ...incoming,
    nodeId: incoming.nodeId ?? existing.nodeId,
  };
  if (recordId) {
    merged.id = recordId;
    merged.riskId = recordId;
  }
  if (displayCode) {
    merged.risk_id = displayCode;
  }
  if (clientRef) {
    merged.clientRiskId = clientRef;
    merged.client_risk_id = clientRef;
  }
  return merged;
};

/** Compare risk lists for undo restore / node sync (ignore order-stable JSON). */
const identifiedRisksListsEqual = (a, b) => {
  try {
    return JSON.stringify(a ?? []) === JSON.stringify(b ?? []);
  } catch {
    return false;
  }
};

/** Keep prior node risks when identify-risks runs again; update by node + record id. */
const mergeRisksByRecordId = (existingRisks = [], incomingRisks = []) => {
  const merged = Array.isArray(existingRisks) ? [...existingRisks] : [];
  if (!Array.isArray(incomingRisks)) return merged;

  incomingRisks.forEach((risk) => {
    if (!risk || typeof risk !== 'object') return;
    const recordId = getRiskRecordId(risk);
    const nodeId = risk.nodeId;
    if (recordId) {
      const existingIndex = merged.findIndex((entry) => (
        getRiskRecordId(entry) === recordId
        && (nodeId == null || entry?.nodeId == null || entry.nodeId === nodeId)
      ));
      if (existingIndex >= 0) {
        merged[existingIndex] = mergeRiskEntryPreserveDisplay(merged[existingIndex], risk);
        return;
      }
    }
    merged.push({
      ...risk,
      nodeId: risk.nodeId,
    });
  });

  return merged;
};

const normalizeRiskForFlowchartSave = (risk) => {
  if (!risk || typeof risk !== 'object') return risk;
  const recordId = getRiskRecordId(risk);
  const action = String(risk?.action || '').trim();
  const displayCode = pickDisplayRiskCode(risk);
  const next = { ...risk };
  if (recordId) {
    next.id = recordId;
    next.riskId = recordId;
  }
  if (displayCode) next.risk_id = displayCode;
  if (action) next.action = action;
  return next;
};

const getRiskMappingRecordId = (riskOrMapping) => {
  const trimmed = (getRiskRecordId(riskOrMapping) ?? '').trim();
  return trimmed.length > 0 ? trimmed : null;
};

const getRiskDisplayIdentifier = (risk) => {
  const trimmed = resolveDisplayOnlyRiskIdentifier(risk).trim();
  return trimmed.length > 0 ? trimmed : null;
};

const getRiskMappingKey = (nodeId, riskOrMapping) => {
  if (!nodeId) return null;
  const riskIdentifier = getRiskMappingRecordId(riskOrMapping);
  if (riskIdentifier == null) return null;
  return `${nodeId}:${riskIdentifier}`;
};

const getControlDisplayLabel = (control) => {
  if (!control || typeof control !== 'object') return '';
  const clientId = String(control.client_control_id ?? control.clientControlId ?? '').trim();
  if (clientId) return clientId;
  return String(control.control_id ?? control.controlId ?? '').trim();
};

const isKeyControlSignificance = (control) => {
  if (!control || typeof control !== 'object' || control.__legacyLabel) return false;
  return String(control.significance ?? '').trim().toLowerCase() === 'key';
};

const collectControlsFromNodeRisks = (risks) => {
  if (!Array.isArray(risks) || risks.length === 0) return [];
  const seen = new Set();
  const controls = [];
  risks.forEach((risk) => {
    const riskControls = risk?.controls;
    if (!Array.isArray(riskControls)) return;
    riskControls.forEach((control) => {
      const dedupeKey = String(control?.id ?? control?.control_id ?? control?.controlId ?? getControlDisplayLabel(control) ?? '').trim();
      if (!dedupeKey || seen.has(dedupeKey)) return;
      seen.add(dedupeKey);
      controls.push(control);
    });
  });
  return controls;
};

/** Risks on a node that include the given attached control (for hexagon popup). */
const findSourceRisksForControl = (risks, control) => {
  if (!Array.isArray(risks) || !control || typeof control !== 'object') return [];
  const controlId = String(control.id ?? '').trim();
  const clientId = String(control.client_control_id ?? control.clientControlId ?? '').trim();
  const ctlId = String(control.control_id ?? control.controlId ?? '').trim();
  return risks.filter((risk) => {
    if (!Array.isArray(risk?.controls)) return false;
    return risk.controls.some((entry) => {
      if (!entry || typeof entry !== 'object') return false;
      const entryId = String(entry.id ?? '').trim();
      const entryClientId = String(entry.client_control_id ?? entry.clientControlId ?? '').trim();
      const entryCtlId = String(entry.control_id ?? entry.controlId ?? '').trim();
      if (controlId && entryId && controlId === entryId) return true;
      if (clientId && entryClientId && clientId === entryClientId) return true;
      if (ctlId && entryCtlId && ctlId === entryCtlId) return true;
      return false;
    });
  });
};

const sortObjectKeysDeep = (value) => {
  if (Array.isArray(value)) {
    return value.map(sortObjectKeysDeep);
  }
  if (value && typeof value === 'object') {
    return Object.keys(value)
      .sort()
      .reduce((acc, key) => {
        acc[key] = sortObjectKeysDeep(value[key]);
        return acc;
      }, {});
  }
  return value;
};

const canonicalJsonString = (value) => JSON.stringify(sortObjectKeysDeep(value));

const normalizeRiskForChangeDetection = (risk) => {
  if (!risk || typeof risk !== 'object') return risk;
  const { nodeId, ...rest } = risk;
  return rest;
};

const normalizeRiskLevelValue = (risk) => {
  const rawLevel = String(risk?.riskLevel ?? risk?.risk_level ?? '').trim().toLowerCase();
  if (rawLevel === 'high') return 'High';
  if (rawLevel === 'medium') return 'Medium';
  if (rawLevel === 'low') return 'Low';
  return '';
};

const buildIdentifiedRiskFormValues = (risk) => ({
  ...defaultRiskFormValues,
  clientRiskId: String(risk?.clientRiskId ?? risk?.client_risk_id ?? ''),
  riskDescription: String(risk?.riskDescription ?? risk?.risk_description ?? ''),
  riskCategory: String(risk?.riskCategory ?? risk?.risk_category ?? 'SOX'),
  riskLevel: (risk?.riskLevel ?? risk?.risk_level ?? 'Medium') || 'Medium',
});

const mergeIdentifiedRiskRecord = (risk, patch, responseData) => ({
  ...(risk || {}),
  ...(responseData && typeof responseData === 'object' ? responseData : {}),
  ...(patch?.clientRiskId !== undefined ? { clientRiskId: patch.clientRiskId, client_risk_id: patch.clientRiskId } : {}),
  ...(patch?.riskDescription !== undefined
    ? { riskDescription: patch.riskDescription, risk_description: patch.riskDescription }
    : {}),
  ...(patch?.riskCategory !== undefined
    ? { riskCategory: patch.riskCategory, risk_category: patch.riskCategory }
    : {}),
  ...(patch?.riskLevel !== undefined ? { riskLevel: patch.riskLevel, risk_level: patch.riskLevel } : {}),
});

const RISK_CARD_STYLES = {
  High: { card: 'border-red-300 bg-red-50', badge: 'bg-red-600 text-white' },
  Medium: { card: 'border-amber-300 bg-amber-50', badge: 'bg-amber-500 text-white' },
  Low: { card: 'border-green-300 bg-green-50', badge: 'bg-green-600 text-white' },
};

function IdentifiedRiskEditableCard({ risk, onUpdateRisk, onDeleteRisk, isSaving }) {
  const riskRecordId = getRiskRecordId(risk);
  const displayRiskId = getRiskDisplayIdentifier(risk);
  const riskDetailsRiskId = resolveMetaBarSystemRiskId(risk);
  const riskDetailsPath = riskDetailsRiskId
    ? TENANT_PATHS.SOX_RISKS_VIEW.replace(':riskId', encodeURIComponent(riskDetailsRiskId))
    : null;
  const canEdit = !!riskRecordId;
  const sourceLabel = formatRiskSourceLabel(risk?.source);
  const initialValues = useMemo(() => buildIdentifiedRiskFormValues(risk), [risk]);
  const [isEditing, setIsEditing] = useState(false);
  const {
    register,
    handleSubmit,
    reset,
    watch,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(riskManualEditSchema),
    defaultValues: initialValues,
    mode: 'onSubmit',
  });

  useEffect(() => {
    reset(initialValues);
  }, [initialValues, reset]);

  const currentLevel = (typeof watch === 'function' ? watch('riskLevel') : undefined) || initialValues.riskLevel;
  const levelStyles = RISK_CARD_STYLES[currentLevel] || {
    card: 'border-gray-200 bg-white',
    badge: 'bg-gray-200 text-gray-700',
  };

  const handleCancel = () => {
    reset(initialValues);
    setIsEditing(false);
  };

  const handleDelete = useCallback(async () => {
    if (typeof onDeleteRisk === 'function') {
      await onDeleteRisk(risk);
    }
  }, [onDeleteRisk, risk]);

  const submit = async (values) => {
    try {
      await onUpdateRisk(risk, values);
      setIsEditing(false);
    } catch {
      // Stay in edit mode; error toast handled by mutation hook
    }
  };

  if (!isEditing) {
    return (
      <div className={`border rounded-lg p-4 ${levelStyles.card}`}>
        <div className="mb-3 flex items-start justify-between gap-2">
          <div className="flex min-w-0 flex-1 flex-wrap items-center gap-1.5">
            {riskDetailsPath ? (
              <Link
                to={riskDetailsPath}
                className="shrink-0 text-xs font-semibold text-blue-700 hover:text-blue-800 hover:underline"
                title="View risk details"
              >
                {displayRiskId}
              </Link>
            ) : (
              <span className="shrink-0 text-xs text-gray-500">—</span>
            )}
            <span className={`shrink-0 px-2 py-1 rounded text-xs font-semibold ${levelStyles.badge}`}>
              {initialValues.riskLevel} Risk
            </span>
            <span className="shrink-0 text-xs text-gray-600 bg-gray-100 px-2 py-1 rounded">
              {getRiskCategoryLabel(initialValues.riskCategory)}
            </span>
            {sourceLabel ? (
              <span className="inline-flex shrink-0 items-center px-2 py-1 text-xs font-semibold text-blue-700 bg-blue-50 border border-blue-100 rounded">
                {sourceLabel}
              </span>
            ) : null}
          </div>
          {canEdit ? (
            <div className="flex shrink-0 items-center gap-1">
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => setIsEditing(true)}
                className="h-8 w-8 p-0 text-gray-600 hover:bg-white/70 hover:text-gray-900"
                aria-label="Edit risk"
                title="Edit risk"
                leftIcon={<Edit2 className="h-4 w-4 shrink-0" />}
              />
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={handleDelete}
                className="h-8 w-8 p-0 text-red-600 hover:bg-red-50 hover:text-red-700"
                aria-label="Delete risk"
                title="Delete risk"
                leftIcon={<Trash2 className="h-4 w-4 shrink-0" />}
              />
            </div>
          ) : (
            <span className="w-6 shrink-0" aria-hidden="true" />
          )}
        </div>
        <p className="text-sm text-gray-800 whitespace-pre-wrap break-words">
          {initialValues.riskDescription || '—'}
        </p>
      </div>
    );
  }

  return (
    <form
      onSubmit={handleSubmit(submit)}
      className={`border rounded-lg p-4 space-y-4 ${levelStyles.card}`}
    >
      {!canEdit ? (
        <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
          This risk cannot be updated because it does not have a stable identifier.
        </div>
      ) : null}

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <div>
          <label className="block text-xs font-semibold uppercase tracking-wide text-gray-500 mb-1">
            Risk ID
          </label>
          {riskDetailsPath ? (
            <Link
              to={riskDetailsPath}
              className="inline-flex items-center min-h-[38px] text-sm font-semibold text-blue-700 hover:text-blue-800 hover:underline"
              title="View risk details"
            >
              {displayRiskId}
            </Link>
          ) : (
            <p className="min-h-[38px] flex items-center text-sm text-gray-700">{displayRiskId || '—'}</p>
          )}
        </div>

        <div>
          <label className="block text-xs font-semibold uppercase tracking-wide text-gray-500 mb-1">
            Risk Level
          </label>
          <select
            className={`w-full rounded-lg border px-3 py-2 text-sm bg-white outline-none focus:ring-2 focus:border-transparent ${errors.riskLevel ? 'border-red-500 focus:ring-red-500' : 'border-gray-200 focus:ring-blue-500'}`}
            aria-invalid={!!errors.riskLevel}
            disabled={isSaving || !canEdit}
            {...register('riskLevel')}
          >
            {RISK_LEVEL_SELECT_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
          {errors.riskLevel ? <p className="mt-1 text-sm text-red-600">{errors.riskLevel.message}</p> : null}
        </div>

        <div>
          <label className="block text-xs font-semibold uppercase tracking-wide text-gray-500 mb-1">
            Risk Category
          </label>
          <select
            className={`w-full rounded-lg border px-3 py-2 text-sm bg-white outline-none focus:ring-2 focus:border-transparent ${errors.riskCategory ? 'border-red-500 focus:ring-red-500' : 'border-gray-200 focus:ring-blue-500'}`}
            aria-invalid={!!errors.riskCategory}
            disabled={isSaving || !canEdit}
            {...register('riskCategory')}
          >
            {RISK_TYPE_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
          {errors.riskCategory ? <p className="mt-1 text-sm text-red-600">{errors.riskCategory.message}</p> : null}
        </div>
      </div>

      <div>
        <label className="block text-xs font-semibold uppercase tracking-wide text-gray-500 mb-1">
          Description
        </label>
        <textarea
          rows={4}
          placeholder="Describe the identified risk"
          className={`w-full rounded-lg border px-3 py-2 text-sm bg-white outline-none focus:ring-2 focus:border-transparent resize-y ${errors.riskDescription ? 'border-red-500 focus:ring-red-500' : 'border-gray-200 focus:ring-blue-500'}`}
          aria-invalid={!!errors.riskDescription}
          disabled={isSaving || !canEdit}
          {...register('riskDescription')}
        />
        {errors.riskDescription ? <p className="mt-1 text-sm text-red-600">{errors.riskDescription.message}</p> : null}
      </div>

      <div className="flex items-center justify-end gap-2">
        <button
          type="button"
          onClick={handleCancel}
          disabled={isSaving}
          className="px-4 py-2 rounded-lg border border-gray-200 bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={isSaving || !canEdit}
          className="px-4 py-2 rounded-lg bg-blue-600 text-white text-sm font-medium hover:bg-blue-700 disabled:opacity-50"
        >
          {isSaving ? 'Updating...' : 'Update Risk'}
        </button>
      </div>
    </form>
  );
}

const normalizeConnectorForChangeDetection = (connector) => ({
  source: connector?.source || '',
  target: connector?.target || '',
  label: connector?.label || '',
  isBidirectional: connector?.isBidirectional === true,
});

// Ignore node coordinates when deciding whether to call update-flowchart.
const stripNodePositionsForChangeDetection = (flowchartData) => ({
  ...(flowchartData || {}),
  nodes: (flowchartData?.nodes || [])
    .map((node) => {
      const { position, ...rest } = node || {};
      const risks = Array.isArray(rest.risks)
        ? [...rest.risks]
          .map((risk) => normalizeRiskForChangeDetection(risk))
          .sort((a, b) => canonicalJsonString(a).localeCompare(canonicalJsonString(b)))
        : rest.risks;
      const hasComparableRisks = Array.isArray(risks) ? risks.length > 0 : Boolean(risks);
      return {
        ...rest,
        ...(hasComparableRisks ? { risks } : {}),
      };
    })
    .sort((a, b) => String(a?.id || '').localeCompare(String(b?.id || ''))),
  connectors: (flowchartData?.connectors || [])
    .map((connector) => normalizeConnectorForChangeDetection(connector))
    .sort((a, b) => canonicalJsonString(a).localeCompare(canonicalJsonString(b))),
});

// True when a hydrate/refresh payload is incomplete (must not wipe the live canvas).
const isIncompleteFlowchartPayload = (data) =>
  !data || !Array.isArray(data.nodes) || !Array.isArray(data.connectors);

// ELK layout with swimlane constraints
const getLayoutedElements = async (nodes, edges, sourceNodeMap, swimlaneHeights, laneOrder) => {
  const elk = new ELK();
  // Coerce IDs — remote payloads sometimes mix string/number; strict Set.has drops all edges.
  const nodeIdSet = new Set((nodes || []).map((node) => String(node.id)));
  const validEdges = (edges || []).filter(
    (edge) => nodeIdSet.has(String(edge?.source)) && nodeIdSet.has(String(edge?.target))
  );
  if ((edges || []).length > 0 && validEdges.length === 0) {
    console.warn('[connector-wipe-debug] elkFilter', {
      path: 'elkFilter',
      edgesIn: (edges || []).length,
      validEdgesOut: validEdges.length,
      nodeIdsSample: [...nodeIdSet].slice(0, 8),
      droppedSample: (edges || []).slice(0, 5).map((e) => ({
        id: e?.id,
        source: e?.source,
        target: e?.target,
        sourceInSet: nodeIdSet.has(String(e?.source)),
        targetInSet: nodeIdSet.has(String(e?.target)),
      })),
    });
  } else {
    console.log('[connector-wipe-debug] elkFilter', {
      path: 'elkFilter',
      edgesIn: (edges || []).length,
      validEdgesOut: validEdges.length,
    });
  }

  // Dynamically create swimlanes from unique actors. Geometry (height,
  // yStart, yEnd) is baked in once via computeSwimlaneGeometry so the
  // layout pass and the materialization downstream share the same map.
  //
  // Lane order: when an explicit laneOrder is supplied (the persisted display
  // order), lanes follow it and any actor not yet in that order is appended at
  // the end. This guarantees a newly-created swimlane always lands last —
  // regardless of where its node happens to sit in the node array — instead of
  // the order being re-derived from node position on every layout.
  const presentActors = [...new Set(nodes.map(n => n.data.actor).filter(a => a && a.trim() !== ''))];
  let uniqueActors;
  if (Array.isArray(laneOrder) && laneOrder.length > 0) {
    const presentSet = new Set(presentActors);
    const ordered = laneOrder.filter((actor) => presentSet.has(actor));
    const orderedSet = new Set(ordered);
    const appended = presentActors.filter((actor) => !orderedSet.has(actor));
    uniqueActors = [...ordered, ...appended];
  } else {
    uniqueActors = presentActors;
  }
  const baseSwimlanes = uniqueActors.map((actor, index) => ({
    id: `swimlane-${index}`,
    label: actor,
    actor: actor
  }));
  const dynamicSwimlanes = computeSwimlaneGeometry(baseSwimlanes, swimlaneHeights);


  // Helper to find swimlane for an actor
  const getDynamicSwimlane = (actor) => {
    if (!actor || actor.trim() === '') return null;
    return dynamicSwimlanes.find(s => s.actor === actor);
  };

  // Group nodes by swimlane (exclude start/end nodes)
  const swimlaneGroups = {};
  const unmappedNodes = [];
  const startEndNodes = [];

  nodes.forEach((node) => {
    // Skip start/end nodes - they don't belong to swimlanes
    if (node.data.nodeType === 'start' || node.data.nodeType === 'end') {
      startEndNodes.push(node);
      return;
    }

    const swimlane = getDynamicSwimlane(node.data.actor);
    if (swimlane) {
      if (!swimlaneGroups[swimlane.id]) {
        swimlaneGroups[swimlane.id] = [];
      }
      swimlaneGroups[swimlane.id].push({ ...node, swimlaneId: swimlane.id, isMapped: true });
    } else {
      unmappedNodes.push(node);
    }
  });


  // Assign unmapped nodes (nodes with empty actor) to source node's swimlane
  unmappedNodes.forEach((node) => {
    const sourceEdge = validEdges.find(e => e.target === node.id);
    if (sourceEdge) {
      const sourceNode = nodes.find(n => n.id === sourceEdge.source);
      const sourceSwimlane = getDynamicSwimlane(sourceNode?.data?.actor);
      if (sourceSwimlane) {
        if (!swimlaneGroups[sourceSwimlane.id]) {
          swimlaneGroups[sourceSwimlane.id] = [];
        }
        swimlaneGroups[sourceSwimlane.id].push({
          ...node,
          swimlaneId: sourceSwimlane.id,
          isMapped: false
        });
      }
    }
  });

  // Create ELK graph with swimlane partitions
  const elkNodes = [];
  const elkEdges = validEdges.map(edge => ({
    id: edge.id,
    sources: [edge.source],
    targets: [edge.target]
  }));

  dynamicSwimlanes.forEach((swimlane, index) => {
    const swimlaneNodes = swimlaneGroups[swimlane.id] || [];
    swimlaneNodes.forEach(node => {
      elkNodes.push({
        id: node.id,
        width: NODE_WIDTH,
        height: NODE_HEIGHT,
        properties: {
          swimlaneId: swimlane.id,
          swimlaneIndex: index,
          isMapped: node.isMapped
        }
      });
    });
  });

  // Add start/end nodes to ELK graph, positioned in same swimlane as connected nodes
  startEndNodes.forEach(node => {
    let swimlaneIndex = 0;
    let swimlaneId = dynamicSwimlanes[0]?.id || 'default';

    // For start node, use the swimlane of the target node
    if (node.data.nodeType === 'start') {
      const outgoingEdge = validEdges.find(e => e.source === node.id);
      if (outgoingEdge) {
        const targetNode = nodes.find(n => n.id === outgoingEdge.target);
        if (targetNode) {
          const targetSwimlane = getDynamicSwimlane(targetNode.data.actor);
          if (targetSwimlane) {
            swimlaneId = targetSwimlane.id;
            swimlaneIndex = dynamicSwimlanes.findIndex(s => s.id === swimlaneId);
          }
        }
      }
    }
    // For end node, use the swimlane of the source node
    else if (node.data.nodeType === 'end') {
      const incomingEdge = validEdges.find(e => e.target === node.id);
      if (incomingEdge) {
        const sourceNode = nodes.find(n => n.id === incomingEdge.source);
        if (sourceNode) {
          const sourceSwimlane = getDynamicSwimlane(sourceNode.data.actor);
          if (sourceSwimlane) {
            swimlaneId = sourceSwimlane.id;
            swimlaneIndex = dynamicSwimlanes.findIndex(s => s.id === swimlaneId);
          }
        }
      }
    }

    elkNodes.push({
      id: node.id,
      width: NODE_WIDTH,
      height: NODE_HEIGHT,
      properties: {
        swimlaneId: swimlaneId,
        swimlaneIndex: swimlaneIndex,
        isMapped: false
      }
    });
  });

  // Ensure all remaining nodes are added to ELK graph
  const addedNodeIds = new Set(elkNodes.map(n => n.id));
  nodes.forEach(node => {
    if (!addedNodeIds.has(node.id)) {
      // Add to first swimlane as fallback
      elkNodes.push({
        id: node.id,
        width: NODE_WIDTH,
        height: NODE_HEIGHT,
        properties: {
          swimlaneId: dynamicSwimlanes[0]?.id || 'default',
          swimlaneIndex: 0,
          isMapped: false
        }
      });
    }
  });

  const graph = {
    id: 'root',
    layoutOptions: {
      'elk.algorithm': 'layered',
      'elk.direction': 'RIGHT',
      'elk.spacing.nodeNode': '120',  // Increased vertical spacing between nodes in same layer
      'elk.layered.spacing.nodeNodeBetweenLayers': '150',  // Increased horizontal spacing between layers
      'elk.layered.spacing.edgeNodeBetweenLayers': '80',  // Space between edges and nodes
      'elk.layered.crossingMinimization.strategy': 'LAYER_SWEEP',
      'elk.layered.nodePlacement.strategy': 'NETWORK_SIMPLEX',  // Better for handling branching
      'elk.layered.considerModelOrder.strategy': 'NODES_AND_EDGES'  // Respect node order for better layout
    },
    children: elkNodes,
    edges: elkEdges
  };

  const layoutedGraph = await elk.layout(graph);

  // Position nodes with swimlane constraints
  const layoutedNodes = nodes.map(node => {
    const elkNode = layoutedGraph.children.find(n => n.id === node.id);
    const fallbackPosition = node.position && typeof node.position.x === 'number' && typeof node.position.y === 'number'
      ? node.position
      : { x: 0, y: 0 };

    // Find which swimlane this node belongs to
    let swimlaneIndex = -1;
    let assignedSwimlaneId = null;

    for (let i = 0; i < dynamicSwimlanes.length; i++) {
      const swimlane = dynamicSwimlanes[i];
      if (swimlaneGroups[swimlane.id]?.some(n => n.id === node.id)) {
        swimlaneIndex = i;
        assignedSwimlaneId = swimlane.id;
        break;
      }
    }

    const isMapped = node.data.actor && node.data.actor.trim() !== '';

    const swimlane = swimlaneIndex >= 0 ? dynamicSwimlanes[swimlaneIndex] : null;
    return {
      ...node,
      position: {
        x: typeof elkNode?.x === 'number' ? elkNode.x : fallbackPosition.x,
        y: swimlane
          ? swimlane.yStart + (swimlane.height - NODE_HEIGHT) / 2
          : (typeof elkNode?.y === 'number' ? elkNode.y : fallbackPosition.y)
      },
      data: {
        ...node.data,
        isMapped,
        swimlaneId: assignedSwimlaneId
      }
    };
  });

  return { nodes: layoutedNodes, edges: validEdges, dynamicSwimlanes };
};

// Transform backend JSON to ReactFlow format
const transformDataToFlow = (data) => {
  if (isIncompleteFlowchartPayload(data)) {
    return { nodes: [], edges: [], savedRisks: [] };
  }

  // Collect all saved risks from nodes
  const savedRisks = [];

  const nodes = data.nodes.map((node) => {
    // Treat any non-empty actor as mapped because swimlanes are generated dynamically.
    const isMapped = !!(node.actor && node.actor.trim() !== '');
    const hasPersistedPosition = node.position && typeof node.position.x === 'number' && typeof node.position.y === 'number';
    // Use nodeType from JSON schema (process/decision/start/end)
    const nodeType = node.nodeType === 'decision' ? 'decision'
      : (node.nodeType === 'start' || node.nodeType === 'end') ? 'oval'
        : 'rectangle';

    // Collect risks from this node if present
    if (node.risks && Array.isArray(node.risks)) {
      // Ensure restored risks are always linked back to their node for click-to-view behavior.
      savedRisks.push(
        ...node.risks.map((risk) => ({
          ...risk,
          nodeId: risk?.nodeId || node.id,
        }))
      );
    }
    return {
      id: node.id,
      type: nodeType,
      data: {
        nodeDescription: node.nodeDescription,
        actor: node.actor,
        system: node.system,
        systems: Array.isArray(node.systems) ? node.systems : [],
        soxControl: node.soxControl || '',
        // Legacy text kept on node for save/identify-risks payloads only — UI uses documents[].
        document: node.document || '',
        documents: normalizeNodeDocuments(node.documents),
        nodeType: node.nodeType,
        keyControl: node.keyControl || false,
        isMapped,
        hasPersistedPosition,
        risks: node.risks || [],
      },
      position: node.position && typeof node.position.x === 'number' && typeof node.position.y === 'number'
        ? node.position
        : { x: 0, y: 0 },
      style: {
        background: 'transparent',
        border: 'none',
        padding: '0',
        width: NODE_WIDTH,
        height: NODE_HEIGHT,
      },
    };
  });

  const edges = data.connectors.map((connector, index) => {
    // Find source node to determine if it's a decision node
    const sourceNode = data.nodes.find(n => n.id === connector.source);
    const isDecision = sourceNode?.nodeType === 'decision';
    const label = connector.label || '';

    // Prefer the persisted handles when present. Decision-node fallback
    // (label-based) only applies to legacy connectors that haven't been
    // saved with explicit handles yet.
    let sourceHandle = connector.sourceHandle ?? undefined;
    let targetHandle = connector.targetHandle ?? undefined;
    if (!sourceHandle && isDecision) {
      if (label.toLowerCase() === 'yes') {
        sourceHandle = 'right';  // Green handle at right
      } else if (label.toLowerCase() === 'no') {
        sourceHandle = 'bottom';  // Red handle at bottom
      }
    }

    const connectorId = connector.id || `connector_${connector.source}_${connector.target}_${index}`;
    const isBidirectional = connector.isBidirectional === true;
    const edgeMarkers = getEdgeMarkers(isBidirectional);
    const waypoints = Array.isArray(connector.waypoints)
      ? connector.waypoints
        .filter((p) => p && Number.isFinite(p.x) && Number.isFinite(p.y))
        .map((p) => ({ x: p.x, y: p.y }))
      : [];
    return {
      id: connectorId,
      source: connector.source,
      target: connector.target,
      sourceHandle,
      targetHandle,
      type: 'custom',
      animated: false,
      ...edgeMarkers,
      style: { stroke: '#000000', strokeWidth: 2 },
      data: {
        label: label,
        connectorId,
        isBidirectional,
        waypoints,
      },
    };
  });

  // Extract key systems from backend data (systems[] only).
  const keySystemsSet = new Set();
  for (const node of data.nodes) {
    if (Array.isArray(node.systems) && node.systems.length > 0) {
      for (const s of node.systems) {
        const name = typeof s?.name === 'string' ? s.name.trim() : '';
        if (name && s.keySystem === true) {
          keySystemsSet.add(name);
        }
      }
    }
  }

  return { nodes, edges, keySystems: keySystemsSet, savedRisks };
};

// Split a node's `system` text into the list of individual system names.
// Matches the backend splitter (PopulateNodeSystemJunctions / BackfillSystems
// FromNodes) which splits on '/' or ','. Trims segments, drops empties,
// and dedupes case-insensitively while keeping the user-typed casing.
function parseSystemNames(systemText) {
  if (typeof systemText !== 'string') return [];
  const seen = new Set();
  const out = [];
  for (const raw of systemText.split(/[,/]/)) {
    const name = raw.trim();
    if (!name) continue;
    const key = name.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(name);
  }
  return out;
}

function nodeIncludesSystemName(systemText, systemName) {
  const target = (systemName || '').trim().toLowerCase();
  if (!target) return false;
  return parseSystemNames(systemText).some((name) => name.toLowerCase() === target);
}

// Build per-cylinder display entries from API systems[] only.
function resolveSystemDisplayEntries(systemsFromApi) {
  if (!Array.isArray(systemsFromApi) || systemsFromApi.length === 0) {
    return [];
  }
  return systemsFromApi
    .map((s) => {
      const name = typeof s?.name === 'string' ? s.name.trim() : '';
      if (!name) return null;
      return { name, isKeySystem: s.keySystem === true };
    })
    .filter(Boolean);
}

// Form/multi-select seed: version-scoped systems[] only (same source as
// cylinders). Empty systems[] → empty form selection (no shared system text).
function systemTextForEditForm(systemsFromApi) {
  const names = resolveSystemDisplayEntries(systemsFromApi).map((e) => e.name);
  if (names.length === 0) {
    return '';
  }
  // Match SystemMultiSelect emit format ("A, B").
  return names.join(', ');
}

// Build per-system key flags for save payload (id + keySystem only).
function buildSystemsSavePayload(systemsFromApi, keySystemsSet) {
  if (!Array.isArray(systemsFromApi) || systemsFromApi.length === 0) return undefined;
  const entries = systemsFromApi
    .map((s) => {
      const id = typeof s?.id === 'string' ? s.id.trim() : '';
      const name = typeof s?.name === 'string' ? s.name.trim() : '';
      if (!id || !name) return null;
      return { id, keySystem: keySystemsSet.has(name) };
    })
    .filter(Boolean);
  return entries.length > 0 ? entries : undefined;
}

function buildKeySystemsSetFromNodes(nodes) {
  const set = new Set();
  (Array.isArray(nodes) ? nodes : []).forEach((node) => {
    if (!node || node.type === 'swimlane') return;
    const systems = Array.isArray(node.data?.systems) ? node.data.systems : [];
    systems.forEach((s) => {
      const name = typeof s?.name === 'string' ? s.name.trim() : '';
      if (name && s.keySystem === true) set.add(name);
    });
    if (systems.length === 0 && node.data?.isKeySystem === true) {
      parseSystemNames(node.data.system || '').forEach((name) => {
        if (name) set.add(name);
      });
    }
  });
  return set;
}

function keySystemsSetsEqual(a, b) {
  if (a === b) return true;
  if (!a || !b || a.size !== b.size) return false;
  for (const name of a) {
    if (!b.has(name)) return false;
  }
  return true;
}

// Build documents[] save payload (id + docType + optional systems).
function buildDocumentsSavePayload(documentsFromApi) {
  if (!Array.isArray(documentsFromApi)) {
    return [];
  }
  const seen = new Set();
  const out = [];
  for (const d of documentsFromApi) {
    const id = typeof d?.id === 'string' ? d.id.trim() : '';
    if (!id || seen.has(id)) continue;
    seen.add(id);
    const isReport = d.docType === 'report';
    const systems = [];
    if (Array.isArray(d.systems)) {
      const sysSeen = new Set();
      for (const s of d.systems) {
        const sid = typeof s?.id === 'string' ? s.id.trim() : '';
        if (!sid || sysSeen.has(sid)) continue;
        sysSeen.add(sid);
        systems.push({ id: sid });
      }
    }
    out.push({
      id,
      docType: isReport ? 'report' : null,
      systems,
    });
  }
  return out;
}

function documentSystemsText(systems) {
  if (!Array.isArray(systems) || systems.length === 0) return '';
  return systems
    .map((s) => (typeof s?.name === 'string' ? s.name.trim() : ''))
    .filter(Boolean)
    .join(', ');
}

function syncDocumentSystemsFromText(systemText, previousSystems, catalogByLowerName = {}) {
  const names = parseSystemNames(systemText);
  if (names.length === 0) return [];
  const prevByLowerName = new Map();
  if (Array.isArray(previousSystems)) {
    for (const s of previousSystems) {
      const name = typeof s?.name === 'string' ? s.name.trim() : '';
      const id = typeof s?.id === 'string' ? s.id.trim() : '';
      if (!name) continue;
      prevByLowerName.set(name.toLowerCase(), { id, name });
    }
  }
  return names.map((name) => {
    const lower = name.toLowerCase();
    const prev = prevByLowerName.get(lower);
    const catalog = catalogByLowerName[lower];
    return {
      id: prev?.id || catalog?.id || '',
      name,
    };
  });
}

// Keep node.data.systems aligned with the comma-separated system text.
function syncNodeSystemsWithText(systemText, previousSystems, keySystemsSet, catalogByLowerName = {}) {
  const names = parseSystemNames(systemText);
  if (names.length === 0) return [];

  const prevByLowerName = new Map();
  if (Array.isArray(previousSystems)) {
    for (const s of previousSystems) {
      const name = typeof s?.name === 'string' ? s.name.trim() : '';
      const id = typeof s?.id === 'string' ? s.id.trim() : '';
      if (!name) continue;
      prevByLowerName.set(name.toLowerCase(), { id, name, keySystem: s.keySystem === true });
    }
  }

  return names.map((name) => {
    const lower = name.toLowerCase();
    const prev = prevByLowerName.get(lower);
    const catalog = catalogByLowerName[lower];
    return {
      id: prev?.id || catalog?.id || '',
      name,
      keySystem: keySystemsSet.has(name),
    };
  });
}

// One cylinder SVG for a single system. Same proportions as the legacy
// single-cylinder shape but slightly smaller so multiple fit along the
// bottom edge of a node without overlap.
function SystemCylinderIcon({ name, isKeySystem, palette = 'rectangle' }) {
  // Two palettes match the prior look:
  //   rectangle: light blue / deep purple-when-key (matches RectangleNode)
  //   decision : solid purple (matches DecisionNode)
  let fillTop;
  let fillBody;
  let stroke;
  let textFill;
  if (palette === 'decision') {
    fillTop = '#a78bfa';
    fillBody = '#8b5cf6';
    stroke = '#6d28d9';
    textFill = '#ffffff';
  } else if (isKeySystem) {
    fillTop = '#8b5cf6';
    fillBody = '#7c3aed';
    stroke = '#5b21b6';
    textFill = '#ffffff';
  } else {
    fillTop = '#bfdbfe';
    fillBody = '#dbeafe';
    stroke = '#93c5fd';
    textFill = '#1e3a8a';
  }
  const strokeWidth = (palette === 'decision' || isKeySystem) ? 2 : 1.5;
  const displayName = name.length > 10 ? `${name.slice(0, 9)}…` : name;
  return (
    <svg
      width="80"
      height="48"
      viewBox="0 0 80 48"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      data-export-badge="system"
      data-export-label={name}
    >
      <ellipse cx="40" cy="7" rx="35" ry="7" fill={fillBody} stroke={stroke} strokeWidth={strokeWidth} />
      <rect x="5" y="7" width="70" height="33" fill={fillBody} />
      <ellipse cx="40" cy="40" rx="35" ry="7" fill={fillTop} stroke={stroke} strokeWidth={strokeWidth} />
      <ellipse cx="40" cy="7" rx="35" ry="7" fill={fillTop} stroke={stroke} strokeWidth={strokeWidth} />
      <text
        x="40"
        y={isKeySystem && palette !== 'decision' ? 21 : 24}
        textAnchor="middle"
        dominantBaseline="middle"
        fill={textFill}
        fontSize="9"
        fontWeight={palette === 'decision' ? 700 : 600}
        style={{ fontFamily: 'system-ui, -apple-system, sans-serif' }}
      >
        <title>{name}</title>
        {displayName}
      </text>
      {isKeySystem && palette !== 'decision' && (
        <text x="40" y="35" textAnchor="middle" dominantBaseline="middle"
          fill="#fde047" fontSize="6" fontWeight="700"
          style={{ fontFamily: 'system-ui, -apple-system, sans-serif' }}>
          KEY
        </text>
      )}
    </svg>
  );
}

// A horizontal row of system cylinders anchored at a node's bottom-right.
// Renders up to MAX_VISIBLE cylinders side-by-side; any beyond that fold
// into a "+N" overflow chip so we never blow past the node's width budget.
function SystemCylinders({ systemEntries, palette = 'rectangle' }) {
  if (!Array.isArray(systemEntries) || systemEntries.length === 0) return null;
  const MAX_VISIBLE = 4;
  const visible = systemEntries.slice(0, MAX_VISIBLE);
  const overflow = systemEntries.length - visible.length;
  return (
    <div
      className="absolute"
      style={{
        bottom: '0',
        right: '0',
        transform: 'translate(20%, 50%)',
        display: 'flex',
        alignItems: 'center',
        gap: '4px',
        pointerEvents: 'none',
      }}
    >
      {visible.map((entry, idx) => (
        <SystemCylinderIcon
          key={`${entry.name}-${idx}`}
          name={entry.name}
          isKeySystem={entry.isKeySystem}
          palette={palette}
        />
      ))}
      {overflow > 0 && (
        <div
          title={systemEntries.slice(MAX_VISIBLE).map((e) => e.name).join(', ')}
          data-export-badge="overflow"
          data-export-label={systemEntries.slice(MAX_VISIBLE).map((e) => e.name).join(', ')}
          className="text-[10px] font-bold text-blue-900 bg-white border border-blue-300 rounded-full px-1.5 py-0.5 shadow"
        >
          +{overflow}
        </div>
      )}
    </div>
  );
}

// Normalize version-scoped documents[] from GET flowchart (catalog junction).
// No backfill from legacy document text — empty array when missing.
function normalizeNodeDocuments(documentsFromApi) {
  if (!Array.isArray(documentsFromApi) || documentsFromApi.length === 0) {
    return [];
  }
  const out = [];
  for (const d of documentsFromApi) {
    if (!d || typeof d !== 'object') continue;
    const id = typeof d.id === 'string' ? d.id.trim() : '';
    const name = typeof d.name === 'string' ? d.name.trim() : '';
    if (!id && !name) continue;
    const rawType = d.docType ?? d.doc_type ?? null;
    let docType = null;
    if (typeof rawType === 'string') {
      const trimmed = rawType.trim();
      docType = trimmed === '' ? null : trimmed;
    }
    out.push({
      id,
      name,
      docType,
      systems: Array.isArray(d.systems)
        ? d.systems
          .map((s) => ({
            id: typeof s?.id === 'string' ? s.id.trim() : '',
            name: typeof s?.name === 'string' ? s.name.trim() : '',
          }))
          .filter((s) => s.id && s.name)
        : [],
    });
  }
  return out;
}

function documentsForCompare(documents) {
  return normalizeNodeDocuments(documents)
    .map((d) => {
      const sys = (d.systems || [])
        .map((s) => s.id)
        .filter(Boolean)
        .sort()
        .join(',');
      return `${d.id}\0${d.name}\0${d.docType === 'report' ? 'report' : ''}\0${sys}`;
    })
    .sort()
    .join('\n');
}

/** Count process nodes that already link the given catalog document id. */
function countNodesWithDocument(nodes, docId) {
  const id = typeof docId === 'string' ? docId.trim() : '';
  if (!id || !Array.isArray(nodes)) return 0;
  return nodes.filter(
    (n) =>
      n?.type !== 'swimlane' &&
      Array.isArray(n?.data?.documents) &&
      n.data.documents.some((d) => d?.id === id)
  ).length;
}

/**
 * Apply catalog-global document fields (docType + systems) onto every node
 * that links this document id — same idea as keySystems for systems.
 */
function applyDocumentCatalogFieldsToNodes(nodes, docId, fields) {
  const id = typeof docId === 'string' ? docId.trim() : '';
  if (!id || !Array.isArray(nodes) || !fields) return nodes;
  const nextDocType = fields.docType === 'report' ? 'report' : null;
  const nextSystems = Array.isArray(fields.systems) ? fields.systems : [];
  return nodes.map((node) => {
    if (node?.type === 'swimlane') return node;
    const docs = Array.isArray(node?.data?.documents) ? node.data.documents : [];
    if (!docs.some((d) => d?.id === id)) return node;
    return {
      ...node,
      data: {
        ...node.data,
        documents: docs.map((d) =>
          d?.id === id
            ? { ...d, docType: nextDocType, systems: nextSystems }
            : d
        ),
      },
    };
  });
}

/**
 * For every document id present in catalogById, stamp docType/systems onto
 * matching entries on all nodes (keeps save payload consistent across nodes).
 */
function applyDocumentCatalogMapToNodes(nodes, catalogById) {
  if (!Array.isArray(nodes) || !(catalogById instanceof Map) || catalogById.size === 0) {
    return nodes;
  }
  return nodes.map((node) => {
    if (node?.type === 'swimlane') return node;
    const docs = Array.isArray(node?.data?.documents) ? node.data.documents : [];
    if (docs.length === 0) return node;
    let changed = false;
    const nextDocs = docs.map((d) => {
      const cat = d?.id ? catalogById.get(d.id) : null;
      if (!cat) return d;
      changed = true;
      return {
        ...d,
        docType: cat.docType === 'report' ? 'report' : null,
        systems: Array.isArray(cat.systems) ? cat.systems : [],
      };
    });
    if (!changed) return node;
    return { ...node, data: { ...node.data, documents: nextDocs } };
  });
}

/** Build id → { docType, systems } from a documents list (e.g. edit form). */
function documentCatalogMapFromDocuments(documents) {
  const map = new Map();
  for (const d of normalizeNodeDocuments(documents)) {
    if (!d.id) continue;
    map.set(d.id, {
      docType: d.docType === 'report' ? 'report' : null,
      systems: Array.isArray(d.systems) ? d.systems : [],
    });
  }
  return map;
}

// Comma/semicolon-separated document field → list of names (legacy helper; UI no longer uses text).
function parseDocumentNames(documentText) {
  if (typeof documentText !== 'string') return [];
  const out = [];
  for (const raw of documentText.split(/[,;]/)) {
    const name = raw.trim();
    if (name) out.push(name);
  }
  return out;
}

function DocumentFolderIcon({ name, isReport = false }) {
  const displayName = name.length > 10 ? `${name.slice(0, 10)}…` : name;
  const fill = isReport ? '#fef3c7' : '#dcfce7';
  const stroke = isReport ? '#f59e0b' : '#86efac';
  const tabFill = isReport ? '#fde047' : '#bbf7d0';
  const textFill = isReport ? '#b45309' : '#15803d';
  return (
    <svg
      width="70"
      height="52"
      viewBox="0 0 70 55"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      data-export-badge="document"
      data-export-label={name}
    >
      <path
        d="M 10 20 L 30 20 L 35 15 L 60 15 L 60 50 L 10 50 Z"
        fill={fill}
        stroke={stroke}
        strokeWidth="1.5"
      />
      <path d="M 10 20 L 60 20 L 60 25 L 10 25 Z" fill={tabFill} />
      <text
        x="35"
        y="38"
        textAnchor="middle"
        dominantBaseline="middle"
        fill={textFill}
        fontSize="9"
        fontWeight="600"
        style={{ fontFamily: 'system-ui, -apple-system, sans-serif' }}
      >
        <title>{name}</title>
        {displayName}
      </text>
    </svg>
  );
}

// Up to 4 document folders at top-right; overflow folds into +N (opens node edit form).
// d1 stays at the original top-right corner; d2, d3… sit beside it going left.
// Report docs (docType === 'report') render in medium-risk yellow; others stay green.
function DocumentFolders({ documents, onOverflowClick }) {
  if (!Array.isArray(documents) || documents.length === 0) return null;
  const MAX_VISIBLE = 4;
  const FOLDER_STEP = 58;
  const visible = documents.slice(0, MAX_VISIBLE);
  const overflow = documents.length - visible.length;
  return (
    <div
      className="absolute z-40"
      style={{
        top: '0',
        right: '0',
        width: 0,
        height: 0,
      }}
    >
      {visible.map((doc, index) => {
        const name = doc?.name || '';
        const isReport = typeof doc === 'object' && doc?.docType === 'report';
        return (
          <div
            key={`${name}-${index}`}
            title={name}
            style={{
              position: 'absolute',
              top: 0,
              right: index * FOLDER_STEP,
              transform: 'translate(50%, -50%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              pointerEvents: 'none',
            }}
          >
            <DocumentFolderIcon name={name} isReport={isReport} />
          </div>
        );
      })}
      {overflow > 0 && (
        <button
          type="button"
          title={documents.slice(MAX_VISIBLE).map((d) => d?.name).filter(Boolean).join(', ')}
          data-export-badge="overflow"
          data-export-label={documents.slice(MAX_VISIBLE).map((d) => d?.name).filter(Boolean).join(', ')}
          onClick={(e) => {
            e.stopPropagation();
            if (typeof onOverflowClick === 'function') onOverflowClick(e);
          }}
          className="text-[10px] font-bold text-green-900 bg-white border border-green-300 rounded-full px-1.5 py-0.5 shadow cursor-pointer hover:bg-green-50"
          style={{
            position: 'absolute',
            top: 0,
            right: visible.length * FOLDER_STEP,
            transform: 'translate(50%, -50%)',
            zIndex: 50,
          }}
        >
          +{overflow}
        </button>
      )}
    </div>
  );
}

// Custom swimlane node component - renders as background in ReactFlow.
// Height comes from data.height (per-actor, persisted) so a resized lane
// stays at the user's chosen size after reload. The bottom-edge
// NodeResizeControl emits the new height through data.onResize.
function SwimlaneNode({ data }) {
  const dragHighlight = useContext(SwimlaneDragHighlightContext);
  const handleEditClick = useCallback((e) => {
    e.stopPropagation();
    if (data.onEdit) {
      data.onEdit(data.actor, data.label);
    }
  }, [data]);

  const laneHeight = Number.isFinite(data.height) && data.height > 0 ? data.height : SWIMLANE_HEIGHT;

  // Highlight the lane currently under a dragged node with the single theme
  // blue, regardless of where it started or lands.
  const isHighlighted = Boolean(dragHighlight && dragHighlight.actorKey === normalizeActorKey(data.actor));

  const handleResizeEnd = useCallback((_event, params) => {
    if (!data.onResize || !Number.isFinite(params?.height)) return;
    const clamped = Math.max(SWIMLANE_MIN_HEIGHT, Math.min(SWIMLANE_MAX_HEIGHT, params.height));
    if (clamped === laneHeight) return;
    data.onResize(data.actor, clamped);
  }, [data, laneHeight]);

  const swimlaneStyle = useMemo(() => {
    const baseBackground = data.index % 2 === 0 ? 'rgba(249, 250, 251, 0.3)' : 'rgba(255, 255, 255, 0.3)';
    const HIGHLIGHT_COLOR = '#2563eb'; // theme blue
    return {
      width: data.width,
      height: laneHeight,
      backgroundColor: isHighlighted ? 'rgba(37, 99, 235, 0.10)' : baseBackground,
      border: isHighlighted ? `2px solid ${HIGHLIGHT_COLOR}` : 'none',
      borderBottom: isHighlighted ? `2px solid ${HIGHLIGHT_COLOR}` : '2px solid #d1d5db',
      boxShadow: isHighlighted ? `inset 0 0 0 2px ${HIGHLIGHT_COLOR}` : 'none',
      transition: 'background-color 120ms ease, box-shadow 120ms ease, border-color 120ms ease',
      position: 'relative',
      // The lane body must never intercept clicks/drags meant for the nodes that
      // sit on top of it (or canvas panning over empty lane space). Only the
      // header and the resize grip below re-enable pointer events.
      pointerEvents: 'none',
    };
  }, [data.width, data.index, laneHeight, isHighlighted]);

  const swimlaneHeaderStyle = useMemo(() => ({
    position: 'absolute',
    left: '16px',
    top: '16px',
    fontWeight: '600',
    color: '#1f2937',
    fontSize: '14px',
    zIndex: 1000,
    backgroundColor: 'rgba(255, 255, 255, 0.95)',
    padding: '4px 8px',
    borderRadius: '4px',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
    pointerEvents: 'auto',
  }), []);

  const swimlaneEditButtonStyle = useMemo(() => ({
    border: 'none',
    background: 'transparent',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    pointerEvents: 'auto',
  }), []);

  return (
    <div style={swimlaneStyle}>
      <div style={swimlaneHeaderStyle}>
        <span>{data.label}</span>
        <button
          onClick={handleEditClick}
          className="hover:bg-gray-200 rounded p-1 transition-colors"
          title="Edit swimlane"
          data-export-ignore
          style={swimlaneEditButtonStyle}
        >
          <Edit2 className="h-3.5 w-3.5 text-gray-600" />
        </button>
      </div>
      <NodeResizeControl
        position="bottom"
        variant="line"
        minWidth={data.width}
        maxWidth={data.width}
        minHeight={SWIMLANE_MIN_HEIGHT}
        maxHeight={SWIMLANE_MAX_HEIGHT}
        onResizeEnd={handleResizeEnd}
        style={{
          background: 'transparent',
          border: 'none',
          // Tall, full-width hit area on the lane's bottom edge so the grip is
          // easy to grab (the old 8px transparent strip was effectively
          // ungrabbable, especially with the lane sitting behind the nodes).
          height: 16,
          width: '100%',
          cursor: 'ns-resize',
          pointerEvents: 'auto',
        }}
      >
        <div
          className="swimlane-resize-grip"
          title="Drag to make this lane taller or shorter"
          style={{
            position: 'absolute',
            bottom: 3,
            left: '50%',
            transform: 'translateX(-50%)',
            width: 56,
            height: 5,
            borderRadius: 9999,
            background: '#9ca3af',
            opacity: 0.55,
            pointerEvents: 'none',
          }}
        />
      </NodeResizeControl>
    </div>
  );
}

// Custom rectangle node (default) with optional system cylinder, SOX control hexagon, and document folder indicators
function RectangleNode({ id, data, selected }) {
  // Highlight this node while a connection dragged from another node could drop here.
  const isConnectionTarget = useConnection(makeConnectionTargetSelector(id));
  const riskControls = useMemo(() => collectControlsFromNodeRisks(data.risks), [data.risks]);
  const legacySoxControl = (data.soxControl || '').trim();
  const controlsToRender = riskControls.length > 0
    ? riskControls
    : (legacySoxControl ? [{ __legacyLabel: legacySoxControl }] : []);
  const hasSoxControl = controlsToRender.length > 0;
  const documentList = useMemo(
    () => normalizeNodeDocuments(data.documents).filter((d) => d.name),
    [data.documents]
  );
  const hasDocument = documentList.length > 0;
  const hasRisks = data.risks && data.risks.length > 0;
  const nodeDescription = data.nodeDescription || '';

  const systemEntries = useMemo(
    () => resolveSystemDisplayEntries(data.systems),
    [data.systems]
  );
  const hasSystem = systemEntries.length > 0;

  // Get highest risk level for color
  const getHighestRiskLevel = () => {
    if (!hasRisks) return null;
    if (data.risks.some((r) => normalizeRiskLevelValue(r) === 'High')) return 'High';
    if (data.risks.some((r) => normalizeRiskLevelValue(r) === 'Medium')) return 'Medium';
    return 'Low';
  };
  const highestRisk = getHighestRiskLevel();
  const nodeContainerStyle = useMemo(() => ({ width: NODE_WIDTH, height: NODE_HEIGHT }), []);
  const mainNodeStyle = useMemo(() => ({
    backgroundColor: data.isMapped ? '#ffffff' : '#fef3c7',
    border: selected ? '2px solid #ef4444' : (data.isMapped ? '2px solid #3b82f6' : '2px solid #fbbf24'),
    borderRadius: '8px',
  }), [data.isMapped, selected]);

  const handleRiskClick = useCallback((e) => {
    e.stopPropagation();
    if (data.onRiskClick) data.onRiskClick();
  }, [data]);

  const handleDeleteClick = useCallback((e) => {
    e.stopPropagation();
    if (data.onDelete) data.onDelete();
  }, [data]);

  const handleControlClick = useCallback((e, control) => {
    e.stopPropagation();
    if (data.onHexagonControlClick) data.onHexagonControlClick(control);
    else if (data.onControlClick) data.onControlClick(control);
  }, [data]);

  const handleControlsOverflowClick = useCallback((e, overflowControls) => {
    e.stopPropagation();
    if (data.onHexagonControlsOverflowClick) {
      data.onHexagonControlsOverflowClick(overflowControls);
    }
  }, [data]);

  const handleDocumentsOverflowClick = useCallback((e) => {
    e.stopPropagation();
    if (data.onEditNode) data.onEditNode();
  }, [data]);

  const MAX_VISIBLE_HEXAGON_CONTROLS = 4;
  const visibleHexagonControls = controlsToRender.slice(0, MAX_VISIBLE_HEXAGON_CONTROLS);
  const overflowHexagonControls = controlsToRender.slice(MAX_VISIBLE_HEXAGON_CONTROLS);
  const hexagonOverflowCount = overflowHexagonControls.length;

  return (
    <div className="relative group flowchart-node-hover-host" style={nodeContainerStyle}>
      {/* Center connect zone: drag from the middle of the node to start a
          connector; the border ring stays grabbable for move/select. The
          full-node target handle below still lets a connection be dropped
          anywhere on the node. */}
      <EasyConnectSource nodeId={id} />
      <ConnectHint />
      <Handle
        type="target"
        position={Position.Left}
        isConnectableStart={false}
        style={getEasyConnectTargetHandleStyle(isConnectionTarget)}
      />

      {/* Risk indicator - red triangle on top-left */}
      {hasRisks && (
        <button
          onClick={handleRiskClick}
          className="absolute -top-3 -left-3 z-50 cursor-pointer hover:scale-110 transition-transform"
          title={`${data.risks.length} risk${data.risks.length > 1 ? 's' : ''} identified - Click to view`}
          data-export-badge="risk"
          data-export-label={`${data.risks.length} risk${data.risks.length > 1 ? 's' : ''}${highestRisk ? ` (highest: ${highestRisk})` : ''}`}
          data-export-count={data.risks.length}
        >
          <svg width="36" height="36" viewBox="0 0 28 28" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path
              d="M14 4 L26 24 L2 24 Z"
              fill={highestRisk === 'High' ? '#dc2626' : highestRisk === 'Medium' ? '#f59e0b' : '#eab308'}
              stroke={highestRisk === 'High' ? '#991b1b' : highestRisk === 'Medium' ? '#d97706' : '#ca8a04'}
              strokeWidth="1.5"
            />
            <text x="14" y="19" textAnchor="middle" fill="white" fontSize="12" fontWeight="bold">!</text>
          </svg>
          {data.risks.length > 1 && (
            <span className="absolute -top-1 -right-1 bg-red-700 text-white text-xs rounded-full w-4 h-4 flex items-center justify-center font-bold">
              {data.risks.length}
            </span>
          )}
        </button>
      )}

      {/* Delete button - shows only when selected */}
      {selected && data.onDelete && (
        <button
          onClick={handleDeleteClick}
          className="absolute -top-3 -right-3 z-50 bg-red-500 hover:bg-red-600 text-white rounded-full p-1.5 shadow-lg transition-colors"
          title="Delete node (Delete/Backspace)"
        >
          <Trash2 className="h-3.5 w-3.5" />
        </button>
      )}

      {/* Outside overflow-hidden content so the card is not clipped. */}
      <FlowchartNodeHoverCard
        nodeType={data.nodeType || 'process'}
        description={nodeDescription}
        actor={data.actor}
        systems={systemEntries}
        documents={documentList}
      />

      {/* Main rectangle content with visible border */}
      <div
        className="p-3 relative h-full overflow-hidden flex flex-col justify-center"
        style={mainNodeStyle}
      >
        {!data.isMapped && (
          <div className="absolute top-2 right-2">
            <AlertCircle className="h-4 w-4 text-amber-500" />
          </div>
        )}

        <div className="font-semibold text-sm mb-2 line-clamp-2">{nodeDescription}</div>
        {data.actor && (
          <div className="text-xs text-blue-600 mb-1">
            <strong>Actor:</strong> {data.actor}
          </div>
        )}
      </div>

      {/* One cylinder per linked system; color/KEY from each system's keySystem when available */}
      {hasSystem && (
        <SystemCylinders
          systemEntries={systemEntries}
          palette="rectangle"
        />
      )}

      {/* SOX Control hexagon indicator - centered on bottom-left corner.
          Same overflow pattern as system cylinders: show up to 4, fold the
          rest into a +N chip that opens the control list popup. */}
      {hasSoxControl && (
        <div
          className="absolute z-40"
          style={{
            bottom: '0',
            left: '0',
            width: 0,
            height: 0,
          }}
        >
          {visibleHexagonControls.map((control, index) => {
            const controlLabel = control.__legacyLabel || getControlDisplayLabel(control);
            if (!controlLabel) return null;
            const controlKey = String(control.id ?? control.control_id ?? control.controlId ?? `${controlLabel}-${index}`);
            const isKeyControl = isKeyControlSignificance(control);
            return (
              <button
                key={controlKey}
                type="button"
                onClick={(e) => handleControlClick(e, control)}
                title={`Control: ${controlLabel} - Click to view`}
                data-export-badge="control"
                data-export-label={`${controlLabel}${isKeyControl ? ' (key control)' : ''}`}
                className="cursor-pointer hover:scale-105 transition-transform"
                style={{
                  position: 'absolute',
                  bottom: index * 34,
                  left: 0,
                  transform: 'translate(-50%, 50%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  padding: 0,
                  border: 'none',
                  background: 'transparent',
                }}
              >
                <svg width="70" height="70" viewBox="0 0 70 70" fill="none" xmlns="http://www.w3.org/2000/svg">
                  {/* Hexagon shape - deeper orange for key controls */}
                  <polygon
                    points="20,30 35,15 50,30 50,40 35,55 20,40"
                    fill={isKeyControl ? "#ea580c" : "#f3e8ff"}
                    stroke={isKeyControl ? "#c2410c" : "#c084fc"}
                    strokeWidth={isKeyControl ? "2" : "1.5"}
                  />
                  {/* Text inside hexagon */}
                  <text
                    x="35"
                    y={isKeyControl ? "30" : "35"}
                    textAnchor="middle"
                    dominantBaseline="middle"
                    fill={isKeyControl ? "#ffffff" : "#6b21a8"}
                    fontSize="9"
                    fontWeight="600"
                    style={{ fontFamily: 'system-ui, -apple-system, sans-serif' }}
                  >
                    {controlLabel.length > 8 ? controlLabel.substring(0, 8) : controlLabel}
                  </text>

                  {/* KEY badge for key controls */}
                  {isKeyControl && (
                    <text
                      x="35"
                      y="44"
                      textAnchor="middle"
                      dominantBaseline="middle"
                      fill="#fde047"
                      fontSize="7"
                      fontWeight="700"
                      style={{ fontFamily: 'system-ui, -apple-system, sans-serif' }}
                    >
                      KEY
                    </text>
                  )}
                </svg>
              </button>
            );
          })}
          {hexagonOverflowCount > 0 && (
            <button
              type="button"
              onClick={(e) => handleControlsOverflowClick(e, overflowHexagonControls)}
              title={`${hexagonOverflowCount} more control${hexagonOverflowCount > 1 ? 's' : ''} — click to view`}
              data-export-badge="overflow"
              data-export-label={overflowHexagonControls.map((c) => c.__legacyLabel || getControlDisplayLabel(c)).filter(Boolean).join(', ')}
              className="cursor-pointer hover:scale-105 transition-transform text-[10px] font-bold text-purple-900 bg-white border border-purple-300 rounded-full px-1.5 py-0.5 shadow"
              style={{
                position: 'absolute',
                bottom: visibleHexagonControls.length * 34 + 8,
                left: 0,
                transform: 'translate(-50%, 50%)',
              }}
            >
              +{hexagonOverflowCount}
            </button>
          )}
        </div>
      )}

      {/* Document folders — catalog docs; report type uses teal */}
      {hasDocument && (
        <DocumentFolders
          documents={documentList}
          onOverflowClick={handleDocumentsOverflowClick}
        />
      )}

      {/* Mapped Control indicators - teal shield badge at bottom-left */}
      {data.mappedControls && data.mappedControls.length > 0 && (
        <div
          className="absolute z-40 flex flex-col gap-1"
          style={{
            bottom: '0',
            left: hasSoxControl ? '35px' : '0',
            transform: 'translate(-20%, 50%)',
          }}
        >
          {data.mappedControls.map((control) => (
            <button
              key={control.controlId}
              onClick={(e) => handleControlClick(e, control)}
              className="cursor-pointer hover:scale-105 transition-transform"
              title={`Control: ${control.controlDisplayId} - Click to view`}
              data-export-badge="mapped-control"
              data-export-label={control.controlDisplayId}
            >
              <svg width="90" height="30" viewBox="0 0 90 30" fill="none" xmlns="http://www.w3.org/2000/svg">
                <rect x="0" y="0" width="90" height="30" rx="6" ry="6" fill="#0d9488" stroke="#0f766e" strokeWidth="1.5" />
                <path d="M6 11 L13 7 L20 11 L20 16 C20 19 13 22 13 22 C13 22 6 19 6 16 Z" fill="none" stroke="#ffffff" strokeWidth="1.5" />
                <text x="28" y="18" fill="#ffffff" fontSize="10" fontWeight="600" style={{ fontFamily: 'system-ui, -apple-system, sans-serif' }}>
                  {control.controlDisplayId.length > 10 ? control.controlDisplayId.substring(0, 10) : control.controlDisplayId}
                </text>
              </svg>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// Custom rhombus/diamond node for decisions
function DecisionNode({ id, data, selected }) {
  // Highlight this node while a connection dragged from another node could drop here.
  const isConnectionTarget = useConnection(makeConnectionTargetSelector(id));
  const riskControls = useMemo(() => collectControlsFromNodeRisks(data.risks), [data.risks]);
  const legacySoxControl = (data.soxControl || '').trim();
  const primaryControlLabel = riskControls.length > 0
    ? getControlDisplayLabel(riskControls[0])
    : legacySoxControl;
  const hasRisks = data.risks && data.risks.length > 0;
  const nodeDescription = data.nodeDescription || '';
  const documentList = useMemo(
    () => normalizeNodeDocuments(data.documents).filter((d) => d.name),
    [data.documents]
  );
  const hasDocument = documentList.length > 0;
  const systemEntries = useMemo(
    () => resolveSystemDisplayEntries(data.systems),
    [data.systems]
  );
  const hasSystem = systemEntries.length > 0;

  // Get highest risk level for color
  const getHighestRiskLevel = () => {
    if (!hasRisks) return null;
    if (data.risks.some((r) => normalizeRiskLevelValue(r) === 'High')) return 'High';
    if (data.risks.some((r) => normalizeRiskLevelValue(r) === 'Medium')) return 'Medium';
    return 'Low';
  };
  const highestRisk = getHighestRiskLevel();
  const nodeContainerStyle = useMemo(() => ({ width: NODE_WIDTH, height: NODE_HEIGHT, position: 'relative' }), []);
  const riskIndicatorStyle = useMemo(() => ({ position: 'absolute', top: '-10px', left: '-10px' }), []);
  const diamondSvgStyle = useMemo(() => ({ position: 'absolute', top: 0, left: 0 }), []);
  const contentOverlayStyle = useMemo(() => ({
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: '70%',
    textAlign: 'center',
    padding: '8px',
  }), []);

  const handleRiskClick = useCallback((e) => {
    e.stopPropagation();
    if (data.onRiskClick) data.onRiskClick();
  }, [data]);

  const handleDeleteClick = useCallback((e) => {
    e.stopPropagation();
    if (data.onDelete) data.onDelete();
  }, [data]);

  const handleControlClick = useCallback((e, control) => {
    e.stopPropagation();
    if (data.onControlClick) data.onControlClick(control);
  }, [data]);

  const handleDocumentsOverflowClick = useCallback((e) => {
    e.stopPropagation();
    if (data.onEditNode) data.onEditNode();
  }, [data]);

  return (
    <div className="group flowchart-node-hover-host" style={nodeContainerStyle}>
      {/* Same visible connect dots as every other node, so a decision is
          connectable from/to all four sides and the line anchors where you drew
          it. (Resolves Neha's "decision circular points don't function" — they
          were special-cased and got overridden by easy-connect.) Branch
          Yes/No is set via the connector label. */}
      {/* Center connect zone (same model as every node): drag from the middle
          of the diamond to start a connector; the rim stays grabbable for
          move/select. */}
      <EasyConnectSource nodeId={id} />
      <ConnectHint />
      {/* Full-node target drop zone: drop a connection anywhere on the diamond. */}
      <Handle
        type="target"
        position={Position.Left}
        isConnectableStart={false}
        style={getEasyConnectTargetHandleStyle(isConnectionTarget)}
      />
      {/* Invisible legacy anchors so existing Yes(right)/No(bottom) edges keep
          attaching after this change. */}
      <Handle id="right" type="source" position={Position.Right} style={{ opacity: 0, width: 1, height: 1, minWidth: 1, minHeight: 1, border: 'none', background: 'transparent' }} />
      <Handle id="bottom" type="source" position={Position.Bottom} style={{ opacity: 0, width: 1, height: 1, minWidth: 1, minHeight: 1, border: 'none', background: 'transparent' }} />

      <FlowchartNodeHoverCard
        nodeType={data.nodeType || 'decision'}
        description={nodeDescription}
        actor={data.actor}
        systems={systemEntries}
        documents={documentList}
      />

      {/* Risk indicator - red triangle on top-left */}
      {hasRisks && (
        <button
          onClick={handleRiskClick}
          className="absolute z-50 cursor-pointer hover:scale-110 transition-transform"
          title={`${data.risks.length} risk${data.risks.length > 1 ? 's' : ''} identified - Click to view`}
          data-export-badge="risk"
          data-export-label={`${data.risks.length} risk${data.risks.length > 1 ? 's' : ''}${highestRisk ? ` (highest: ${highestRisk})` : ''}`}
          data-export-count={data.risks.length}
          style={riskIndicatorStyle}
        >
          <svg width="36" height="36" viewBox="0 0 28 28" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path
              d="M14 4 L26 24 L2 24 Z"
              fill={highestRisk === 'High' ? '#dc2626' : highestRisk === 'Medium' ? '#f59e0b' : '#eab308'}
              stroke={highestRisk === 'High' ? '#991b1b' : highestRisk === 'Medium' ? '#d97706' : '#ca8a04'}
              strokeWidth="1.5"
            />
            <text x="14" y="19" textAnchor="middle" fill="white" fontSize="12" fontWeight="bold">!</text>
          </svg>
          {data.risks.length > 1 && (
            <span className="absolute -top-1 -right-1 bg-red-700 text-white text-xs rounded-full w-4 h-4 flex items-center justify-center font-bold">
              {data.risks.length}
            </span>
          )}
        </button>
      )}

      {/* Delete button - shows only when selected */}
      {selected && data.onDelete && (
        <button
          onClick={handleDeleteClick}
          className="absolute -top-3 -right-3 z-50 bg-red-500 hover:bg-red-600 text-white rounded-full p-1.5 shadow-lg transition-colors"
          title="Delete node (Delete/Backspace)"
        >
          <Trash2 className="h-3.5 w-3.5" />
        </button>
      )}

      {/* SVG rhombus background */}
      <svg
        width={NODE_WIDTH}
        height={NODE_HEIGHT}
        style={diamondSvgStyle}
      >
        <defs>
          <filter id="shadow" x="-50%" y="-50%" width="200%" height="200%">
            <feDropShadow dx="0" dy="2" stdDeviation="3" floodOpacity="0.2" />
          </filter>
        </defs>
        <polygon
          points={`${NODE_WIDTH / 2},10 ${NODE_WIDTH - 10},${NODE_HEIGHT / 2} ${NODE_WIDTH / 2},${NODE_HEIGHT - 10} 10,${NODE_HEIGHT / 2}`}
          fill={data.isMapped ? '#ffffff' : '#fef3c7'}
          stroke={selected ? '#ef4444' : (data.isMapped ? '#3b82f6' : '#fbbf24')}
          strokeWidth="2.5"
          filter="url(#shadow)"
        />
      </svg>

      {/* Content overlay */}
      <div style={contentOverlayStyle}>
        {!data.isMapped && (
          <div style={{ position: 'absolute', top: '-10px', right: '-10px' }}>
            <AlertCircle className="h-4 w-4 text-amber-500" />
          </div>
        )}

        <div style={{ fontWeight: '600', fontSize: '12px', marginBottom: '4px', color: '#1f2937' }}>
          {nodeDescription}
        </div>
        {primaryControlLabel && (
          <div style={{ fontSize: '9px', color: '#9333ea', marginTop: '2px' }}>
            SOX: {primaryControlLabel}
          </div>
        )}
      </div>

      {/* Document folders — catalog docs; report type uses teal */}
      {hasDocument && (
        <DocumentFolders
          documents={documentList}
          onOverflowClick={handleDocumentsOverflowClick}
        />
      )}

      {/* One cylinder per linked system; color/KEY from each system's keySystem when available */}
      {hasSystem && (
        <SystemCylinders
          systemEntries={systemEntries}
          palette="rectangle"
        />
      )}

      {/* Mapped Control indicators - teal shield badge at bottom-left */}
      {data.mappedControls && data.mappedControls.length > 0 && (
        <div
          className="absolute z-40 flex flex-col gap-1"
          style={{
            bottom: '0',
            left: '0',
            transform: 'translate(-20%, 50%)',
          }}
        >
          {data.mappedControls.map((control) => (
            <button
              key={control.controlId}
              onClick={(e) => handleControlClick(e, control)}
              className="cursor-pointer hover:scale-105 transition-transform"
              title={`Control: ${control.controlDisplayId} - Click to view`}
              data-export-badge="mapped-control"
              data-export-label={control.controlDisplayId}
            >
              <svg width="90" height="30" viewBox="0 0 90 30" fill="none" xmlns="http://www.w3.org/2000/svg">
                <rect x="0" y="0" width="90" height="30" rx="6" ry="6" fill="#0d9488" stroke="#0f766e" strokeWidth="1.5" />
                <path d="M6 11 L13 7 L20 11 L20 16 C20 19 13 22 13 22 C13 22 6 19 6 16 Z" fill="none" stroke="#ffffff" strokeWidth="1.5" />
                <text x="28" y="18" fill="#ffffff" fontSize="10" fontWeight="600" style={{ fontFamily: 'system-ui, -apple-system, sans-serif' }}>
                  {control.controlDisplayId.length > 10 ? control.controlDisplayId.substring(0, 10) : control.controlDisplayId}
                </text>
              </svg>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// Bounding box of the visible ellipse within the oval node container; shared
// with the floating-edge geometry so edges anchor on the ellipse (SCRUM-460).
const OVAL_VISUAL_RECT = getOvalVisualRect({ x: 0, y: 0, width: NODE_WIDTH, height: NODE_HEIGHT });

// OvalNode component for start/end nodes
function OvalNode({ id, data, selected }) {
  const isStart = data.nodeType === 'start';
  // Highlight this node while a connection dragged from another node could drop here.
  const isConnectionTarget = useConnection(makeConnectionTargetSelector(id));

  // Green for start, red for end
  const fillColor = isStart ? '#d1fae5' : '#fee2e2';
  const strokeColor = isStart ? '#10b981' : '#ef4444';
  const textColor = isStart ? '#047857' : '#dc2626';

  // Ellipse radii - smaller size for start/end nodes (60% of normal node size,
  // minus padding) derived from the shared visual rect.
  const rx = OVAL_VISUAL_RECT.width / 2;
  const ry = OVAL_VISUAL_RECT.height / 2;

  const ovalContainerStyle = useMemo(() => ({ position: 'relative', width: NODE_WIDTH, height: NODE_HEIGHT }), []);
  const ovalSvgStyle = useMemo(() => ({ overflow: 'visible' }), []);

  return (
    <div className="group" style={ovalContainerStyle}>
      <svg width={NODE_WIDTH} height={NODE_HEIGHT} style={ovalSvgStyle}>
        {/* Oval shape - centered within the container */}
        <ellipse
          cx={NODE_WIDTH / 2}
          cy={NODE_HEIGHT / 2}
          rx={rx}
          ry={ry}
          fill={fillColor}
          stroke={selected ? '#3b82f6' : strokeColor}
          strokeWidth={selected ? 3 : 2}
        />

        {/* Text - only "Start" or "End" - centered within the container */}
        <text
          x={NODE_WIDTH / 2}
          y={NODE_HEIGHT / 2}
          textAnchor="middle"
          dominantBaseline="middle"
          fill={textColor}
          fontSize="14"
          fontWeight="600"
          style={{ fontFamily: 'system-ui, -apple-system, sans-serif' }}
          title={data.nodeDescription}
        >
          {isStart ? 'Start' : 'End'}
        </text>
      </svg>

      {/* Center connect zone sized to the visible ellipse: drag from the middle
          of the oval to start a connector. The full-ellipse target zone below
          still receives a dropped connection anywhere on the oval. */}
      <EasyConnectSource nodeId={id} bounds={OVAL_VISUAL_RECT} />
      <ConnectHint bounds={OVAL_VISUAL_RECT} />
      <Handle
        type="target"
        position={Position.Left}
        isConnectableStart={false}
        style={getEasyConnectTargetHandleStyle(isConnectionTarget, OVAL_VISUAL_RECT, '9999px')}
      />
      {/* No overlapping legacy anchors here — they intermittently swallowed the
          press on the start/end circles. The center EasyConnectSource handles
          starting and the full-ellipse target handles ending
          (ConnectionMode.Loose); any legacy handle-less edge falls back to
          floating geometry in CustomEdge. */}

      {/* Start/End are protected: no delete button — they cannot be removed. */}
    </div>
  );
}

// Custom edge with editable label.
// Connectors without an explicit handle render as floating step edges: both
// endpoints are derived each render from the relative position of the two
// nodes (SCRUM-460 "easy-connect"). Edges with an explicit handle (decision
// Yes/No, legacy data) keep the fixed-handle path React Flow computed.
function CustomEdge({
  id,
  source,
  target,
  sourceX,
  sourceY,
  targetX,
  targetY,
  sourcePosition,
  targetPosition,
  sourceHandleId,
  targetHandleId,
  style = {},
  markerStart,
  markerEnd,
  data,
  selected,
}) {
  // Subscribing to the internal nodes keeps the floating endpoints live while
  // a node is dragged.
  const sourceNode = useInternalNode(source);
  const targetNode = useInternalNode(target);
  const { screenToFlowPosition } = useReactFlow();

  // Resolve each endpoint independently: anchored to its handle when the user
  // connected one (line stays attached to that exact dot and exits in its
  // direction), otherwise floating to the node edge. A line is therefore always
  // attached at both ends.
  const needFloating = !sourceHandleId || !targetHandleId;
  const floatingParams = needFloating ? getFloatingEdgeParamsForNodes(sourceNode, targetNode) : null;
  const sx = sourceHandleId ? sourceX : (floatingParams ? floatingParams.sx : sourceX);
  const sy = sourceHandleId ? sourceY : (floatingParams ? floatingParams.sy : sourceY);
  const tx = targetHandleId ? targetX : (floatingParams ? floatingParams.tx : targetX);
  const ty = targetHandleId ? targetY : (floatingParams ? floatingParams.ty : targetY);
  const sourcePos = sourceHandleId ? sourcePosition : (floatingParams ? floatingParams.sourcePos : sourcePosition);
  const targetPos = targetHandleId ? targetPosition : (floatingParams ? floatingParams.targetPos : targetPosition);

  const savedWaypoints = Array.isArray(data?.waypoints)
    ? data.waypoints.filter((p) => p && Number.isFinite(p.x) && Number.isFinite(p.y))
    : [];

  // While a waypoint is dragged, override just that point for a live path.
  const [drag, setDrag] = useState(null);
  const dragRef = useRef(null);
  const waypoints = savedWaypoints.map((p, i) =>
    (drag && drag.index === i ? { x: drag.x, y: drag.y } : p)
  );
  const hasWaypoints = waypoints.length > 0;

  let edgePath;
  let labelX;
  let labelY;
  if (hasWaypoints) {
    // Route through the waypoints (Neha feedback: editable connector bends).
    const pts = [{ x: sx, y: sy }, ...waypoints, { x: tx, y: ty }];
    edgePath = buildWaypointPath(pts);
    const mid = pts[Math.floor(pts.length / 2)];
    labelX = mid.x;
    labelY = mid.y;
  } else {
    const [p, lx, ly] = getSmoothStepPath({
      sourceX: sx,
      sourceY: sy,
      sourcePosition: sourcePos,
      targetX: tx,
      targetY: ty,
      targetPosition: targetPos,
      borderRadius: 14,
    });
    edgePath = p;
    labelX = lx;
    labelY = ly;
  }

  const onWaypointsChange = useContext(WaypointActionsContext);
  const commitWaypoints = useCallback((next) => {
    if (onWaypointsChange) onWaypointsChange(id, next);
    else if (data?.onWaypointsChange) data.onWaypointsChange(next);
  }, [onWaypointsChange, id, data]);

  // Drag a waypoint to move it; commit on release. Listens to BOTH pointer and
  // mouse events so it works across input methods (and automation).
  const beginWaypointDrag = useCallback((evt, index, seedWaypoints) => {
    evt.stopPropagation();
    evt.preventDefault();
    const base = seedWaypoints || savedWaypoints;
    const move = (e) => {
      const pos = screenToFlowPosition({ x: e.clientX, y: e.clientY });
      dragRef.current = { index, x: pos.x, y: pos.y };
      setDrag({ index, x: pos.x, y: pos.y });
    };
    const up = () => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
      window.removeEventListener('mousemove', move);
      window.removeEventListener('mouseup', up);
      const d = dragRef.current;
      dragRef.current = null;
      setDrag(null);
      if (d) {
        const next = base.map((p, i) => (i === d.index ? { x: d.x, y: d.y } : p));
        commitWaypoints(next);
      }
    };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
    window.addEventListener('mousemove', move);
    window.addEventListener('mouseup', up);
  }, [screenToFlowPosition, savedWaypoints, commitWaypoints]);

  const onWaypointPointerDown = useCallback((evt, index) => {
    beginWaypointDrag(evt, index, savedWaypoints);
  }, [beginWaypointDrag, savedWaypoints]);

  // Remove a waypoint (the "×" badge on the marker — an unambiguous target that
  // doesn't fight the drag gesture).
  const onWaypointDelete = useCallback((evt, index) => {
    evt.stopPropagation();
    evt.preventDefault();
    commitWaypoints(savedWaypoints.filter((_, i) => i !== index));
  }, [savedWaypoints, commitWaypoints]);

  // The "+" add-handle (shown on a selected edge): insert a waypoint at the
  // clicked point and immediately start dragging it. This is the reliable
  // add path — React Flow's onEdgeDoubleClick doesn't fire consistently.
  const onAddHandlePointerDown = useCallback((evt) => {
    const start = screenToFlowPosition({ x: evt.clientX, y: evt.clientY });
    const insertAt = waypointInsertIndex({ x: sx, y: sy }, savedWaypoints, { x: tx, y: ty }, start);
    const seeded = [...savedWaypoints];
    seeded.splice(insertAt, 0, { x: start.x, y: start.y });
    commitWaypoints(seeded);
    beginWaypointDrag(evt, insertAt, seeded);
  }, [screenToFlowPosition, savedWaypoints, sx, sy, tx, ty, commitWaypoints, beginWaypointDrag]);

  const onLabelDoubleClick = useCallback((evt) => {
    evt.stopPropagation();
    if (data?.onEdit) {
      data.onEdit();
    }
  }, [data]);

  const onDeleteClick = useCallback((evt) => {
    evt.stopPropagation();
    if (data?.onDelete) {
      data.onDelete();
    }
  }, [data]);
  const edgeStyle = useMemo(
    () => ({
      ...(style || {}),
      stroke: selected ? '#dc2626' : (style?.stroke || '#000000'),
      strokeWidth: selected ? 3 : (style?.strokeWidth || 2),
    }),
    [selected, style]
  );

  return (
    <>
      <BaseEdge path={edgePath} markerStart={markerStart} markerEnd={markerEnd} style={edgeStyle} />
      <EdgeLabelRenderer>
        {/* "+" add-handle on a selected edge: press to drop a waypoint and drag
            it into place in one gesture. */}
        {selected && (
          <div
            className="nodrag nopan"
            onPointerDown={onAddHandlePointerDown}
            title="Add a waypoint (drag to position)"
            style={{
              position: 'absolute',
              transform: `translate(-50%, -50%) translate(${(sx + tx) / 2}px, ${(sy + ty) / 2}px)`,
              width: 16,
              height: 16,
              borderRadius: '9999px',
              background: '#2563eb',
              color: '#ffffff',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 13,
              lineHeight: 1,
              fontWeight: 700,
              border: '2px solid #ffffff',
              boxShadow: '0 1px 3px rgba(0,0,0,0.35)',
              cursor: 'copy',
              pointerEvents: 'all',
              zIndex: 6,
              userSelect: 'none',
            }}
          >
            +
          </div>
        )}
        {/* Draggable waypoint markers (shown when the edge is selected or it
            already has bends). Drag to move, double-click to remove. */}
        {(selected || hasWaypoints) && waypoints.map((p, i) => (
          <div
            key={`wp-${i}`}
            className="nodrag nopan group/wp"
            onPointerDown={(e) => onWaypointPointerDown(e, i)}
            title="Drag to move this bend"
            style={{
              position: 'absolute',
              transform: `translate(-50%, -50%) translate(${p.x}px, ${p.y}px)`,
              width: 14,
              height: 14,
              borderRadius: '9999px',
              background: '#ffffff',
              border: '2px solid #2563eb',
              boxShadow: '0 1px 2px rgba(0,0,0,0.3)',
              cursor: 'grab',
              pointerEvents: 'all',
              zIndex: 5,
            }}
          >
            <button
              type="button"
              className="opacity-0 group-hover/wp:opacity-100 transition-opacity"
              onPointerDown={(e) => { e.stopPropagation(); e.preventDefault(); }}
              onClick={(e) => onWaypointDelete(e, i)}
              title="Remove this bend"
              style={{
                position: 'absolute',
                top: -9,
                right: -9,
                width: 15,
                height: 15,
                borderRadius: '9999px',
                background: '#dc2626',
                color: '#ffffff',
                border: '1.5px solid #ffffff',
                fontSize: 10,
                lineHeight: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                pointerEvents: 'all',
                zIndex: 7,
              }}
            >
              ×
            </button>
          </div>
        ))}
        <div
          style={{
            position: 'absolute',
            transform: `translate(-50%, -50%) translate(${labelX}px,${labelY}px)`,
            fontSize: 10,
            fontWeight: 500,
            pointerEvents: 'all',
          }}
          className="nodrag nopan"
          data-export-edge-label={id}
        >
          <div className="flex items-center gap-1">
            {/* Label with background - always show clickable area */}
            <div
              onDoubleClick={onLabelDoubleClick}
              style={{
                background: 'rgba(255, 255, 255, 0.9)',
                padding: '2px 6px',
                borderRadius: '3px',
                border: selected ? '1px solid #dc2626' : '1px solid #d1d5db',
                cursor: 'pointer',
                color: data?.label ? '#1f2937' : '#9ca3af',
                minWidth: '40px',
                textAlign: 'center',
              }}
              title="Double-click to edit label"
            >
              {data?.label || 'Add label'}
            </div>
            {selected && (
              <button
                type="button"
                onClick={onDeleteClick}
                className="bg-white border border-red-300 rounded p-1 text-red-500 hover:bg-red-50 hover:border-red-400 transition-colors"
                title="Delete connector"
              >
                <Trash2 className="h-3 w-3" />
              </button>
            )}
          </div>
        </div>
      </EdgeLabelRenderer>
    </>
  );
}

const nodeTypes = {
  swimlane: SwimlaneNode,
  rectangle: RectangleNode,
  decision: DecisionNode,
  oval: OvalNode,
};

const edgeTypes = {
  custom: CustomEdge,
};

function FlowchartViewer({
  flowchartData,
  processData,
  onBack,
  onComplete,
  cycleId,
  processId,
  flowchartVersionId,
  setFlowchartVersionId = () => { },
  flowchartVersionStatus,
  setFlowchartVersionStatus = () => { },
  flowchartVersionNumber,
  setFlowchartVersionNumber = () => { },
  selectedVersionNumber = null,
  onVersionNumberChange = async () => true,
  onRefreshForVersionNumber = async () => true,
  syncInitialPositions = false,
}) {
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);
  const [isLayouting, setIsLayouting] = useState(true);

  // Store viewport state for placing new nodes
  const viewportRef = useRef({ x: 0, y: 0, zoom: 1 });
  const reactFlowWrapper = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();

  const { allProcesses, versionOptions, refetchVersionOptions } = useFlowchartQueries({
    cycleId,
    processId
  });
  const { selectedAuditYearId } = useAuditYear();
  const { publishFlowchartMutation, setActiveFlowchartMutation } = useFlowchartMutations();
  const queryClient = useQueryClient();

  // Filter processes with flowcharts
  const processesWithFlowcharts = allProcesses.filter(p => p.has_flowchart);

  // Handle process navigation
  const handleProcessChange = useCallback((selectedProcessId) => {
    if (selectedProcessId && selectedProcessId !== processId) {
      navigate(soxProcessFlowchartPath(cycleId, selectedProcessId), {
        state: location.state,
      });
    }
  }, [cycleId, processId, navigate, location.state]);

  // Key systems state - global tracking for keySystem field
  const [keySystems, setKeySystems] = useState(new Set());
  // Catalog name -> { id } for resolving system UUIDs on save when node memory has none.
  const [systemsCatalogByLowerName, setSystemsCatalogByLowerName] = useState({});

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axiosInstance.get('/api/systems', { params: { page_size: 200 } });
        if (cancelled) return;
        const list = Array.isArray(res.data?.systems) ? res.data.systems : [];
        const map = {};
        for (const s of list) {
          const name = typeof s?.name === 'string' ? s.name.trim() : '';
          const id = typeof s?.id === 'string' ? s.id.trim() : '';
          if (!name || !id) continue;
          map[name.toLowerCase()] = { id };
        }
        setSystemsCatalogByLowerName(map);
      } catch {
        // Save still works via system text; key flags sync only when ids resolve.
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const registerSystemInCatalog = useCallback((system) => {
    const id = typeof system?.id === 'string' ? system.id.trim() : '';
    const name = typeof system?.name === 'string' ? system.name.trim() : '';
    if (!id || !name) return;
    setSystemsCatalogByLowerName((prev) => ({
      ...prev,
      [name.toLowerCase()]: { id },
    }));
  }, []);

  // Edit/Add modal state
  const [editingNode, setEditingNode] = useState(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isAddingNode, setIsAddingNode] = useState(false); // Track if adding vs editing
  const [isAddingNewActor, setIsAddingNewActor] = useState(false); // Track if user is adding new actor
  const [editForm, setEditForm] = useState({
    nodeDescription: '',
    actor: '',
    system: '',
    soxControl: '',
    document: '',
    documents: [],
    nodeType: 'process',
    keySystem: false,
    keyControl: false
  });
  const [nodeFormErrors, setNodeFormErrors] = useState({});
  // Which attached document card is expanded for edit (id or doc-${index}); null = all collapsed.
  const [editingDocumentKey, setEditingDocumentKey] = useState(null);

  // Save state
  const [isPublishing, setIsPublishing] = useState(false);
  const [isSettingActive, setIsSettingActive] = useState(false);
  const [isActionsMenuOpen, setIsActionsMenuOpen] = useState(false);
  const [isProcessMenuOpen, setIsProcessMenuOpen] = useState(false);
  const [isVersionMenuOpen, setIsVersionMenuOpen] = useState(false);
  const [isStatusMenuOpen, setIsStatusMenuOpen] = useState(false);
  const [isAutoSaving, setIsAutoSaving] = useState(false);
  const [isCatalogRisksPanelOpen, setIsCatalogRisksPanelOpen] = useState(false);
  const [isSubprocessLinksPanelOpen, setIsSubprocessLinksPanelOpen] = useState(false);
  const [subprocessLinks, setSubprocessLinks] = useState([]);
  const [saveMessage, setSaveMessage] = useState('');
  const [lastFlowchartSavedAt, setLastFlowchartSavedAt] = useState(null);
  const messageQueueRef = useRef([]);
  const messageTimerRef = useRef(null);
  const actionsMenuRef = useRef(null);
  const processMenuRef = useRef(null);
  const versionMenuRef = useRef(null);
  const versionMenuListRef = useRef(null);
  const statusMenuRef = useRef(null);

  const showNextMessage = useCallback(() => {
    if (messageQueueRef.current.length === 0) {
      setSaveMessage('');
      return;
    }
    const { text, duration } = messageQueueRef.current.shift();
    setSaveMessage(text);
    messageTimerRef.current = setTimeout(() => {
      messageTimerRef.current = null;
      showNextMessage();
    }, duration);
  }, []);

  const queueSaveMessage = useCallback((text, duration = 2000) => {
    messageQueueRef.current.push({ text, duration });
    if (!messageTimerRef.current) {
      showNextMessage();
    }
  }, [showNextMessage]);

  useEffect(() => {
    return () => {
      if (messageTimerRef.current) {
        clearTimeout(messageTimerRef.current);
        messageTimerRef.current = null;
      }
      messageQueueRef.current = [];
    };
  }, []);

  useEffect(() => {
    if (!isActionsMenuOpen) return undefined;

    const handleDocumentClick = (event) => {
      if (actionsMenuRef.current && !actionsMenuRef.current.contains(event.target)) {
        setIsActionsMenuOpen(false);
      }
    };

    const handleEscape = (event) => {
      if (event.key === 'Escape') {
        setIsActionsMenuOpen(false);
      }
    };

    document.addEventListener('pointerdown', handleDocumentClick, true);
    document.addEventListener('keydown', handleEscape);

    return () => {
      document.removeEventListener('pointerdown', handleDocumentClick, true);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isActionsMenuOpen]);

  useEffect(() => {
    if (!isProcessMenuOpen) return undefined;

    const handleDocumentClick = (event) => {
      if (processMenuRef.current && !processMenuRef.current.contains(event.target)) {
        setIsProcessMenuOpen(false);
      }
    };

    const handleEscape = (event) => {
      if (event.key === 'Escape') {
        setIsProcessMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleDocumentClick);
    document.addEventListener('keydown', handleEscape);

    return () => {
      document.removeEventListener('mousedown', handleDocumentClick);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isProcessMenuOpen]);

  useEffect(() => {
    if (!isVersionMenuOpen) return undefined;

    const handleDocumentClick = (event) => {
      if (versionMenuRef.current && !versionMenuRef.current.contains(event.target)) {
        setIsVersionMenuOpen(false);
      }
    };

    const handleEscape = (event) => {
      if (event.key === 'Escape') {
        setIsVersionMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleDocumentClick);
    document.addEventListener('keydown', handleEscape);

    return () => {
      document.removeEventListener('mousedown', handleDocumentClick);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isVersionMenuOpen]);

  useEffect(() => {
    if (!isStatusMenuOpen) return undefined;

    const handleDocumentClick = (event) => {
      if (statusMenuRef.current && !statusMenuRef.current.contains(event.target)) {
        setIsStatusMenuOpen(false);
      }
    };

    const handleEscape = (event) => {
      if (event.key === 'Escape') {
        setIsStatusMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleDocumentClick);
    document.addEventListener('keydown', handleEscape);

    return () => {
      document.removeEventListener('mousedown', handleDocumentClick);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isStatusMenuOpen]);

  // Process metadata state - initialize from processData if available
  const [processName, setProcessName] = useState(processData?.name || 'New Process');
  const [processOwner, setProcessOwner] = useState(processData?.owner || 'Unassigned');
  const [processStatus, setProcessStatus] = useState(
    normalizeSoxDashboardProcessStatus(processData?.status)
  );
  const [isStatusUpdating, setIsStatusUpdating] = useState(false);
  const [swimlaneRemapSaveNonce, setSwimlaneRemapSaveNonce] = useState(0);

  const normalizedFlowchartVersionStatus = useMemo(
    () => String(flowchartVersionStatus ?? '').trim().toLowerCase(),
    [flowchartVersionStatus]
  );
  const [isVersionChanging, setIsVersionChanging] = useState(false);
  const dropdownOptions = useMemo(() => {
    const normalizedOptions = (Array.isArray(versionOptions) ? versionOptions : [])
      .map((option) => ({
        ...option,
        value: option?.value == null ? null : Number(option.value),
        status: String(option?.status ?? '').trim().toLowerCase(),
        isActive: option?.isActive === true,
        flowchartVersionId: option?.flowchartVersionId || null,
      }))
      .filter((option) => option.label && (option.value == null || !Number.isNaN(option.value)));

    return normalizedOptions.sort((a, b) => {
      if (a.value == null && b.value == null) return 0;
      if (a.value == null) return -1;
      if (b.value == null) return 1;
      return b.value - a.value;
    });
  }, [versionOptions]);
  const selectedVersionOption = useMemo(
    () => dropdownOptions.find((option) => option.value === selectedVersionNumber) || null,
    [dropdownOptions, selectedVersionNumber]
  );
  const selectedProcess = useMemo(
    () => processesWithFlowcharts.find((process) => String(process.process_id) === String(processId)) || null,
    [processesWithFlowcharts, processId]
  );
  const selectedProcessLabel = selectedProcess?.name || 'Select process';
  const selectedVersionLabel = selectedVersionOption?.label || 'Select version';
  const selectedStatusLabel = useMemo(
    () =>
      SOX_DASHBOARD_STATUS_OPTIONS.find((option) => option.value === processStatus)?.label || 'Select status',
    [processStatus]
  );
  const isSelectedVersionActive = useMemo(() => {
    if (!selectedVersionOption) return false;
    return selectedVersionOption.isActive || selectedVersionOption.status === 'active';
  }, [selectedVersionOption]);
  const isSelectedVersionDraft = useMemo(
    () => selectedVersionOption?.status === 'draft',
    [selectedVersionOption]
  );
  const isSelectedVersionPublished = useMemo(
    () => selectedVersionOption?.status === 'published',
    [selectedVersionOption]
  );
  const isFlowchartPublished = useMemo(
    () => (selectedVersionOption ? isSelectedVersionPublished : normalizedFlowchartVersionStatus === 'published'),
    [selectedVersionOption, isSelectedVersionPublished, normalizedFlowchartVersionStatus]
  );
  const isFlowchartPublishedRef = useRef(isFlowchartPublished);
  useEffect(() => {
    isFlowchartPublishedRef.current = isFlowchartPublished;
  }, [isFlowchartPublished]);
  const isFlowchartVersionActive = useMemo(() => {
    if (selectedVersionOption) return isSelectedVersionActive;
    return normalizedFlowchartVersionStatus === 'active';
  }, [isSelectedVersionActive, normalizedFlowchartVersionStatus, selectedVersionOption]);
  const selectedFlowchartVersionId = selectedVersionOption?.flowchartVersionId || flowchartVersionId;

  // Update process metadata when processData changes
  useEffect(() => {
    if (processData) {
      const nextProcessName = processData.name || 'New Process';
      const nextProcessOwner = processData.owner || 'Unassigned';
      setProcessName(nextProcessName);
      setProcessOwner(nextProcessOwner);
      setProcessStatus(normalizeSoxDashboardProcessStatus(processData.status));
      // Server-hydrated process metadata should be treated as already saved.
      lastSavedProcessMetaRef.current = {
        processName: nextProcessName,
        processOwner: nextProcessOwner,
      };
    }
  }, [processData]);

  const handleProcessStatusChange = useCallback(
    async (nextStatus) => {
      const normalizedStatus = normalizeSoxDashboardProcessStatus(nextStatus);
      if (!SOX_STATUS_VALUES.has(normalizedStatus)) {
        queueSaveMessage('✗ Invalid status selected');
        return;
      }

      if (normalizedStatus === processStatus) return;

      const processUuid = processData?.id || processData?.process_id || processId;
      if (!processUuid) {
        queueSaveMessage('✗ Process ID not found');
        return;
      }

      const previousStatus = processStatus;
      setProcessStatus(normalizedStatus);
      setIsStatusUpdating(true);

      try {
        const response = await axiosInstance.put(
          API_ENDPOINTS.putProcessStatus(processUuid),
          { status: normalizedStatus }
        );

        if (response?.data?.success === false) {
          setProcessStatus(previousStatus);
          queueSaveMessage('✗ Failed to update status');
          return;
        }

        setProcessStatus(normalizedStatus);
        queueSaveMessage('✓ Status updated', 2000);

        const invalidations = [
          queryClient.invalidateQueries({ queryKey: processesQueryKey(cycleId) }),
          queryClient.invalidateQueries({ queryKey: cycleProcessesQueryKey(cycleId) }),
          queryClient.invalidateQueries({ queryKey: SOX_CYCLES_QUERY_KEY }),
          queryClient.invalidateQueries({ queryKey: ['process', processUuid] }),
        ];
        if (processId && processId !== processUuid) {
          invalidations.push(
            queryClient.invalidateQueries({ queryKey: ['process', processId] })
          );
        }
        await Promise.all(invalidations);
      } catch (error) {
        console.error('Status update error:', error);
        setProcessStatus(previousStatus);
        queueSaveMessage(`✗ ${resolve403ErrorMessage(error, 'Failed to update status')}`);
      } finally {
        setIsStatusUpdating(false);
      }
    },
    [cycleId, processData, processId, processStatus, queryClient, queueSaveMessage]
  );

  const activeFlowchartData = flowchartData;
  const processUuid = processData?.id || processData?.process_uuid || processId || null;
  const catalogPanelFlowchartNodes = useMemo(() => {
    const rawNodes = activeFlowchartData?.nodes;
    if (!Array.isArray(rawNodes)) return [];
    return rawNodes
      .filter((node) => {
        const type = String(node?.nodeType || '').toLowerCase();
        return type !== 'start' && type !== 'end' && type !== 'decision' && node?.id;
      })
      .map((node) => ({
        id: node.id,
        nodeDescription: node.nodeDescription || '',
        nodeType: node.nodeType || '',
      }));
  }, [activeFlowchartData]);

  const subprocessPanelFlowchartNodes = useMemo(() => {
    return (Array.isArray(nodes) ? nodes : [])
      .filter((node) => {
        if (!node?.id || node.type === 'swimlane') return false;
        const type = String(node?.data?.nodeType || node?.type || '').toLowerCase();
        return type !== 'start' && type !== 'end';
      })
      .map((node) => ({
        id: node.id,
        nodeDescription: node.data?.nodeDescription || '',
        nodeType: node.data?.nodeType || '',
        actor: node.data?.actor || node.data?.actorRef?.name || '',
      }));
  }, [nodes]);

  const toggleCatalogRisksPanel = useCallback(() => {
    // Same gate as Identify Risks: no open during Ctrl+Z/Y autosave pause.
    if (flowchartUndoPersistPausedRef.current) return;
    setIsSubprocessLinksPanelOpen(false);
    setIsCatalogRisksPanelOpen((prev) => !prev);
  }, []);

  const toggleSubprocessLinksPanel = useCallback(() => {
    setIsCatalogRisksPanelOpen(false);
    setIsSubprocessLinksPanelOpen((prev) => !prev);
  }, []);

  useEffect(() => {
    let cancelled = false;
    if (!flowchartVersionId) {
      setSubprocessLinks([]);
      return undefined;
    }
    fetchFlowchartSubprocessLinks(flowchartVersionId)
      .then((rows) => {
        if (!cancelled) setSubprocessLinks(Array.isArray(rows) ? rows : []);
      })
      .catch(() => {
        if (!cancelled) setSubprocessLinks([]);
      });
    return () => {
      cancelled = true;
    };
  }, [flowchartVersionId]);

  // Swimlane boundaries for drag detection
  const [swimlaneBoundaries, setSwimlaneBoundaries] = useState([]);

  // Live highlight of the lane under a dragged node (green = valid drop, red =
  // straddling a border). Backed by a ref so drag frames can dedupe cheaply and
  // only push a state update when the highlight actually changes.
  const [swimlaneDragHighlight, setSwimlaneDragHighlight] = useState(null);
  const swimlaneDragHighlightRef = useRef(null);

  // Per-actor swimlane heights. Seeded from the BE payload on load and
  // mutated locally on user resize; a debounced effect PATCHes the
  // server so the next load comes back with the same dimensions.
  const [swimlaneHeights, setSwimlaneHeights] = useState({});
  // Tracks actors whose heights changed locally since the last successful
  // PATCH — only these get sent on flush, so we don't re-PATCH the whole
  // map every time one lane moves.
  const dirtyActorsRef = useRef(new Set());
  const swimlaneSaveTimerRef = useRef(null);
  // Distinguishes a user-driven lane resize (manual grip drag or drop-triggered
  // auto-grow) from a hydration/alignment height change. Only user resizes push
  // the lanes below down and shift their nodes; hydration just realigns the lane
  // backgrounds because the nodes are already at their persisted positions.
  const userSwimlaneResizeRef = useRef(false);
  // Established top-to-bottom lane display order (by actor). New actors are
  // appended so a freshly created swimlane always lands last and keeps that slot
  // across re-layouts, regardless of node array order or server refreshes.
  const swimlaneOrderRef = useRef([]);
  // Node ids that must snap to their lane's freshly-computed vertical center on
  // the next layout (e.g. a node moved to a new/other actor). Their X is
  // preserved; only Y is taken from the layout so they always sit INSIDE the
  // lane, never below it — the pre-move Y estimate can be stale once lanes are
  // added/removed and geometry shifts.
  const pendingLaneCenterNodeIdsRef = useRef(new Set());

  useEffect(() => {
    const layouts = activeFlowchartData?.swimlaneLayouts;
    if (!Array.isArray(layouts) || layouts.length === 0) return;
    setSwimlaneHeights((prev) => {
      const next = { ...prev };
      let changed = false;
      layouts.forEach((entry) => {
        const actor = (entry?.actorName || '').trim();
        const h = Number(entry?.swimlaneHeight);
        if (!actor || !Number.isFinite(h) || h <= 0) return;
        if (next[actor] !== h && !dirtyActorsRef.current.has(actor)) {
          next[actor] = h;
          changed = true;
        }
      });
      return changed ? next : prev;
    });
  }, [activeFlowchartData]);

  // Keep the latest version id and heights in refs so the debounced flush —
  // bound into each swimlane node's onResize at layout time — never reads a
  // stale closure (which silently dropped the persist call before).
  const flowchartVersionIdRef = useRef(flowchartVersionId);
  useEffect(() => { flowchartVersionIdRef.current = flowchartVersionId; }, [flowchartVersionId]);
  const swimlaneHeightsRef = useRef(swimlaneHeights);
  useEffect(() => { swimlaneHeightsRef.current = swimlaneHeights; }, [swimlaneHeights]);

  const flushSwimlaneLayoutSave = useCallback(() => {
    const versionId = flowchartVersionIdRef.current;
    if (!versionId) return Promise.resolve();
    const heights = swimlaneHeightsRef.current || {};
    const dirtyActors = Array.from(dirtyActorsRef.current);
    if (dirtyActors.length === 0) return Promise.resolve();
    const layouts = dirtyActors
      // Round to an integer — the backend's swimlaneHeight is an int, and a
      // float body (from drag math) fails to bind => 400 "Invalid request body".
      .map((actor) => ({ actorName: actor, swimlaneHeight: Math.round(heights[actor]) }))
      .filter((l) => Number.isFinite(l.swimlaneHeight) && l.swimlaneHeight > 0);
    if (layouts.length === 0) return Promise.resolve();
    dirtyActorsRef.current = new Set();
    return updateFlowchartSwimlaneLayout(versionId, layouts).catch((err) => {
      // Do NOT re-queue on failure — a rejected payload would retry forever.
      console.error('Failed to persist swimlane layout:', err);
      if (err?.response?.status === 403) {
        queueSaveMessage(`✗ ${resolve403ErrorMessage(err, 'Failed to save swimlane layout')}`);
      }
    });
  }, [queueSaveMessage]);

  const handleSwimlaneResize = useCallback((actor, nextHeight) => {
    const trimmed = (actor || '').trim();
    if (!trimmed || !Number.isFinite(nextHeight) || nextHeight <= 0) return;
    // Enforce the lane height bounds centrally so no resize path (grip drag,
    // drop auto-grow, etc.) can shrink a lane below the 120px minimum.
    const clampedHeight = Math.max(SWIMLANE_MIN_HEIGHT, Math.min(SWIMLANE_MAX_HEIGHT, nextHeight));
    setSwimlaneHeights((prev) => {
      if (prev[trimmed] === clampedHeight) return prev;
      // Mark this as a user-driven resize so the geometry effect shifts the
      // lanes/nodes below instead of treating it like a hydration realign.
      userSwimlaneResizeRef.current = true;
      return { ...prev, [trimmed]: clampedHeight };
    });
    dirtyActorsRef.current.add(trimmed);
    if (swimlaneSaveTimerRef.current) {
      clearTimeout(swimlaneSaveTimerRef.current);
    }
    swimlaneSaveTimerRef.current = setTimeout(() => {
      swimlaneSaveTimerRef.current = null;
      flushSwimlaneLayoutSave();
    }, SWIMLANE_LAYOUT_SAVE_DEBOUNCE_MS);
    // The layout effect re-runs whenever swimlaneHeights changes
    // (see its deps), so the cumulative yStart math picks up the new
    // height on the next render without an explicit trigger bump.
  }, [flushSwimlaneLayoutSave]);

  useEffect(() => () => {
    if (swimlaneSaveTimerRef.current) {
      clearTimeout(swimlaneSaveTimerRef.current);
      flushSwimlaneLayoutSave();
    }
  }, [flushSwimlaneLayoutSave]);

  // Delete confirmation state
  const [deleteConfirmation, setDeleteConfirmation] = useState(null); // { nodeId, nodeTitle, edgeCount }
  const [selectedNodes, setSelectedNodes] = useState([]);
  const [selectedEdges, setSelectedEdges] = useState([]);

  // Edge edit modal state
  const [editingEdge, setEditingEdge] = useState(null);
  const [isEdgeEditModalOpen, setIsEdgeEditModalOpen] = useState(false);
  const [edgeEditForm, setEdgeEditForm] = useState({ label: '', isBidirectional: false });
  const [edgeLabelError, setEdgeLabelError] = useState('');
  const [isPendingConnectorConfirmation, setIsPendingConnectorConfirmation] = useState(false);

  // Swimlane edit modal state
  const [editingSwimlane, setEditingSwimlane] = useState(null); // { oldActor, label }
  const [isSwimlaneEditModalOpen, setIsSwimlaneEditModalOpen] = useState(false);
  const [swimlaneEditForm, setSwimlaneEditForm] = useState({ newActor: '' });
  const [swimlaneEditError, setSwimlaneEditError] = useState('');
  const [swimlaneDeleteConfirmation, setSwimlaneDeleteConfirmation] = useState(null);
  const pendingSwimlaneReorderPersistRef = useRef(false);

  // New swimlane modal state
  const [isNewSwimlaneModalOpen, setIsNewSwimlaneModalOpen] = useState(false);
  const [newSwimlaneForm, setNewSwimlaneForm] = useState({
    actorName: '',
    placement: 'bottom', // bottom | before | after
    relativeTo: '',
  });
  const [newSwimlaneError, setNewSwimlaneError] = useState('');
  const [isMiniMapVisible, setIsMiniMapVisible] = useState(false);
  const [isFlowchartExpanded, setIsFlowchartExpanded] = useState(false);

  useEffect(() => {
    if (!isFlowchartExpanded) {
      setIsActionsMenuOpen(false);
    }
  }, [isFlowchartExpanded]);

  // Trigger to force re-layout after swimlane changes
  const [layoutTrigger, setLayoutTrigger] = useState(0);

  // Risk identification state
  const [identifiedRisks, setIdentifiedRisks] = useState([]); // Array of risks from AI
  const identifiedRisksRef = useRef([]);
  useEffect(() => { identifiedRisksRef.current = identifiedRisks; }, [identifiedRisks]);
  const [isIdentifyingRisks, setIsIdentifyingRisks] = useState(false);
  const [selectedRisk, setSelectedRisk] = useState(null); // For modal display
  const [isRiskModalOpen, setIsRiskModalOpen] = useState(false);
  const [riskDeleteConfirmation, setRiskDeleteConfirmation] = useState(null);
  const [riskAnalysisProgress, setRiskAnalysisProgress] = useState(0);
  const [riskAnalysisSteps, setRiskAnalysisSteps] = useState([
    { name: 'Collecting process steps', status: 'pending' },
    { name: 'Analyzing SOX control points', status: 'pending' },
    { name: 'Identifying potential risks', status: 'pending' },
    { name: 'Evaluating risk levels', status: 'pending' },
    { name: 'Generating recommendations', status: 'pending' }
  ]);

  // Control indicator state (for risk-control mappings)
  const [riskControlMappings, setRiskControlMappings] = useState({});
  const [selectedControlIndicator, setSelectedControlIndicator] = useState(null);
  const [isControlModalOpen, setIsControlModalOpen] = useState(false);
  const updateRiskMutation = useUpdateRisk();
  const [savingRiskId, setSavingRiskId] = useState(null);
  const {
    checkAccess: checkRisksModuleAccess,
    dialogProps: risksModuleAccessDialogProps,
  } = useRequireModuleAccess(MODULE_KEYS.RISKS, {
    hint:
      'Creating, editing, or deleting risks from a flowchart requires the Risks module. You can still view risks and link catalog risks to subprocesses. Contact your organization administrator if you need access.',
  });

  const deleteIdentifiedRiskMutation = useMutation({
    mutationFn: async (risk) => {
      const recordId = getRiskRecordId(risk);
      if (!recordId) {
        throw new Error('This risk cannot be deleted because it does not have an identifier.');
      }

      const userHash = localStorage.getItem('userHash');
      await axiosInstance.delete(`/api/process-risks/${recordId}`, {
        headers: { Authorization: userHash || '' },
      });

      return recordId;
    },
    onMutate: (risk) => {
      const recordId = getRiskRecordId(risk);
      if (recordId) {
        setSavingRiskId(recordId);
      }
      return { recordId };
    },
    onSuccess: async (recordId) => {
      setIdentifiedRisks((prev) => prev.filter((entry) => getRiskRecordId(entry) !== recordId));
      setSelectedRisk((prev) => {
        if (!prev) return prev;
        const remainingRisks = prev.risks.filter((entry) => getRiskRecordId(entry) !== recordId);
        if (remainingRisks.length === 0) {
          setIsRiskModalOpen(false);
          return null;
        }
        return {
          ...prev,
          risks: remainingRisks,
        };
      });

      await queryClient.invalidateQueries({ queryKey: queryKeys.risks.all });
      await refreshFlowchartDataRef.current?.();
      toast.success('Risk deleted');
    },
    onError: (error) => {
      console.error('Error deleting identified risk:', error);
      toast.error(
        resolve403ErrorMessage(
          error,
          error instanceof Error ? error.message : 'Failed to delete risk. Please try again.'
        )
      );
    },
    onSettled: (_data, _error, risk) => {
      const recordId = getRiskRecordId(risk);
      setRiskDeleteConfirmation(null);
      setSavingRiskId((current) => (current && recordId && current === recordId ? null : current));
    },
  });

  // Refs to track current nodes/edges for re-layout
  const nodesRef = useRef([]);
  const edgesRef = useRef([]);
  const hasInitializedAutoSaveRef = useRef(false);
  const hasUnsavedChangesRef = useRef(false);
  const lastSavedFlowchartRef = useRef(null);
  const lastSavedProcessMetaRef = useRef(null);
  const autoSaveInFlightRef = useRef(false);
  const pendingAutoSaveRef = useRef(false);
  const lastHydratedComparableFlowchartRef = useRef(null);
  const runAutoSaveRef = useRef(null);
  const updateNodePositionsRef = useRef(null);
  const persistFlowchartRef = useRef(null);
  const refreshFlowchartDataRef = useRef(null);
  const draggingNodeIdsRef = useRef(new Set());
  const handleDeleteClickRef = useRef(null);
  const handleEditNodeRef = useRef(null);
  const handleEdgeEditRef = useRef(null);
  const handleEdgeDeleteRef = useRef(null);
  const handleRiskClickRef = useRef(null);
  const handleHexagonControlClickRef = useRef(null);
  const handleHexagonControlsOverflowClickRef = useRef(null);
  const pendingSwimlaneRemapSaveRef = useRef(false);
  const hasUserMutatedFlowchartRef = useRef(false);
  const pendingKeySystemSaveRef = useRef(false);
  const pendingDocumentCatalogSaveRef = useRef(false);
  // Snapshot of nodes before first in-modal catalog stamp; restored on Cancel.
  const nodesBeforeDocumentCatalogEditRef = useRef(null);
  const keySystemsHydratedRef = useRef(false);
  const initialPositionSyncDoneRef = useRef(false);
  const suppressAutoSaveDuringVersionSwitchRef = useRef(false);
  const suppressAutoSaveDuringPublishRef = useRef(false);
  const skipNextPersistAfterPublishRef = useRef(false);
  const flowchartUndoPersistPausedRef = useRef(false);
  const flowchartUndoSuppressGetRef = useRef(false);
  const flowchartUndoIgnorePersistResultRef = useRef(false);
  const flowchartUndoNotifyPersistCaughtUpRef = useRef(null);
  const flowchartUndoRequestFlushNowRef = useRef(null);
  // Immediate stack record for swimlane height / reorder / position (paths that skip mutated gate).
  const flowchartUndoRecordCanvasNowRef = useRef(null);
  const flowchartUndoBeginLiveAbsorbRef = useRef(null);
  const positionOnlySyncInFlightRef = useRef(false);
  const nodePositionPatchInFlightRef = useRef(false);
  const pendingNodePositionPatchesRef = useRef(new Map());
  const pendingNodePositionPatchIncludesPositionOnlyRef = useRef(false);
  const pendingNodePositionPatchIncludesNonPositionOnlyRef = useRef(false);
  const dragStartSwimlaneByNodeIdRef = useRef(new Map());
  const useSwimlaneSnapLogicRef = useRef(true);
  const suppressNodeChangePersistenceUntilRef = useRef(0);

  // Reset autosave state when navigating to a different process/cycle
  useEffect(() => {
    hasInitializedAutoSaveRef.current = false;
    hasUnsavedChangesRef.current = false;
    hasUserMutatedFlowchartRef.current = false;
    lastSavedFlowchartRef.current = null;
    useSwimlaneSnapLogicRef.current = true;
    lastSavedProcessMetaRef.current = null;
    lastHydratedComparableFlowchartRef.current = null;
    autoSaveInFlightRef.current = false;
    pendingAutoSaveRef.current = false;
    swimlaneOrderRef.current = [];
    setLastFlowchartSavedAt(null);
  }, [processId, cycleId]);

  // Switching versions hydrates a different server snapshot; reset autosave session
  // so the next render establishes a fresh baseline instead of saving immediately.
  useEffect(() => {
    hasInitializedAutoSaveRef.current = false;
    hasUnsavedChangesRef.current = false;
    hasUserMutatedFlowchartRef.current = false;
    pendingAutoSaveRef.current = false;
    lastHydratedComparableFlowchartRef.current = null;
    lastSavedFlowchartRef.current = null;
    useSwimlaneSnapLogicRef.current = true;
    suppressAutoSaveDuringVersionSwitchRef.current = true;
    swimlaneOrderRef.current = [];
    setLastFlowchartSavedAt(null);
    // Force server hydration on the next layout. Otherwise a prior local
    // re-layout (e.g. add swimlane) leaves layoutTrigger > 0 and every version
    // switch keeps painting the in-memory graph until a full page refresh.
    setLayoutTrigger(0);
  }, [selectedVersionNumber]);

  useEffect(() => {
    if (isVersionChanging) {
      suppressAutoSaveDuringVersionSwitchRef.current = true;
      return;
    }

    // Clear suppression once dropdown interaction settles and selected version
    // matches the currently hydrated flowchart version.
    if (selectedVersionNumber === flowchartVersionNumber) {
      suppressAutoSaveDuringVersionSwitchRef.current = false;
    }
  }, [isVersionChanging, selectedVersionNumber, flowchartVersionNumber]);

  useEffect(() => {
    initialPositionSyncDoneRef.current = false;
  }, [flowchartVersionId, processId, cycleId]);

  // Update refs whenever nodes/edges change
  useEffect(() => {
    nodesRef.current = nodes;
    edgesRef.current = edges;
  }, [nodes, edges]);

  const nodeTypeById = useMemo(
    () => new Map((nodes || []).map((node) => [node.id, node.type])),
    [nodes]
  );
  const backendPositionByNodeId = useMemo(
    () =>
      new Map(
        ((activeFlowchartData?.nodes || []).map((node) => [
          node?.id,
          node?.position && typeof node.position === 'object'
            ? { x: node.position.x, y: node.position.y }
            : null,
        ]))
      ),
    [activeFlowchartData]
  );
  const getMovedNodePositions = useMemo(
    () => (changes) => {
      const movedNodePositions = [];
      changes.forEach((change) => {
        if (change.type !== 'position' || !change.position) return;

        if (change.dragging === true) {
          draggingNodeIdsRef.current.add(change.id);
          return;
        }

        if (change.dragging === false && draggingNodeIdsRef.current.has(change.id)) {
          draggingNodeIdsRef.current.delete(change.id);
          if (nodeTypeById.get(change.id) === 'swimlane') return;
          movedNodePositions.push({
            nodeId: change.id,
            position: {
              x: change.position.x,
              y: change.position.y,
            },
          });
        }
      });
      return movedNodePositions;
    },
    [nodeTypeById]
  );

  // Initialize keySystems from backend data when flowchart payload loads
  useEffect(() => {
    if (activeFlowchartData) {
      const transformed = transformDataToFlow(activeFlowchartData);
      setKeySystems(transformed.keySystems || new Set());
      keySystemsHydratedRef.current = true;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeFlowchartData]); // Hydrate from server or local draft

  // Restore saved risks from flowchart data (only once on mount)
  useEffect(() => {
    if (activeFlowchartData) {
      const transformed = transformDataToFlow(activeFlowchartData);
      setIdentifiedRisks(Array.isArray(transformed.savedRisks) ? transformed.savedRisks : []);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeFlowchartData]); // Hydrate from server or local draft

  const fetchRiskControlMappings = useCallback(async () => {
    if (!processId) {
      setRiskControlMappings({});
      return {};
    }
    try {
      const userHash = localStorage.getItem('userHash');
      const queryParams = (
        selectedVersionNumber != null &&
        !Number.isNaN(Number(selectedVersionNumber))
      )
        ? { versionNumber: Number(selectedVersionNumber) }
        : undefined;
      const response = await axiosInstance.get(
        `/api/risk-control-mappings/process/${processId}`,
        {
          headers: { 'Authorization': userHash || '' },
          ...(queryParams ? { params: queryParams } : {}),
        }
      );
      const mappingsArray = Array.isArray(response.data) ? response.data : [];
      const mappingsObj = {};
      mappingsArray.forEach(m => {
        const key = getRiskMappingKey(m?.node_id, m);
        if (!key) return;
        mappingsObj[key] = {
          controlId: m.sox_control_id,
          controlDisplayId: m.control_display_id,
          controlTitle: m.control_title,
          controlType: m.control_type,
          significance: m.significance,
          isAIRecommendation: m.is_ai_recommendation
        };
      });
      setRiskControlMappings(mappingsObj);
      return mappingsObj;
    } catch (error) {
      console.error('Error fetching risk-control mappings:', error);
      setRiskControlMappings({});
      return {};
    }
  }, [processId, selectedVersionNumber]);

  useEffect(() => {
    if (!isVersionMenuOpen) return;
    const listElement = versionMenuListRef.current;
    if (!listElement) return;
    const preferredOption = listElement.querySelector('[data-version-selected="true"]')
      || listElement.querySelector('[data-version-active="true"]');
    if (!preferredOption || typeof preferredOption.scrollIntoView !== 'function') return;
    preferredOption.scrollIntoView({ block: 'center', behavior: 'auto' });
  }, [isVersionMenuOpen, dropdownOptions, selectedVersionNumber]);

  // Fetch risk-control mappings for this process/version.
  useEffect(() => {
    fetchRiskControlMappings();
  }, [fetchRiskControlMappings]);

  // Transform and layout the data (async)
  useEffect(() => {
    let cancelled = false;
    const layoutNodes = async () => {
      setIsLayouting(true);

      // Any explicit re-layout trigger should use current in-memory graph state.
      // Hydration/initial loads (layoutTrigger===0) should use server payload.
      // During version switch, always hydrate from server even if layoutTrigger
      // has not yet flushed back to 0 in this same tick.
      let nodesToLayout, edgesToLayout;
      const shouldUseLocalGraph =
        layoutTrigger > 0 && !suppressAutoSaveDuringVersionSwitchRef.current;
      if (shouldUseLocalGraph) {
        nodesToLayout = nodesRef.current.filter(n => n.type !== 'swimlane');
        edgesToLayout = edgesRef.current;
      } else {
        // Incomplete server snapshot: skip so a slower remote GET/layout race
        // cannot paint empty edges/nodes over a good canvas (dev-only flake).
        const liveEdgesBeforeHydrate = (edgesRef.current || []).length;
        const incomingConnectors = Array.isArray(activeFlowchartData?.connectors)
          ? activeFlowchartData.connectors.length
          : null;
        if (
          isIncompleteFlowchartPayload(activeFlowchartData) &&
          ((edgesRef.current || []).length > 0 ||
            (nodesRef.current || []).some((n) => n.type !== 'swimlane'))
        ) {
          console.warn('[connector-wipe-debug] layoutHydrate SKIP incomplete', {
            path: 'layoutHydrate',
            liveEdges: liveEdgesBeforeHydrate,
            incomingConnectors,
            incomplete: true,
          });
          if (!cancelled) setIsLayouting(false);
          return;
        }
        const transformed = transformDataToFlow(activeFlowchartData);
        nodesToLayout = transformed.nodes;
        edgesToLayout = transformed.edges;
        console.log('[connector-wipe-debug] layoutHydrate', {
          path: 'layoutHydrate',
          liveEdges: liveEdgesBeforeHydrate,
          incomingConnectors,
          transformedEdges: edgesToLayout.length,
          wouldWipe: liveEdgesBeforeHydrate > 0 && edgesToLayout.length === 0,
        });
      }

      // Lane display order:
      // - Local re-layout: keep the established in-session order (swimlaneOrderRef),
      //   appending any brand-new actor last.
      // - Hydration: follow the persisted swimlaneLayouts order from the server so
      //   the top-to-bottom lane order after a refresh matches what the backend
      //   stored (a just-created lane the backend appends stays last). Any actor
      //   that has nodes but no layout entry is appended after.
      let laneOrder;
      if (shouldUseLocalGraph) {
        laneOrder = buildLaneOrder(
          nodesToLayout.map((node) => node?.data?.actor),
          swimlaneOrderRef.current
        );
      } else {
        const layoutOrder = (activeFlowchartData?.swimlaneLayouts || [])
          .map((entry) => (entry?.actorName || '').trim())
          .filter((actor) => actor !== '');
        const presentActors = nodesToLayout.map((node) => node?.data?.actor);
        laneOrder = buildLaneOrder(presentActors, layoutOrder);
      }
      swimlaneOrderRef.current = laneOrder;

      // Effective lane heights: seed from the server payload so hydration always
      // uses the real heights (the swimlaneHeights STATE may not be seeded yet on
      // the first pass after a refresh, which would size every lane at the 200px
      // default and misalign nodes). In-session state (user edits) wins where set.
      const serverHeights = {};
      (activeFlowchartData?.swimlaneLayouts || []).forEach((entry) => {
        const actor = (entry?.actorName || '').trim();
        const h = Number(entry?.swimlaneHeight);
        if (actor && Number.isFinite(h) && h > 0) serverHeights[actor] = h;
      });
      const effectiveHeights = { ...serverHeights, ...swimlaneHeights };

      const layouted = await getLayoutedElements(nodesToLayout, edgesToLayout, undefined, effectiveHeights, laneOrder);
      if (cancelled) return;

      // Calculate the maximum X position to determine swimlane width
      const maxX = layouted.nodes.length > 0
        ? Math.max(...layouted.nodes.map(n => n.position.x + NODE_WIDTH))
        : NODE_WIDTH;
      const swimlaneWidth = maxX + 400; // Add padding on the right
      const minX = layouted.nodes.length > 0
        ? Math.min(...layouted.nodes.map(n => n.position.x))
        : 0;
      const swimlaneStartX = minX - 200; // Add padding on the left

      // Add swimlane background nodes and store boundaries using dynamic
      // swimlanes. Geometry (yStart, height) is baked into each lane by
      // computeSwimlaneGeometry inside getLayoutedElements, so resized
      // lanes flow through here with their saved heights.
      const swimlaneNodes = layouted.dynamicSwimlanes.map((swimlane, index) => ({
        id: swimlane.id,
        type: 'swimlane',
        position: { x: swimlaneStartX, y: swimlane.yStart },
        data: {
          label: swimlane.label,
          actor: swimlane.actor,
          index,
          width: swimlaneWidth,
          height: swimlane.height,
          onEdit: handleSwimlaneEditClick,
          onResize: handleSwimlaneResize,
        },
        draggable: false,
        selectable: false,
        // zIndex 0 (not -1): a negative z-index dropped the lane — and its
        // bottom-edge resize grip — behind the canvas pane, making the grip
        // ungrabbable. Process nodes are appended after the lanes in the nodes
        // array, so with equal z-index they still paint on top of the lanes.
        zIndex: 0,
      }));

      // Store swimlane boundaries for drag detection. yStart/yEnd already
      // reflect any per-lane resized height.
      const boundaries = layouted.dynamicSwimlanes.map((swimlane) => ({
        id: swimlane.id,
        label: swimlane.label,
        actors: [swimlane.actor],
        yStart: swimlane.yStart,
        yEnd: swimlane.yEnd
      }));
      setSwimlaneBoundaries(boundaries);

      // Merge keySystems status into node data before setting
      const persistedPositionMap = new Map(
        nodesToLayout
          .filter(n => n.data?.hasPersistedPosition && n.position)
          .map(n => [n.id, n.position])
      );
      const currentLocalPositionMap = new Map(
        (nodesRef.current || [])
          .filter(n => n.type !== 'swimlane' && n.position)
          .map(n => [n.id, n.position])
      );

      // Old vs new lane top (yStart) per actor. When a lane is added/removed or
      // resized, every lane below it shifts. Locked nodes keep an absolute Y, so
      // without correction they'd fall outside their (now-shifted) lane. We shift
      // each locked node by its own lane's delta so it travels with its lane and
      // stays inside it, preserving its relative position within the lane.
      const normalizeActor = (actor) => String(actor || '').trim().toLowerCase();
      const oldLaneTopByActor = new Map(
        (nodesRef.current || [])
          .filter(n => n.type === 'swimlane' && n.data?.actor != null && Number.isFinite(n.position?.y))
          .map(n => [normalizeActor(n.data.actor), n.position.y])
      );
      const newLaneTopByActor = new Map(
        layouted.dynamicSwimlanes
          .filter(s => Number.isFinite(s.yStart))
          .map(s => [normalizeActor(s.actor), s.yStart])
      );
      // Full lane bounds per actor, used on hydration to pull any node whose
      // persisted Y has drifted outside its lane back inside. Lane geometry is
      // recomputed deterministically from heights, but persisted node Y can lag
      // behind height/order changes, leaving nodes rendered in the wrong lane.
      const laneBoundsByActor = new Map(
        layouted.dynamicSwimlanes
          .filter(s => Number.isFinite(s.yStart) && Number.isFinite(s.height))
          .map(s => [normalizeActor(s.actor), { yStart: s.yStart, height: s.height }])
      );

      const nodesWithKeySystemStatus = layouted.nodes.map(node => {
        const isSwimlaneReorderLayout =
          shouldUseLocalGraph && pendingSwimlaneReorderPersistRef.current;
        const previousLocalPosition = currentLocalPositionMap.get(node.id);
        const lockedLocalPosition =
          shouldUseLocalGraph && !isSwimlaneReorderLayout
            ? previousLocalPosition
            : null;
        const persistedPosition = shouldUseLocalGraph ? null : persistedPositionMap.get(node.id);
        let finalPosition = lockedLocalPosition || persistedPosition || node.position;

        // Move up/down: keep each node's exact X and its relative place inside
        // the lane. Only shift Y by how far that lane moved (same idea as the
        // locked-node lane-shift path). Do NOT take ELK's centered Y — that
        // collapses every node in a lane onto one row and causes overlaps.
        if (isSwimlaneReorderLayout && previousLocalPosition) {
          const actorKey = normalizeActor(node.data?.actor);
          let oldTop = oldLaneTopByActor.get(actorKey);
          let newTop = newLaneTopByActor.get(actorKey);

          // Start/end often have no actor — resolve lane from pre-move Y.
          if (!Number.isFinite(oldTop) || !Number.isFinite(newTop)) {
            const prevY = previousLocalPosition.y;
            let matchedActorKey = null;
            for (const [key, top] of oldLaneTopByActor.entries()) {
              const bounds = laneBoundsByActor.get(key);
              const height = bounds?.height;
              if (!Number.isFinite(top) || !Number.isFinite(height)) continue;
              if (prevY >= top && prevY < top + height) {
                matchedActorKey = key;
                break;
              }
            }
            // Fallback: nearest old lane top at or above the node.
            if (!matchedActorKey) {
              let bestTop = -Infinity;
              for (const [key, top] of oldLaneTopByActor.entries()) {
                if (Number.isFinite(top) && top <= prevY && top >= bestTop) {
                  bestTop = top;
                  matchedActorKey = key;
                }
              }
            }
            if (matchedActorKey) {
              oldTop = oldLaneTopByActor.get(matchedActorKey);
              newTop = newLaneTopByActor.get(matchedActorKey);
            }
          }

          if (Number.isFinite(oldTop) && Number.isFinite(newTop)) {
            finalPosition = {
              x: previousLocalPosition.x,
              y: previousLocalPosition.y + (newTop - oldTop),
            };
          } else {
            finalPosition = {
              x: previousLocalPosition.x,
              y: previousLocalPosition.y,
            };
          }
        // A node flagged for lane-center snap (just moved to a new/other actor)
        // takes its Y from the layout's computed lane center so it always sits
        // inside the lane, while keeping its existing X.
        } else if (pendingLaneCenterNodeIdsRef.current.has(node.id)) {
          finalPosition = {
            x: (lockedLocalPosition || persistedPosition || node.position)?.x ?? node.position.x,
            y: node.position.y,
          };
          pendingLaneCenterNodeIdsRef.current.delete(node.id);
        } else if (lockedLocalPosition) {
          // Keep this locked node aligned with its lane if the lane shifted.
          const actorKey = normalizeActor(node.data?.actor);
          const oldTop = oldLaneTopByActor.get(actorKey);
          const newTop = newLaneTopByActor.get(actorKey);
          if (Number.isFinite(oldTop) && Number.isFinite(newTop) && oldTop !== newTop) {
            finalPosition = {
              x: lockedLocalPosition.x,
              y: lockedLocalPosition.y + (newTop - oldTop),
            };
          }
        } else if (!shouldUseLocalGraph && node.data?.nodeType !== 'start' && node.data?.nodeType !== 'end') {
          // Hydration: if a node's persisted Y sits outside its lane (drifted
          // after height/order changes), clamp it back inside so it never
          // renders in a neighboring lane. Start/end nodes are ELK-positioned
          // and not lane-bound, so they're left alone.
          const bounds = laneBoundsByActor.get(normalizeActor(node.data?.actor));
          if (bounds) {
            const LANE_INNER_PADDING = 12;
            const minY = bounds.yStart + LANE_INNER_PADDING;
            const maxY = bounds.yStart + bounds.height - NODE_HEIGHT - LANE_INNER_PADDING;
            const currentY = Number.isFinite(finalPosition?.y) ? finalPosition.y : bounds.yStart;
            const clampedY = maxY < minY ? minY : Math.min(Math.max(currentY, minY), maxY);
            if (clampedY !== currentY) {
              finalPosition = { x: finalPosition?.x ?? node.position.x, y: clampedY };
            }
          }
        }
        const nodeRisks = Array.isArray(node.data?.risks) ? node.data.risks : [];
        return {
          ...node,
          position: finalPosition,
          data: {
            ...node.data,
            isKeySystem: keySystems.has(node.data.system),
            ...(nodeRisks.length > 0
              ? {
                onRiskClick: () => handleRiskClickRef.current?.(node.id, nodeRisks),
                onHexagonControlClick: (control) => handleHexagonControlClickRef.current?.(
                  node.id,
                  node.data?.nodeDescription,
                  control,
                  nodeRisks
                ),
                onHexagonControlsOverflowClick: (controls) => handleHexagonControlsOverflowClickRef.current?.(
                  node.id,
                  node.data?.nodeDescription,
                  controls,
                  nodeRisks
                ),
              }
              : {}),
          }
        };
      });

      const nextNodes = [...swimlaneNodes, ...nodesWithKeySystemStatus];
      if (cancelled) return;
      const liveEdgesBeforePaint = (edgesRef.current || []).length;
      const nextEdgeCount = (layouted.edges || []).length;
      console.log('[connector-wipe-debug] layoutPaint setEdges', {
        path: 'layoutPaint',
        shouldUseLocalGraph,
        liveEdges: liveEdgesBeforePaint,
        outgoingEdges: nextEdgeCount,
        wouldWipe: liveEdgesBeforePaint > 0 && nextEdgeCount === 0,
      });
      setNodes(nextNodes);
      setEdges(layouted.edges);
      // Keep refs in sync immediately so any follow-up persist in this tick
      // reads the just-layouted graph (not a stale pre-move snapshot).
      nodesRef.current = nextNodes;
      edgesRef.current = layouted.edges;

      // Persist swimlane reorder from these exact layouted positions — do not
      // wait for a later effect (that raced autosave / read stale nodesRef).
      if (pendingSwimlaneReorderPersistRef.current) {
        pendingSwimlaneReorderPersistRef.current = false;
        // Prevent the generic autosave effect from also forking a draft.
        pendingAutoSaveRef.current = false;
        hasUserMutatedFlowchartRef.current = false;
        // Reorder clears hasUserMutated — record stack from this layouted graph.
        flowchartUndoRecordCanvasNowRef.current?.(nextNodes, layouted.edges);
        positionOnlySyncInFlightRef.current = true;

        const positions = nodesWithKeySystemStatus
          .filter((node) => node?.id && node?.position)
          .map((node) => ({
            nodeId: node.id,
            position: { x: node.position.x, y: node.position.y },
          }));

        if (isFlowchartPublishedRef.current) {
          autoSaveInFlightRef.current = true;
          void persistFlowchartRef.current?.({
            manual: false,
            forceSave: true,
            nodesOverride: nextNodes,
            edgesOverride: layouted.edges,
          }).finally(() => {
            autoSaveInFlightRef.current = false;
            pendingAutoSaveRef.current = false;
            positionOnlySyncInFlightRef.current = false;
          });
        } else if (positions.length > 0) {
          void updateNodePositionsRef.current?.(positions, { positionOnlySync: true });
        } else {
          positionOnlySyncInFlightRef.current = false;
        }
      }

      if (!cancelled) setIsLayouting(false);
    };
    layoutNodes();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeFlowchartData, layoutTrigger]); // swimlaneHeights intentionally excluded: height changes are handled by the in-place geometry effect below (a full ELK re-layout here would rebuild from the server snapshot and wipe in-flight drags/actor remaps). keySystems also excluded to prevent an infinite loop.

  // Apply swimlane height changes in place, WITHOUT a full ELK re-layout.
  //
  // A lane height change (manual grip resize or a drop-triggered auto-grow)
  // used to be a dependency of the ELK effect above. That re-ran the whole
  // layout from the server snapshot and re-centered every node, which threw
  // away the user's just-dropped position and any actor remap (the "jerk +
  // snap-back" and "actor not changing" bugs). Instead we mutate lane geometry
  // directly: resize the lane backgrounds, push the lanes below down by the
  // height delta, and shift only those lower-lane nodes so nothing misaligns.
  useEffect(() => {
    const currentNodes = nodesRef.current || [];
    const swimlaneNodes = currentNodes.filter((n) => n.type === 'swimlane');
    if (swimlaneNodes.length === 0) return;

    // Existing render order (top-to-bottom) is the lane stacking order.
    const orderedLanes = [...swimlaneNodes].sort(
      (a, b) => (a.position?.y ?? 0) - (b.position?.y ?? 0)
    );

    // Recompute cumulative geometry from the target heights, matching
    // computeSwimlaneGeometry so this path is identical to a full re-layout.
    let cursor = 0;
    let geometryChanged = false;
    const laneGeometryById = new Map();
    const laneBounds = [];
    orderedLanes.forEach((lane) => {
      const actor = lane.data?.actor;
      const oldYStart = Number.isFinite(lane.position?.y) ? lane.position.y : cursor;
      const oldHeight = Number.isFinite(lane.data?.height) ? lane.data.height : SWIMLANE_HEIGHT;
      const rawNext = actor ? swimlaneHeights[actor] : undefined;
      const newHeight = Number.isFinite(rawNext) && rawNext > 0 ? rawNext : SWIMLANE_HEIGHT;
      const newYStart = cursor;
      cursor += newHeight;
      const delta = newYStart - oldYStart;
      if (delta !== 0 || newHeight !== oldHeight) geometryChanged = true;
      laneGeometryById.set(lane.id, { newYStart, newHeight, delta });
      laneBounds.push({ oldYStart, oldYEnd: oldYStart + oldHeight, delta, newYStart, newHeight });
    });

    if (!geometryChanged) {
      userSwimlaneResizeRef.current = false;
      return;
    }

    // Hydration/alignment realigns lane backgrounds only (nodes already sit at
    // their persisted positions). A user resize also carries the lower lanes'
    // nodes along so they stay inside their lanes.
    const isUserResize = userSwimlaneResizeRef.current;
    userSwimlaneResizeRef.current = false;

    const shiftedPositions = [];
    const nextNodes = currentNodes.map((node) => {
      if (node.type === 'swimlane') {
        const geo = laneGeometryById.get(node.id);
        if (!geo) return node;
        return {
          ...node,
          position: { ...node.position, y: geo.newYStart },
          data: { ...node.data, height: geo.newHeight },
        };
      }
      if (!isUserResize) return node;
      const oldY = node.position?.y ?? 0;
      const centerY = oldY + NODE_HEIGHT / 2;
      const lane =
        laneBounds.find((b) => centerY >= b.oldYStart && centerY < b.oldYEnd) ||
        laneBounds[laneBounds.length - 1];
      if (!lane) return node;
      // Follow the lane down/up (start delta) and then clamp fully inside the
      // resized lane so a shrunk lane never leaves its node on/over the border.
      const shiftedY = oldY + lane.delta;
      const LANE_INNER_PADDING = 12;
      const minY = lane.newYStart + LANE_INNER_PADDING;
      const maxY = lane.newYStart + lane.newHeight - NODE_HEIGHT - LANE_INNER_PADDING;
      const clampedY = maxY < minY ? minY : Math.min(Math.max(shiftedY, minY), maxY);
      if (clampedY === oldY) return node;
      const newPosition = { x: node.position?.x ?? 0, y: clampedY };
      shiftedPositions.push({ nodeId: node.id, position: newPosition });
      return { ...node, position: newPosition };
    });

    setSwimlaneBoundaries(
      orderedLanes.map((lane) => {
        const geo = laneGeometryById.get(lane.id);
        return {
          id: lane.id,
          label: lane.data?.label,
          actors: [lane.data?.actor],
          yStart: geo.newYStart,
          yEnd: geo.newYStart + geo.newHeight,
        };
      })
    );
    setNodes(nextNodes);
    nodesRef.current = nextNodes;

    // Height resize skips hasUserMutated — record stack here.

    if (isUserResize) {
      flowchartUndoRecordCanvasNowRef.current?.(nextNodes, edgesRef.current);
    }

    // Persist the follow-along shift so a reload keeps lower-lane nodes aligned
    // with their (taller) lanes. The dragged/resized node itself is persisted by
    // its own drop handler.
    if (isUserResize && shiftedPositions.length > 0) {
      updateNodePositionsRef.current?.(shiftedPositions, { positionOnlySync: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [swimlaneHeights, isLayouting]);

  // Update isKeySystem flag and per-system keySystem in node data when keySystems changes (without re-layout)
  useEffect(() => {
    setNodes((currentNodes) =>
      currentNodes.map(node => {
        if (node.type === 'swimlane') return node;
        const systemNames = parseSystemNames(node.data.system || '');
        const baseSystems = Array.isArray(node.data.systems) && node.data.systems.length > 0
          ? node.data.systems
          : systemNames.map((name) => ({ id: '', name, keySystem: false }));
        const updatedSystems = baseSystems.map((s) => {
          const name = typeof s?.name === 'string' ? s.name.trim() : '';
          const fromApi = s.keySystem === true;
          return {
            ...s,
            keySystem: name
              ? (keySystemsHydratedRef.current ? keySystems.has(name) : fromApi)
              : false,
          };
        });
        return {
          ...node,
          data: {
            ...node.data,
            isKeySystem: keySystems.has(node.data.system),
            systems: updatedSystems,
          }
        };
      })
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [keySystems]); // setNodes is stable and doesn't need to be in deps

  // Add delete / edit handlers to all nodes (only once after layout)
  useEffect(() => {
    if (isLayouting) return; // Wait for layout to complete

    setNodes((nds) =>
      nds.map((node) => {
        // Only add handler if not already present
        if (node.type === 'swimlane') return node;
        const needsDelete = !node.data.onDelete;
        const needsEdit = !node.data.onEditNode;
        if (!needsDelete && !needsEdit) return node;
        return {
          ...node,
          data: {
            ...node.data,
            ...(needsDelete
              ? { onDelete: () => handleDeleteClickRef.current?.(node.id) }
              : {}),
            ...(needsEdit
              ? { onEditNode: () => handleEditNodeRef.current?.(node.id) }
              : {}),
          }
        };
      })
    );
  }, [isLayouting, setNodes]); // Only run when layout completes

  // Persist edited connector waypoints surgically via the connector-layout
  // PATCH. We deliberately do NOT trigger the full autosave here: that path
  // refreshes edges from the server and would wipe the just-edited waypoints
  // before they round-trip.
  const connectorLayoutSaveTimerRef = useRef(null);
  const dirtyConnectorWaypointsRef = useRef(new Map());
  const flushConnectorLayoutSave = useCallback(() => {
    const versionId = flowchartVersionIdRef.current;
    if (!versionId) return;
    const entries = Array.from(dirtyConnectorWaypointsRef.current.entries());
    if (entries.length === 0) return;
    const layouts = entries.map(([connectorId, wps]) => (
      wps.length > 0
        ? { connectorId, waypoints: wps }
        : { connectorId, clearWaypoints: true }
    ));
    dirtyConnectorWaypointsRef.current = new Map();
    updateFlowchartConnectorLayout(versionId, layouts).catch((err) => {
      console.error('Failed to persist connector waypoints:', err);
      entries.forEach(([id, wps]) => dirtyConnectorWaypointsRef.current.set(id, wps));
    });
  }, []);

  const handleWaypointsChange = useCallback((edgeId, waypoints) => {
    const clean = Array.isArray(waypoints)
      ? waypoints
        .filter((p) => p && Number.isFinite(p.x) && Number.isFinite(p.y))
        .map((p) => ({ x: p.x, y: p.y }))
      : [];
    hasUserMutatedFlowchartRef.current = true;
    setEdges((eds) => eds.map((e) => (
      e.id === edgeId ? { ...e, data: { ...e.data, waypoints: clean } } : e
    )));
    dirtyConnectorWaypointsRef.current.set(edgeId, clean);
    if (connectorLayoutSaveTimerRef.current) clearTimeout(connectorLayoutSaveTimerRef.current);
    connectorLayoutSaveTimerRef.current = setTimeout(() => {
      connectorLayoutSaveTimerRef.current = null;
      flushConnectorLayoutSave();
    }, 700);
  }, [setEdges, flushConnectorLayoutSave]);

  // Double-click a connector to add a waypoint. Handled at the React Flow
  // container level (onEdgeDoubleClick) rather than on the edge SVG itself —
  // edge selection re-renders the edge between the two clicks, which ate the
  // native dblclick on the path.
  const rfInstanceRef = useRef(null);
  const handleEdgeDoubleClickAddWaypoint = useCallback((event, edge) => {
    const inst = rfInstanceRef.current;
    if (!inst || !edge || typeof inst.screenToFlowPosition !== 'function') return;
    const click = inst.screenToFlowPosition({ x: event.clientX, y: event.clientY });
    const cur = (edgesRef.current || []).find((e) => e.id === edge.id);
    const existing = Array.isArray(cur?.data?.waypoints) ? cur.data.waypoints : [];
    const sNode = inst.getNode(edge.source);
    const tNode = inst.getNode(edge.target);
    const center = (n) => ({ x: (n?.position?.x || 0) + NODE_WIDTH / 2, y: (n?.position?.y || 0) + NODE_HEIGHT / 2 });
    const insertAt = waypointInsertIndex(center(sNode), existing, center(tNode), click);
    const next = [...existing];
    next.splice(insertAt, 0, { x: click.x, y: click.y });
    handleWaypointsChange(edge.id, next);
  }, [handleWaypointsChange]);

  // Add handlers to all edges (only once after layout)
  useEffect(() => {
    if (isLayouting) return; // Wait for layout to complete

    setEdges((eds) =>
      eds.map((edge) => ({
        ...edge,
        data: {
          ...edge.data,
          onDelete: () => handleEdgeDeleteRef.current?.(edge.id),
          onEdit: () => handleEdgeEditRef.current?.(edge.id),
          onWaypointsChange: (wps) => handleWaypointsChange(edge.id, wps),
        }
      }))
    );
  }, [isLayouting, setEdges, handleWaypointsChange]); // Only run when layout completes

  const resolveSwimlaneByY = useCallback((y) => {
    if (!Array.isArray(swimlaneBoundaries) || swimlaneBoundaries.length === 0) return null;
    if (typeof y !== 'number') return null;

    // Primary match; include last lane end boundary to avoid drop-edge misses.
    const lastIndex = swimlaneBoundaries.length - 1;
    const directMatch = swimlaneBoundaries.find((boundary, index) => (
      y >= boundary.yStart && (y < boundary.yEnd || (index === lastIndex && y <= boundary.yEnd))
    ));
    if (directMatch) return directMatch;

    // Fallback: clamp by lane index derived from fixed swimlane height.
    const laneIndex = Math.max(0, Math.min(lastIndex, Math.floor(y / SWIMLANE_HEIGHT)));
    return swimlaneBoundaries[laneIndex] || null;
  }, [swimlaneBoundaries]);

  const resolveSwimlaneForNodePlacement = useCallback((nodeTopY) => {
    if (!Array.isArray(swimlaneBoundaries) || swimlaneBoundaries.length === 0) return null;
    if (typeof nodeTopY !== 'number') return null;

    const nodeBottomY = nodeTopY + NODE_HEIGHT;
    const lastIndex = swimlaneBoundaries.length - 1;
    return swimlaneBoundaries.find((boundary, index) => (
      nodeTopY >= boundary.yStart &&
      (nodeBottomY < boundary.yEnd || (index === lastIndex && nodeBottomY <= boundary.yEnd))
    )) || null;
  }, [swimlaneBoundaries]);

  const resolveSwimlaneForNodeCenter = useCallback((nodeTopY) => {
    if (typeof nodeTopY !== 'number') return null;
    return resolveSwimlaneByY(nodeTopY + (NODE_HEIGHT / 2));
  }, [resolveSwimlaneByY]);

  // Given a node's top Y, resolve the lane its CENTER is over and whether the
  // whole node fits inside that lane. `status` is 'invalid' when the node
  // straddles a border (pokes into the lane above, or into an existing lane
  // below). Overflow past the very bottom lane is allowed (auto-grow handles
  // it), so it stays 'valid'.
  const evaluateDropTarget = useCallback((nodeTopY) => {
    if (typeof nodeTopY !== 'number') return null;
    const lane = resolveSwimlaneForNodeCenter(nodeTopY);
    if (!lane) return null;
    const nodeBottomY = nodeTopY + NODE_HEIGHT;
    const lastIndex = swimlaneBoundaries.length - 1;
    const laneIndex = swimlaneBoundaries.findIndex((b) => b.id === lane.id);
    const isLastLane = laneIndex === lastIndex;
    const straddlesTop = nodeTopY < lane.yStart;
    const straddlesBottom = !isLastLane && nodeBottomY > lane.yEnd;
    const status = straddlesTop || straddlesBottom ? 'invalid' : 'valid';
    return { lane, status };
  }, [resolveSwimlaneForNodeCenter, swimlaneBoundaries]);

  // Clamp a node's top Y so the whole node sits inside the given lane, never on
  // a border. Returns the original Y if the lane is too short to hold it (the
  // auto-grow path handles growing the lane in that case).
  const clampNodeTopIntoLane = useCallback((nodeTopY, lane) => {
    if (!lane || !Number.isFinite(lane.yStart) || !Number.isFinite(lane.yEnd)) return nodeTopY;
    const LANE_INNER_PADDING = 12;
    const minY = lane.yStart + LANE_INNER_PADDING;
    const maxY = lane.yEnd - NODE_HEIGHT - LANE_INNER_PADDING;
    if (maxY < minY) return minY;
    return Math.min(Math.max(nodeTopY, minY), maxY);
  }, []);

  const publishDragHighlight = useCallback((next) => {
    const prev = swimlaneDragHighlightRef.current;
    const changed = (prev?.actorKey !== next?.actorKey) || (prev?.status !== next?.status);
    if (!changed) return;
    swimlaneDragHighlightRef.current = next;
    setSwimlaneDragHighlight(next);
  }, []);

  const onNodeDrag = useCallback((_event, node) => {
    if (!node || node.type === 'swimlane') return;
    const target = evaluateDropTarget(node.position?.y);
    if (!target) {
      publishDragHighlight(null);
      return;
    }
    publishDragHighlight({
      actorKey: normalizeActorKey(target.lane.actors?.[0]),
      status: target.status,
    });
  }, [evaluateDropTarget, publishDragHighlight]);

  const onNodeDragStop = useCallback(() => {
    publishDragHighlight(null);
  }, [publishDragHighlight]);

  // Custom handler for node changes to detect swimlane crossing
  const handleNodesChange = useCallback((changes) => {
    const nodesBeforeChange = nodesRef.current || [];
    // Apply the changes first
    onNodesChange(changes);
    if (Date.now() < suppressNodeChangePersistenceUntilRef.current) {
      return;
    }
    // Final drop Y after clamping a node fully inside its target lane (never on
    // a border). Feeds both the visual setNodes and the persisted position so
    // the saved coordinate matches what the user sees.
    const clampedYByNodeId = new Map();
    const movedNodePositions = getMovedNodePositions(changes);
    const positionChangesFromEvent = changes
      .filter((change) => (
        change.type === 'position' &&
        change.position &&
        change.dragging !== true &&
        nodeTypeById.get(change.id) !== 'swimlane'
      ))
      .map((change) => ({
        nodeId: change.id,
        position: {
          x: change.position.x,
          y: change.position.y,
        },
      }));
    let hasSwimlaneRemap = false;

    // Check for drag end events
    changes.forEach(change => {
      if (change.type === 'position' && change.dragging === true) {
        // Capture source lane only once at drag start. During drag, React Flow emits
        // multiple position updates; overwriting source lane causes missed remap detection.
        if (dragStartSwimlaneByNodeIdRef.current.has(change.id)) return;
        const nodeAtDragStart = nodesBeforeChange.find((n) => n.id === change.id);
        if (!nodeAtDragStart || nodeAtDragStart.type === 'swimlane') return;
        const startY = typeof nodeAtDragStart.position?.y === 'number'
          ? nodeAtDragStart.position.y
          : change.position?.y;
        const startLane =
          resolveSwimlaneForNodeCenter(startY) ||
          resolveSwimlaneForNodePlacement(startY) ||
          resolveSwimlaneByY(startY);
        dragStartSwimlaneByNodeIdRef.current.set(change.id, startLane);
        return;
      }

      if (change.type === 'position' && change.dragging === false && change.position) {
        // Find the node that was dragged
        const draggedNode = nodesBeforeChange.find((n) => n.id === change.id);

        // Skip swimlane nodes
        if (!draggedNode || draggedNode.type === 'swimlane') return;

        const sourceSwimlane = dragStartSwimlaneByNodeIdRef.current.get(change.id)
          || (() => {
            const previousY =
              typeof draggedNode.position?.y === 'number' ? draggedNode.position.y : change.position.y;
            return (
              resolveSwimlaneForNodeCenter(previousY) ||
              resolveSwimlaneForNodePlacement(previousY) ||
              resolveSwimlaneByY(previousY)
            );
          })();
        dragStartSwimlaneByNodeIdRef.current.delete(change.id);

        const targetSwimlane =
          resolveSwimlaneForNodeCenter(change.position.y) ||
          resolveSwimlaneForNodePlacement(change.position.y) ||
          resolveSwimlaneByY(change.position.y);

        // Auto-extend the lane (Neha feedback): if a node is dropped near or
        // past the lane's bottom edge, grow that lane's height so it stays
        // fully inside the lane. Reuses the same persisted-height path as a
        // manual resize.
        let effectiveLaneYEnd = targetSwimlane?.yEnd;
        if (targetSwimlane && Number.isFinite(targetSwimlane.yStart) && Number.isFinite(targetSwimlane.yEnd)) {
          const LANE_BOTTOM_PADDING = 28;
          const neededHeight = (change.position.y + NODE_HEIGHT + LANE_BOTTOM_PADDING) - targetSwimlane.yStart;
          const currentLaneHeight = targetSwimlane.yEnd - targetSwimlane.yStart;
          const targetActor = targetSwimlane.actors?.[0];
          if (targetActor && neededHeight > currentLaneHeight) {
            const grownHeight = Math.min(neededHeight, SWIMLANE_MAX_HEIGHT);
            handleSwimlaneResize(targetActor, grownHeight);
            effectiveLaneYEnd = targetSwimlane.yStart + grownHeight;
          }
        }

        // Enforce the no-border rule: clamp the node fully inside its target
        // lane (using the possibly-grown bottom) so it can never rest on the
        // line between two lanes.
        const dropY = targetSwimlane
          ? clampNodeTopIntoLane(change.position.y, { ...targetSwimlane, yEnd: effectiveLaneYEnd })
          : change.position.y;
        clampedYByNodeId.set(change.id, dropY);

        // Auto-extend lane WIDTH too: if a node is dropped near or past the
        // right edge of the swimlane frame, widen ALL lanes (they share one
        // frame width) so the node never sits outside the frame.
        {
          const LANE_RIGHT_PADDING = 120;
          const neededWidth = change.position.x + NODE_WIDTH + LANE_RIGHT_PADDING;
          setNodes((nds) => {
            const currentWidth = nds.find((n) => n.type === 'swimlane')?.data?.width;
            if (!Number.isFinite(currentWidth) || neededWidth <= currentWidth) return nds;
            return nds.map((n) =>
              n.type === 'swimlane'
                ? { ...n, data: { ...n.data, width: neededWidth } }
                : n
            );
          });
        }

        if (targetSwimlane) {
          // Get the first actor from the target swimlane as the new actor
          const newActor = targetSwimlane.actors[0];
          const hasLaneIds = Boolean(sourceSwimlane?.id && targetSwimlane?.id);
          const changedSwimlaneById = hasLaneIds
            ? sourceSwimlane.id !== targetSwimlane.id
            : false;
          // Actor fallback is only for cases where lane boundaries cannot resolve one side.
          // This prevents false remaps after refresh when Start/End actor metadata lags lane state.
          const changedSwimlaneByActor = !hasLaneIds
            ? String(draggedNode?.data?.actor || '') !== String(newActor || '')
            : false;
          const changedSwimlane = changedSwimlaneById || changedSwimlaneByActor;
          // Deterministic rule:
          // - Lane changed => update-flowchart path
          // - Same lane (x/y-only) => node-position patch path
          if (changedSwimlane) {
            hasSwimlaneRemap = true;
            setNodes((nds) =>
              nds.map((node) => {
                if (node.id === change.id) {
                  return {
                    ...node,
                    position: {
                      x: change.position.x,
                      y: dropY,
                    },
                    data: {
                      ...node.data,
                      actor: newActor,
                      isMapped: true // Node is now mapped since it has a valid swimlane actor
                    }
                  };
                }
                return node;
              })
            );

            // Show feedback message
            queueSaveMessage(`✓ Moved to '${targetSwimlane.label}' swimlane`);
          } else if (dropY !== change.position.y) {
            // Same lane, but the drop landed on a border — nudge the node fully
            // inside. onNodesChange already applied the raw (unclamped) Y above,
            // so correct it here.
            setNodes((nds) =>
              nds.map((node) => (
                node.id === change.id
                  ? { ...node, position: { x: change.position.x, y: dropY } }
                  : node
              ))
            );
          }
        }
      }
    });

    const hasPositionChangeCandidate = changes.some((change) => {
      if (change.type !== 'position' || !change.position || change.dragging === true) return false;
      return nodeTypeById.get(change.id) !== 'swimlane';
    });

    // Ignore internal node-change events emitted by React Flow during hydration,
    // measurement, or selection updates.
    if (!hasSwimlaneRemap && !hasPositionChangeCandidate) {
      return;
    }

    // Lane remap is structural. Pure node movement uses node-positions API.
    if (hasSwimlaneRemap) {
      hasUserMutatedFlowchartRef.current = true;
      pendingSwimlaneRemapSaveRef.current = true;
      setSwimlaneRemapSaveNonce((prev) => prev + 1);
      // Once a node is explicitly dropped into a lane, keep x/y as source of truth.
      useSwimlaneSnapLogicRef.current = false;
    } else if (hasPositionChangeCandidate) {
      const rawPositionsToSync = positionChangesFromEvent.length > 0
        ? positionChangesFromEvent
        : movedNodePositions;
      // Persist the clamped Y (what the user sees) rather than the raw drop.
      const positionsToSync = rawPositionsToSync.map((p) => (
        clampedYByNodeId.has(p.nodeId)
          ? { ...p, position: { ...p.position, y: clampedYByNodeId.get(p.nodeId) } }
          : p
      ));
      if (positionsToSync.length === 0) return;
      hasUserMutatedFlowchartRef.current = true;
      pendingAutoSaveRef.current = false;
      positionOnlySyncInFlightRef.current = true;
      // Position-only save skips notifyPersistCaughtUp — record stack here.

      flowchartUndoRecordCanvasNowRef.current?.(nodesRef.current, edgesRef.current);
      updateNodePositionsRef.current?.(positionsToSync, { positionOnlySync: true });
    } else {
      hasUserMutatedFlowchartRef.current = true;
      pendingAutoSaveRef.current = true;
      runAutoSaveRef.current?.();
    }
  }, [getMovedNodePositions, onNodesChange, setNodes, queueSaveMessage, resolveSwimlaneByY, resolveSwimlaneForNodePlacement, resolveSwimlaneForNodeCenter, nodeTypeById, handleSwimlaneResize, clampNodeTopIntoLane]);

  const openEdgeEditModal = useCallback((edgeId) => {
    const edge = (edgesRef.current || []).find((e) => e.id === edgeId);
    if (!edge) return;

    setEditingEdge(edge);
    setEdgeEditForm({
      label: edge.data?.label || '',
      isBidirectional: edge.data?.isBidirectional === true,
    });
    setEdgeLabelError('');
    setIsEdgeEditModalOpen(true);
  }, []);

  const onConnect = useCallback(
    (params) => {
      // Final safety net. `isValidConnection` already blocks self-loops and
      // duplicates DURING the drag (so the line just won't connect — no popups),
      // but guard here too in case onConnect is ever reached for an invalid one.
      if (!isConnectionAllowed(params, edgesRef.current)) {
        return;
      }
      const connectorId = generateConnectorId();
      const edgeMarkers = getEdgeMarkers(false);
      // Keep the exact handles the user connected so the line stays ATTACHED to
      // the points they picked (start dot -> end dot), and exits each node in
      // that handle's direction. Endpoints left without a handle (e.g. a drop on
      // the node body) fall back to floating geometry in CustomEdge, so the line
      // is always visually attached at both ends.
      const newEdge = {
        ...params,
        sourceHandle: params.sourceHandle ?? undefined,
        targetHandle: params.targetHandle ?? undefined,
        id: connectorId,
        type: 'custom',
        animated: false,
        ...edgeMarkers,
        style: { stroke: '#000000', strokeWidth: 2 },
        data: {
          label: '',
          connectorId,
          isBidirectional: false,
          isTempConnector: true,
          waypoints: [],
          onDelete: () => handleEdgeDeleteRef.current?.(connectorId),
          onEdit: () => openEdgeEditModal(connectorId),
          onWaypointsChange: (wps) => handleWaypointsChange(connectorId, wps),
        },
      };
      setEdges((eds) => [...eds, newEdge]);
      setIsPendingConnectorConfirmation(true);

      // Open edge edit modal to add label
      setEditingEdge(newEdge);
      setEdgeEditForm({ label: '', isBidirectional: false });
      setEdgeLabelError('');
      setIsEdgeEditModalOpen(true);
    },
    [setEdges, openEdgeEditModal, handleWaypointsChange]
  );

  // Track viewport changes for node placement
  const onMoveEnd = useCallback((event, viewport) => {
    viewportRef.current = viewport;
  }, []);

  // Handle double-click on node to edit
  const openNodeEditModal = useCallback((node) => {
    // Don't edit swimlane nodes
    if (!node || node.type === 'swimlane') return;

    setEditingNode(node);
    setEditForm({
      nodeDescription: node.data.nodeDescription || '',
      actor: node.data.actor || '',
      // Version-scoped systems[] only (same as cylinders); no shared system text.
      system: systemTextForEditForm(node.data.systems),
      soxControl: node.data.soxControl || '',
      // Legacy document string unused in form; kept for schema/save compatibility.
      document: '',
      // Version-scoped documents[] only — no backfill from document text.
      documents: normalizeNodeDocuments(node.data.documents),
      nodeType: node.data.nodeType || 'process',
      keySystem: false,  // Not used in form, determined by keySystems Set
      keyControl: node.data.keyControl || false
    });
    setNodeFormErrors({});
    pendingKeySystemSaveRef.current = false;
    pendingDocumentCatalogSaveRef.current = false;
    nodesBeforeDocumentCatalogEditRef.current = null;
    setEditingDocumentKey(null);
    setIsEditModalOpen(true);
  }, []);

  const onNodeDoubleClick = useCallback((event, node) => {
    openNodeEditModal(node);
  }, [openNodeEditModal]);

  handleEditNodeRef.current = (nodeId) => {
    const node = (nodesRef.current || []).find((n) => n.id === nodeId);
    openNodeEditModal(node);
  };

  // Handle global keySystem toggle with confirmation
  const handleKeySystemToggle = useCallback((systemName, isChecked) => {
    if (!systemName || systemName.trim() === '') return;

    if (isChecked) {
      // Adding system to key systems
      const nodeCount = nodes.filter(
        (n) => n.type !== 'swimlane' && nodeIncludesSystemName(n.data.system, systemName)
      ).length;
      const confirmed = window.confirm(
        `This will mark all ${nodeCount} node(s) with system "${systemName}" as key. Continue?`
      );

      if (confirmed) {
        // Persist only when user clicks "Save Changes" in node modal.
        pendingKeySystemSaveRef.current = true;
        setKeySystems(prev => new Set([...prev, systemName]));
      }
    } else {
      // Removing system from key systems
      const nodeCount = nodes.filter(
        (n) => n.type !== 'swimlane' && nodeIncludesSystemName(n.data.system, systemName)
      ).length;
      const confirmed = window.confirm(
        `This will remove key designation from all ${nodeCount} node(s) with system "${systemName}". Continue?`
      );

      if (confirmed) {
        // Persist only when user clicks "Save Changes" in node modal.
        pendingKeySystemSaveRef.current = true;
        setKeySystems(prev => {
          const newSet = new Set(prev);
          newSet.delete(systemName);
          return newSet;
        });
      }
    }
  }, [nodes]);

  // Catalog-global document fields (docType / systems) — confirm then stamp onto
  // every node that already links this document (mirrors key system behavior).
  const confirmDocumentCatalogChange = useCallback((docId, docName, actionLabel) => {
    if (!docId) return true;
    const nodeCount = countNodesWithDocument(nodesRef.current || nodes, docId);
    const label = (docName || '').trim() || 'this document';
    const places = Math.max(nodeCount, 1);
    return window.confirm(
      `This will ${actionLabel} for "${label}" on all ${places} node(s) where this document is present. Continue?`
    );
  }, [nodes]);

  const applyDocumentCatalogChange = useCallback((docId, fields) => {
    if (!docId || !fields) return;
    // Mirror key system: stamp in-memory only. Persist on "Save Changes", not on confirm.
    // Snapshot once before the first stamp so Cancel can undo catalog edits.
    if (!pendingDocumentCatalogSaveRef.current) {
      const current = nodesRef.current || [];
      nodesBeforeDocumentCatalogEditRef.current = current.map((node) => {
        if (node?.type === 'swimlane') return node;
        return {
          ...node,
          data: {
            ...node.data,
            documents: normalizeNodeDocuments(node.data?.documents),
          },
        };
      });
    }
    pendingDocumentCatalogSaveRef.current = true;
    setNodes((nds) => applyDocumentCatalogFieldsToNodes(nds, docId, fields));
  }, [setNodes]);

  // Handle opening add node modal
  const handleAddNode = useCallback(() => {
    setEditForm({
      nodeDescription: '',
      actor: '',
      system: '',
      soxControl: '',
      document: '',
      documents: [],
      nodeType: 'process'
    });
    setNodeFormErrors({});
    setIsAddingNode(true);
    pendingKeySystemSaveRef.current = false;
    pendingDocumentCatalogSaveRef.current = false;
    nodesBeforeDocumentCatalogEditRef.current = null;
    setEditingDocumentKey(null);
    setIsEditModalOpen(true);
  }, []);

  // Handle saving edited or new node
  const handleSaveNode = useCallback(async () => {
    try {
      await NODE_DETAILS_SCHEMA.validate(editForm, { abortEarly: false });
      setNodeFormErrors({});
    } catch (validationError) {
      const formErrors = {};
      if (validationError?.inner?.length) {
        validationError.inner.forEach((error) => {
          if (error.path && !formErrors[error.path]) {
            formErrors[error.path] = error.message;
          }
        });
      } else if (validationError?.path) {
        formErrors[validationError.path] = validationError.message;
      }
      setNodeFormErrors(formErrors);
      return;
    }

    if (
      !isAddingNode &&
      editingNode &&
      nodeEditFormMatchesNode(editForm, editingNode) &&
      !pendingKeySystemSaveRef.current &&
      !pendingDocumentCatalogSaveRef.current
    ) {
      pendingKeySystemSaveRef.current = false;
      pendingDocumentCatalogSaveRef.current = false;
      setEditingDocumentKey(null);
      closeNodeEditModal({
        setIsEditModalOpen,
        setEditingNode,
        setIsAddingNode,
        setIsAddingNewActor,
        setNodeFormErrors,
      });
      return;
    }

    hasUserMutatedFlowchartRef.current = true;
    if (isAddingNode) {
      // Adding new node
      const newNodeId = `node-${Date.now()}`;

      // Calculate center of visible viewport for placement
      let viewportCenter = { x: 400, y: 200 }; // Fallback position

      try {
        // Get ReactFlow container dimensions
        const reactFlowBounds = reactFlowWrapper.current?.getBoundingClientRect();
        const viewport = viewportRef.current;

        if (reactFlowBounds && viewport) {
          // Calculate center of visible viewport in screen coordinates
          const screenCenterX = reactFlowBounds.width / 2;
          const screenCenterY = reactFlowBounds.height / 2;

          // Convert screen coordinates to flow coordinates
          // Formula: flowCoord = (screenCoord - viewportOffset) / zoom
          viewportCenter = {
            x: (screenCenterX - viewport.x) / viewport.zoom,
            y: (screenCenterY - viewport.y) / viewport.zoom
          };
        }
      } catch (error) {
        console.log('Using fallback position for new node');
      }

      // Check if actor is new (creates new swimlane)
      const actorName = editForm.actor?.trim() || '';
      const existingActors = [...new Set(nodes.map(n => n.data.actor).filter(a => a && a.trim() !== ''))];
      const isNewActor = actorName && !existingActors.includes(actorName);

      const reactFlowType = editForm.nodeType === 'decision' ? 'decision'
        : (editForm.nodeType === 'start' || editForm.nodeType === 'end') ? 'oval'
          : 'rectangle';

      const targetActor = actorName;
      const targetSwimlane = targetActor
        ? swimlaneBoundaries.find((boundary) =>
          boundary.actors.some((actor) => (actor || '').trim() === targetActor)
        )
        : null;
      const targetLaneHeight = targetSwimlane
        ? (targetSwimlane.yEnd - targetSwimlane.yStart)
        : SWIMLANE_HEIGHT;
      const initialPosition = targetSwimlane
        ? {
          x: viewportCenter.x,
          y: targetSwimlane.yStart + (targetLaneHeight - NODE_HEIGHT) / 2,
        }
        : viewportCenter;
      const spawnPosition = getAvailableNodeSpawnPosition({
        desiredPosition: initialPosition,
        existingNodes: nodes,
        constrainToSwimlane: targetSwimlane
          ? { y: targetSwimlane.yStart + (targetLaneHeight - NODE_HEIGHT) / 2 }
          : null,
      });

      const newNode = {
        id: newNodeId,
        type: reactFlowType,
        position: spawnPosition,
        data: {
          nodeDescription: editForm.nodeDescription,
          actor: editForm.actor,
          system: editForm.system || '',
          systems: syncNodeSystemsWithText(editForm.system, [], keySystems, systemsCatalogByLowerName),
          soxControl: editForm.soxControl || '',
          document: '',
          documents: normalizeNodeDocuments(editForm.documents),
          nodeType: editForm.nodeType,
          keyControl: editForm.keyControl || false,
          onDelete: () => setDeleteConfirmation({
            nodeId: newNodeId,
            nodeTitle: editForm.nodeDescription || 'this node',
          })
        },
        style: {
          background: 'transparent',
          border: 'none',
          padding: '0',
          width: NODE_WIDTH,
          height: NODE_HEIGHT,
        }
      };

      setNodes((nds) => {
        const withNew = [...nds, newNode];
        return applyDocumentCatalogMapToNodes(
          withNew,
          documentCatalogMapFromDocuments(editForm.documents)
        );
      });

      // Trigger re-layout if new actor/swimlane was created
      if (isNewActor) {
        setLayoutTrigger(prev => prev + 1);
        queueSaveMessage(`✓ Added node and created swimlane "${actorName}"`);
      } else {
        queueSaveMessage(`✓ Added node`);
      }
    } else if (editingNode) {
      // Editing existing node
      const reactFlowType = editForm.nodeType === 'decision' ? 'decision'
        : (editForm.nodeType === 'start' || editForm.nodeType === 'end') ? 'oval'
          : 'rectangle';

      // Check if actor changed to a new one
      const oldActor = (editingNode.data.actor || '').trim();
      const newActor = editForm.actor?.trim() || '';
      const actorChanged = oldActor !== newActor;
      const normalizedNewActor = newActor.toLowerCase();

      // IMPORTANT: compute all of these SYNCHRONOUSLY (not inside the setNodes
      // updater). React defers the updater to render time, so reading these
      // afterwards would see their initial values and skip setLayoutTrigger —
      // which is exactly why a new lane from the edit modal only appeared after
      // a server refresh.
      const currentNodesSnapshot = nodesRef.current || nodes || [];
      const existingActors = [...new Set(
        currentNodesSnapshot
          .filter((n) => n.type !== 'swimlane')
          .map((n) => n.data.actor)
          .filter((a) => a && a.trim() !== '')
      )];
      const isNewActor = Boolean(newActor && !existingActors.includes(newActor));

      const matchedSwimlaneNode = normalizedNewActor
        ? currentNodesSnapshot.find(
          (node) => node.type === 'swimlane'
            && String(node?.data?.actor || '').trim().toLowerCase() === normalizedNewActor
        )
        : null;
      const targetSwimlane = newActor
        ? swimlaneBoundaries.find((boundary) =>
          boundary.actors.some((actor) => String(actor || '').trim().toLowerCase() === normalizedNewActor)
        )
        : null;
      const currentEditingNode = currentNodesSnapshot.find((node) => node.id === editingNode.id) || editingNode;
      const matchedSwimlaneHeight = matchedSwimlaneNode
        ? (Number.isFinite(matchedSwimlaneNode.data?.height) ? matchedSwimlaneNode.data.height : SWIMLANE_HEIGHT)
        : null;
      const targetSwimlaneLaneHeight = targetSwimlane
        ? (targetSwimlane.yEnd - targetSwimlane.yStart)
        : null;
      // A brand-new actor has no lane yet. Mirror handleCreateNewSwimlane:
      // append the lane below the last one (using the accumulated yEnd, which
      // already reflects any resized heights) and estimate its center Y so the
      // edited node drops straight into the correct spot. The re-layout below
      // then materializes the lane background.
      const lastBoundary = Array.isArray(swimlaneBoundaries) && swimlaneBoundaries.length > 0
        ? swimlaneBoundaries[swimlaneBoundaries.length - 1]
        : null;
      const appendedLaneCenterY = (lastBoundary ? lastBoundary.yEnd : 0) + (SWIMLANE_HEIGHT - NODE_HEIGHT) / 2;
      const resolvedTargetSwimlaneCenterY = matchedSwimlaneNode
        ? (Number.isFinite(matchedSwimlaneNode.position?.y) ? matchedSwimlaneNode.position.y : 0)
        + (matchedSwimlaneHeight - NODE_HEIGHT) / 2
        : targetSwimlane
          ? targetSwimlane.yStart + (targetSwimlaneLaneHeight - NODE_HEIGHT) / 2
          : isNewActor
            ? appendedLaneCenterY
            : null;
      // Preserve the node's current X, only re-estimate Y for the new lane.
      const updatedNodePosition = actorChanged && typeof resolvedTargetSwimlaneCenterY === 'number'
        ? {
          x: typeof currentEditingNode?.position?.x === 'number' ? currentEditingNode.position.x : 0,
          y: resolvedTargetSwimlaneCenterY,
        }
        : null;

      // A brand-new actor must be appended last in the lane order so its lane
      // renders at the bottom immediately (buildLaneOrder appends unknown actors).
      if (isNewActor) {
        swimlaneOrderRef.current = buildLaneOrder(
          [...swimlaneOrderRef.current, newActor],
          swimlaneOrderRef.current
        );
      }

      // Any actor change relocates the node to another lane. Let the upcoming
      // layout decide its exact Y (lane center) so it lands INSIDE the lane —
      // the estimate above can be stale once lanes are added/removed and the
      // cumulative geometry shifts.
      if (actorChanged) {
        pendingLaneCenterNodeIdsRef.current.add(editingNode.id);
      }

      let updatedNodesSnapshot = null;
      const documentCatalogFromForm = documentCatalogMapFromDocuments(editForm.documents);
      setNodes((nds) => {
        const mappedNodes = nds.map((node) => {
          if (node.id === editingNode.id) {
            const nextPosition = updatedNodePosition || node.position;
            const previousSystems = Array.isArray(node.data.systems) ? node.data.systems : [];
            const nextSystems = syncNodeSystemsWithText(
              editForm.system,
              previousSystems,
              keySystems,
              systemsCatalogByLowerName
            );
            // Had systems, user cleared all → keep those system ids so BE can
            // soft-delete-mark them on the new draft (skip legacy NULL fallback).
            const clearedAll = previousSystems.length > 0 && nextSystems.length === 0;
            const systemPresent = clearedAll
              ? true
              : nextSystems.length > 0
                ? false
                : Boolean(node.data.systemPresent);
            const systemPresentSystems = clearedAll
              ? previousSystems
                  .map((s) => ({ id: typeof s?.id === 'string' ? s.id.trim() : '' }))
                  .filter((s) => s.id)
              : nextSystems.length > 0
                ? []
                : (Array.isArray(node.data.systemPresentSystems) ? node.data.systemPresentSystems : []);
            return {
              ...node,
              type: reactFlowType,
              position: nextPosition,
              data: {
                ...node.data,
                nodeDescription: editForm.nodeDescription,
                actor: newActor,
                system: editForm.system,
                systems: nextSystems,
                systemPresent,
                systemPresentSystems,
                soxControl: editForm.soxControl,
                // Keep legacy document text for existing save/identify-risks paths;
                // UI display uses documents[] only (no text backfill).
                document: node.data.document || '',
                documents: normalizeNodeDocuments(editForm.documents),
                nodeType: editForm.nodeType,
                keyControl: editForm.keyControl,
              },
            };
          }
          return node;
        });

        // Stamp catalog-global docType/systems from this form onto every other
        // node that links the same document ids (keeps update-flowchart payload consistent).
        const withCatalogSynced = applyDocumentCatalogMapToNodes(
          mappedNodes,
          documentCatalogFromForm
        );

        // Move the edited node to the end so its brand-new lane materializes at
        // the bottom, matching the appendedLaneCenterY we positioned it at.
        if (isNewActor) {
          const editedIndex = withCatalogSynced.findIndex((node) => node.id === editingNode.id);
          if (editedIndex !== -1) {
            const [editedNode] = withCatalogSynced.splice(editedIndex, 1);
            withCatalogSynced.push(editedNode);
          }
        }

        updatedNodesSnapshot = withCatalogSynced;
        return updatedNodesSnapshot;
      });
      // Prevent programmatic node updates from triggering position API/update-flowchart
      // through handleNodesChange side-effects; explicit save runs below post-render.
      suppressNodeChangePersistenceUntilRef.current = Date.now() + 1000;
      if (updatedNodesSnapshot) {
        nodesRef.current = updatedNodesSnapshot;
      }

      // Trigger re-layout whenever the actor changed so the moved node snaps to
      // its target lane's computed center (see pendingLaneCenterNodeIdsRef). This
      // covers both new lanes and moves into existing lanes, including the case
      // where the node's old lane is emptied and removed.
      if (actorChanged) {
        setLayoutTrigger(prev => prev + 1);
        if (isNewActor) {
          queueSaveMessage(`✓ Updated node and created swimlane "${newActor}"`);
        } else {
          queueSaveMessage(`✓ Updated node and moved to swimlane "${newActor}"`);
        }
      }

    }

    closeNodeEditModal({
      setIsEditModalOpen,
      setEditingNode,
      setIsAddingNode,
      setIsAddingNewActor,
      setNodeFormErrors,
    });
    pendingKeySystemSaveRef.current = false;
    pendingDocumentCatalogSaveRef.current = false;
    nodesBeforeDocumentCatalogEditRef.current = null;
    setEditingDocumentKey(null);
  }, [isAddingNode, editingNode, editForm, nodes, setNodes, queueSaveMessage, swimlaneBoundaries, keySystems, systemsCatalogByLowerName]);

  // Handle canceling edit/add
  const handleCancelEdit = useCallback(() => {
    if (pendingDocumentCatalogSaveRef.current && nodesBeforeDocumentCatalogEditRef.current) {
      setNodes(nodesBeforeDocumentCatalogEditRef.current);
    }
    pendingKeySystemSaveRef.current = false;
    pendingDocumentCatalogSaveRef.current = false;
    nodesBeforeDocumentCatalogEditRef.current = null;
    setEditingDocumentKey(null);
    closeNodeEditModal({
      setIsEditModalOpen,
      setEditingNode,
      setIsAddingNode,
      setIsAddingNewActor,
      setNodeFormErrors,
    });
  }, [setNodes]);

  // Convert current nodes and edges back to JSON format
  const convertToJSON = useCallback((nodesOverride, edgesOverride, identifiedRisksOverride) => {
    const effectiveNodes = nodesOverride || nodes;
    const effectiveEdges = edgesOverride || edges;
    const effectiveIdentifiedRisks = identifiedRisksOverride ?? identifiedRisks;

    // Filter out swimlane nodes
    const processNodes = effectiveNodes.filter(node => node.type !== 'swimlane');

    // Catalog-global document fields: one canonical docType/systems per document id
    // so every node that links it sends the same values (mirrors keySystems).
    const documentCatalogById = documentCatalogMapFromDocuments(
      processNodes.flatMap((n) => (Array.isArray(n.data?.documents) ? n.data.documents : []))
    );

    const jsonNodes = processNodes.map(node => {
      // identifiedRisks is source of truth (same as node badge/popup). Do not merge
      // stale node.data.risks — that re-inflates counts after delete/re-identify.
      const nodeRisks = effectiveIdentifiedRisks
        .filter((r) => r.nodeId === node.id)
        .map(normalizeRiskForFlowchartSave);
      const nodeX = typeof node.position?.x === 'number' ? node.position.x : 0;
      const nodeY = typeof node.position?.y === 'number' ? node.position.y : 0;

      const systemsPayload = buildSystemsSavePayload(node.data.systems, keySystems);
      const systemText = node.data.system || '';
      // Cleared-all: send the systems that were on the node so BE soft-deletes
      // those exact ids on the new draft (no parent/legacy lookup).
      const systemPresentSystems = Array.isArray(node.data.systemPresentSystems)
        ? node.data.systemPresentSystems
          .map((s) => ({ id: typeof s?.id === 'string' ? s.id.trim() : '' }))
          .filter((s) => s.id)
        : [];
      const systemPresent =
        !systemsPayload &&
        !String(systemText).trim() &&
        node.data.systemPresent === true &&
        systemPresentSystems.length > 0;
      // Always send documents[] (including []) so BE uses junction UUID compare
      // like systems — never fall back to legacy document text for catalog nodes.
      const documentsPayload = buildDocumentsSavePayload(
        (Array.isArray(node.data.documents) ? node.data.documents : []).map((d) => {
          const cat = d?.id ? documentCatalogById.get(d.id) : null;
          if (!cat) return d;
          return { ...d, docType: cat.docType, systems: cat.systems };
        })
      );

      return {
        id: node.id,
        nodeType: node.data.nodeType || 'process',
        nodeDescription: node.data.nodeDescription || '',
        actor: node.data.actor || '',
        system: systemText,
        ...(systemsPayload ? { systems: systemsPayload } : {}),
        ...(systemPresent
          ? { systemPresent: true, systemPresentSystems }
          : {}),
        soxControl: node.data.soxControl || '',
        document: node.data.document || '',
        documents: documentsPayload,
        keySystem: keySystems.has(node.data.system) || false,
        keyControl: node.data.keyControl || false,
        position: { x: nodeX, y: nodeY },
        risks: nodeRisks.length > 0 ? nodeRisks : undefined, // Include risks if present
      };
    });

    const jsonConnectors = effectiveEdges.map(edge => {
      const waypoints = Array.isArray(edge.data?.waypoints)
        ? edge.data.waypoints
          .filter((p) => p && Number.isFinite(p.x) && Number.isFinite(p.y))
          .map((p) => ({ x: p.x, y: p.y }))
        : [];
      const out = {
        id: edge.data?.connectorId || edge.id || generateConnectorId(),
        source: edge.source,
        target: edge.target,
        label: edge.data?.label || '',
        isBidirectional: edge.data?.isBidirectional === true,
      };
      // Only include layout fields when set — keeps the diff path on the
      // BE from seeing spurious "changed" connectors on legacy data.
      if (edge.sourceHandle) out.sourceHandle = edge.sourceHandle;
      if (edge.targetHandle) out.targetHandle = edge.targetHandle;
      if (waypoints.length > 0) out.waypoints = waypoints;
      return out;
    });

    return {
      nodes: jsonNodes,
      connectors: jsonConnectors,
    };
  }, [nodes, edges, keySystems, identifiedRisks]);

  const buildSavePayload = useCallback(({ nodesOverride, edgesOverride, identifiedRisksOverride } = {}) => {
    const userHash = localStorage.getItem('userHash');
    const flowchartData = sanitizeFlowchartForSave(
      convertToJSON(nodesOverride, edgesOverride, identifiedRisksOverride)
    );
    const parsedVersionNumber = Number(selectedVersionNumber);
    const hasValidVersionNumber =
      selectedVersionNumber != null && !Number.isNaN(parsedVersionNumber);
    return {
      cycleId,
      processId,
      processName,
      processOwner,
      processStatus,
      ...(hasValidVersionNumber ? { versionNumber: parsedVersionNumber } : {}),
      flowchartData,
      userHash,
      ...(selectedAuditYearId ? { audit_year_id: selectedAuditYearId } : {}),
    };
  }, [cycleId, processId, processName, processOwner, processStatus, selectedVersionNumber, convertToJSON, selectedAuditYearId]);

  const persistFlowchart = useCallback(async ({
    manual = false,
    forceSave = false,
    nodesOverride,
    edgesOverride,
    identifiedRisksOverride,
  } = {}) => {
    if (!manual && skipNextPersistAfterPublishRef.current) {
      skipNextPersistAfterPublishRef.current = false;
      const probePayload = buildSavePayload({ nodesOverride, edgesOverride, identifiedRisksOverride });
      const probeComparable = stripNodePositionsForChangeDetection(probePayload.flowchartData);
      const serverComparable = activeFlowchartData
        ? stripNodePositionsForChangeDetection(sanitizeFlowchartForSave(activeFlowchartData))
        : null;
      if (serverComparable && deepEqual(probeComparable, serverComparable)) {
        hasUnsavedChangesRef.current = false;
        return true;
      }
    }

    if (manual) {
      setSaveMessage('');
    } else {
      setIsAutoSaving(true);
    }

    try {
      const payload = buildSavePayload({ nodesOverride, edgesOverride, identifiedRisksOverride });
      const currentFlowchart = payload.flowchartData;
      const currentComparableFlowchart = stripNodePositionsForChangeDetection(currentFlowchart);
      const currentProcessMetaSnapshot = {
        processName: payload.processName,
        processOwner: payload.processOwner,
      };

      if (
        !forceSave &&
        lastSavedFlowchartRef.current &&
        deepEqual(
          currentComparableFlowchart,
          stripNodePositionsForChangeDetection(lastSavedFlowchartRef.current)
        ) &&
        deepEqual(currentProcessMetaSnapshot, lastSavedProcessMetaRef.current)
      ) {
        if (manual) {
          queueSaveMessage('No changes to save');
        }
        hasUnsavedChangesRef.current = false;
        return true;
      }

      const invalidNodes = payload.flowchartData.nodes.filter(node =>
        node.nodeType !== 'start' &&
        node.nodeType !== 'end' &&
        (!node.nodeDescription || !node.nodeDescription.trim())
      );
      if (invalidNodes.length > 0) {
        if (manual) {
          queueSaveMessage(`Error: ${invalidNodes.length} node(s) missing description`);
        }
        return false;
      }

      const effectiveNodes = nodesOverride || nodes;
      const swimlaneNodes = effectiveNodes.filter(n => n.type === 'swimlane');
      const processNodes = effectiveNodes.filter(n => n.type !== 'swimlane');
      const emptySwimlanes = swimlaneNodes.filter(swimlane => {
        const hasNodes = processNodes.some(node => node.data.actor === swimlane.data.actor);
        return !hasNodes;
      });

      if (emptySwimlanes.length > 0) {
        const swimlaneNames = emptySwimlanes.map(s => s.data.label).join(', ');
        if (manual) {
          window.alert(
            `Note: ${emptySwimlanes.length} empty swimlane(s) will be removed after save:\n${swimlaneNames}`
          );
        } else {
          queueSaveMessage(
            `⚠ ${emptySwimlanes.length} empty swimlane(s) removed: ${swimlaneNames}`,
            4000
          );
        }
      }

      const response = await axiosInstance.post(API_ENDPOINTS.saveFlowchart, payload);

      if (response.data.success) {
        // Ignore stale autosave that finished during Z/Y pause (manual still applies).
        if (!manual && flowchartUndoIgnorePersistResultRef.current) {
          return true;
        }
        hasUnsavedChangesRef.current = false;
        hasUserMutatedFlowchartRef.current = false;
        lastSavedFlowchartRef.current = currentFlowchart;
        lastSavedProcessMetaRef.current = currentProcessMetaSnapshot;
        setLastFlowchartSavedAt(Date.now());
        if (response.data.processStatus) {
          setProcessStatus(normalizeSoxDashboardProcessStatus(response.data.processStatus));
        }
        if (manual) {
          queueSaveMessage(`✓ Saved successfully (${response.data.controlsExtracted} controls extracted)`, 3000);
        } else {
          queueSaveMessage('✓ Flowchart saved', 1500);
        }
        const responseVersionRaw = response.data?.versionNumber;
        const responseVersionNumber = responseVersionRaw == null ? null : Number(responseVersionRaw);
        const currentSelectedVersion = selectedVersionNumber == null ? null : Number(selectedVersionNumber);
        const versionChangedAfterSave =
          responseVersionNumber != null &&
          !Number.isNaN(responseVersionNumber) &&
          responseVersionNumber !== currentSelectedVersion;

        // Catch up undo stack before GET refresh — refresh can tweak nodes/edges
        // and a post-refresh commit was adding a fake past entry (first Ctrl+Z no-op).
        flowchartUndoNotifyPersistCaughtUpRef.current?.();

        if (versionChangedAfterSave) {
          await onRefreshForVersionNumber(responseVersionNumber);
        } else {
          await refreshFlowchartDataRef.current?.();
        }
        await fetchRiskControlMappings();
        if (manual || versionChangedAfterSave) {
          await refetchVersionOptions();
        }
        return true;
      }

      queueSaveMessage('✗ Save failed');
      return false;
    } catch (error) {
      console.error('Save error:', error);
      queueSaveMessage(`✗ ${resolve403ErrorMessage(error, 'Save failed')}`);
      return false;
    } finally {
      setIsAutoSaving(false);
    }
  }, [activeFlowchartData, buildSavePayload, fetchRiskControlMappings, nodes, onRefreshForVersionNumber, queueSaveMessage, refetchVersionOptions, selectedVersionNumber]);
  persistFlowchartRef.current = persistFlowchart;

  const handlePublish = useCallback(async () => {
    if (!flowchartVersionId) {
      queueSaveMessage('✗ Flowchart version not found');
      return;
    }

    // Mid undo-pause: same flush as Save-during-countdown, then publish.
    if (flowchartUndoPersistPausedRef.current) {
      const flushed = await flowchartUndoRequestFlushNowRef.current?.();
      if (flushed === false) {
        queueSaveMessage('✗ Could not save before publish. Publish cancelled.');
        return;
      }
    }

    suppressAutoSaveDuringPublishRef.current = true;
    pendingAutoSaveRef.current = false;
    setIsPublishing(true);
    try {
      const response = await publishFlowchartMutation.mutateAsync(flowchartVersionId);

      if (response?.success) {
        const publishedStatus = response?.status ?? response?.versionStatus;
        const publishedVersionRaw = response?.versionNumber ?? response?.version_number;
        const publishedVersionNumber = publishedVersionRaw == null ? null : Number(publishedVersionRaw);
        const publishedVersionId =
          response?.flowchartVersionId
          ?? null;

        if (publishedStatus != null) {
          setFlowchartVersionStatus(String(publishedStatus));
        }
        if (publishedVersionNumber != null && !Number.isNaN(publishedVersionNumber)) {
          setFlowchartVersionNumber(publishedVersionNumber);
        }
        if (publishedVersionId) {
          setFlowchartVersionId(publishedVersionId);
        }

        queueSaveMessage('✓ Flowchart published successfully', 3000);
        skipNextPersistAfterPublishRef.current = true;
        await refreshFlowchartDataRef.current?.();
        await fetchRiskControlMappings();
        await refetchVersionOptions();
      } else {
        queueSaveMessage(`✗ Publish failed: ${response?.message || 'An unknown error occurred.'}`);
      }
    } catch (error) {
      console.error('Publish error:', error);
      queueSaveMessage(`✗ ${resolve403ErrorMessage(error, 'Publish failed')}`);
    } finally {
      setIsPublishing(false);
      suppressAutoSaveDuringPublishRef.current = false;
    }
  }, [fetchRiskControlMappings, flowchartVersionId, publishFlowchartMutation, queueSaveMessage, refetchVersionOptions, setFlowchartVersionId, setFlowchartVersionNumber, setFlowchartVersionStatus]);

  const handleSetActive = useCallback(async () => {
    if (!selectedFlowchartVersionId) {
      queueSaveMessage('✗ Flowchart version not found');
      return;
    }
    if (isSelectedVersionActive) {
      queueSaveMessage('This version is already active');
      return;
    }

    suppressAutoSaveDuringPublishRef.current = true;
    pendingAutoSaveRef.current = false;
    setIsSettingActive(true);
    try {
      const response = await setActiveFlowchartMutation.mutateAsync(selectedFlowchartVersionId);
      if (response?.success) {
        const activeStatus = response?.status ?? response?.versionStatus ?? 'active';
        setFlowchartVersionStatus(String(activeStatus));
        skipNextPersistAfterPublishRef.current = true;
        queueSaveMessage('✓ Active version updated', 2500);
        // Set-active should only update version metadata/options; it should not
        // trigger an unnecessary flowchart payload refetch.
        await refetchVersionOptions();
      } else {
        queueSaveMessage(`✗ Set active failed: ${response?.message || 'An unknown error occurred.'}`);
      }
    } catch (error) {
      console.error('Set active error:', error);
      queueSaveMessage(`✗ ${resolve403ErrorMessage(error, 'Set active failed')}`);
    } finally {
      setIsSettingActive(false);
      suppressAutoSaveDuringPublishRef.current = false;
    }
  }, [isSelectedVersionActive, queueSaveMessage, refetchVersionOptions, selectedFlowchartVersionId, setActiveFlowchartMutation, setFlowchartVersionStatus]);

  const runAutoSave = useCallback(async () => {
    if (flowchartUndoPersistPausedRef.current) {
      return;
    }
    if (autoSaveInFlightRef.current) {
      pendingAutoSaveRef.current = true;
      return;
    }

    autoSaveInFlightRef.current = true;
    hasUserMutatedFlowchartRef.current = false;
    let saveSucceeded = false;
    try {
      saveSucceeded = await persistFlowchart({ manual: false });
    } finally {
      autoSaveInFlightRef.current = false;
      // Chain another save ONLY when the last one succeeded and new changes
      // queued meanwhile. A FAILED save (e.g. a 400 the server will always
      // reject) must NOT auto-retry — that storms the backend and wedges the
      // editor. We leave the dirty flag clear; the next genuine user edit
      // re-triggers a fresh save with the corrected payload.
      if (saveSucceeded && pendingAutoSaveRef.current) {
        pendingAutoSaveRef.current = false;
        runAutoSave();
      } else if (!saveSucceeded) {
        pendingAutoSaveRef.current = false;
      }
    }
  }, [persistFlowchart]);

  // Keep a stable ref so setTimeout always calls the latest version.
  runAutoSaveRef.current = runAutoSave;

  // Ensure swimlane remaps always trigger update-flowchart after nodes state is applied.
  useEffect(() => {
    if (!pendingSwimlaneRemapSaveRef.current) return;
    if (isLayouting || isPendingConnectorConfirmation) return;
    if (flowchartUndoPersistPausedRef.current) return;
    if (autoSaveInFlightRef.current) {
      // Avoid firing a second update-flowchart call while one is already running.
      pendingSwimlaneRemapSaveRef.current = false;
      return;
    }
    pendingSwimlaneRemapSaveRef.current = false;
    // Force update-flowchart for swimlane remaps (including start/end),
    // even if position-stripped snapshot would otherwise short-circuit.
    lastSavedFlowchartRef.current = null;
    runAutoSaveRef.current?.();
  }, [swimlaneRemapSaveNonce, nodes, isLayouting, isPendingConnectorConfirmation]);

  const currentFlowchartData = useMemo(
    () => sanitizeFlowchartForSave(convertToJSON()),
    [convertToJSON]
  );
  const currentComparableFlowchartData = useMemo(
    () => stripNodePositionsForChangeDetection(currentFlowchartData),
    [currentFlowchartData]
  );
  const serverFullFlowchartData = useMemo(
    () => (activeFlowchartData ? sanitizeFlowchartForSave(activeFlowchartData) : null),
    [activeFlowchartData]
  );
  const serverComparableFlowchartData = useMemo(
    () => (serverFullFlowchartData ? stripNodePositionsForChangeDetection(serverFullFlowchartData) : null),
    [serverFullFlowchartData]
  );
  const currentProcessMeta = useMemo(
    () => ({
      processName,
      processOwner,
    }),
    [processName, processOwner]
  );

  // When new server flowchart data is hydrated into the canvas (initial load, version switch,
  // or post-save refresh), treat it as the new saved baseline and skip one autosave tick.
  useEffect(() => {
    if (!activeFlowchartData) return;
    const serverComparable = stripNodePositionsForChangeDetection(
      sanitizeFlowchartForSave(activeFlowchartData)
    );
    if (deepEqual(serverComparable, lastHydratedComparableFlowchartRef.current)) {
      return;
    }
    lastHydratedComparableFlowchartRef.current = serverComparable;
    lastSavedFlowchartRef.current = serverFullFlowchartData;
    lastSavedProcessMetaRef.current = currentProcessMeta;
    hasInitializedAutoSaveRef.current = true;
    hasUnsavedChangesRef.current = false;
    const shouldPreserveDirtyState =
      hasUserMutatedFlowchartRef.current &&
      !deepEqual(currentComparableFlowchartData, serverComparable);
    hasUserMutatedFlowchartRef.current = shouldPreserveDirtyState;
    suppressAutoSaveDuringVersionSwitchRef.current = false;
  }, [activeFlowchartData, currentComparableFlowchartData, currentProcessMeta, serverFullFlowchartData]);

  // Debounced autosave — fires only when flowchart data actually changes (deep compare).
  useEffect(() => {
    if (isLayouting) return;
    if (isPendingConnectorConfirmation) return;
    if (flowchartUndoPersistPausedRef.current) return;
    if (suppressAutoSaveDuringVersionSwitchRef.current) return;
    if (suppressAutoSaveDuringPublishRef.current) return;
    if (!hasUserMutatedFlowchartRef.current) return;

    if (!hasInitializedAutoSaveRef.current) {
      hasInitializedAutoSaveRef.current = true;
      if (!lastSavedFlowchartRef.current) {
        lastSavedFlowchartRef.current = currentFlowchartData;
      }
      if (!lastSavedProcessMetaRef.current) {
        lastSavedProcessMetaRef.current = currentProcessMeta;
      }
    }

    if (positionOnlySyncInFlightRef.current) return;

    const lastSavedComparable = lastSavedFlowchartRef.current
      ? stripNodePositionsForChangeDetection(lastSavedFlowchartRef.current)
      : null;
    if (lastSavedComparable && deepEqual(currentComparableFlowchartData, lastSavedComparable)) return;
    if (
      !isFlowchartPublished &&
      serverComparableFlowchartData &&
      deepEqual(currentComparableFlowchartData, serverComparableFlowchartData)
    ) {
      lastSavedFlowchartRef.current = currentFlowchartData;
      hasUnsavedChangesRef.current = false;
      return;
    }

    hasUnsavedChangesRef.current = true;
    runAutoSaveRef.current?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentComparableFlowchartData, isFlowchartPublished, isLayouting, isPendingConnectorConfirmation, serverComparableFlowchartData]);

  // Debounced autosave for process metadata changes (status/owner/name).
  useEffect(() => {
    if (isLayouting) return;
    if (isPendingConnectorConfirmation) return;
    if (flowchartUndoPersistPausedRef.current) return;
    if (suppressAutoSaveDuringVersionSwitchRef.current) return;
    if (suppressAutoSaveDuringPublishRef.current) return;
    if (!hasUserMutatedFlowchartRef.current) return;
    if (!hasInitializedAutoSaveRef.current) {
      hasInitializedAutoSaveRef.current = true;
      if (!lastSavedProcessMetaRef.current) {
        lastSavedProcessMetaRef.current = currentProcessMeta;
      }
    }
    if (deepEqual(currentProcessMeta, lastSavedProcessMetaRef.current)) return;

    hasUnsavedChangesRef.current = true;
    runAutoSaveRef.current?.();
  }, [currentProcessMeta, isLayouting, isPendingConnectorConfirmation]);


  // Handle export to PDF
  const handleExportPDF = useCallback(async () => {
    try {
      setSaveMessage('Generating PDF...');

      // Filter out swimlane nodes and get process nodes only
      const processNodes = nodes.filter(node => node.type !== 'swimlane');

      // Get all nodes and calculate bounds
      const nodesBounds = getNodesBounds(processNodes);

      // Fixed dimensions for consistent output - 4K resolution
      const imageWidth = 3840;
      const imageHeight = 2160;

      // Calculate viewport to fit all nodes
      const viewport = getViewportForBounds(
        nodesBounds,
        imageWidth,
        imageHeight,
        0.1, // min zoom (padding)
        2    // max zoom
      );

      // Capture the entire flow using the viewport element
      const dataUrl = await toPng(document.querySelector('.react-flow__viewport'), {
        backgroundColor: '#ffffff',
        width: imageWidth,
        height: imageHeight,
        quality: 0.65, // Lower quality for better PDF performance
        style: {
          width: `${imageWidth}px`,
          height: `${imageHeight}px`,
          transform: `translate(${viewport.x}px, ${viewport.y}px) scale(${viewport.zoom})`,
        },
      });

      // Create PDF
      const img = new Image();
      img.src = dataUrl;

      img.onload = () => {
        const pdf = new jsPDF({
          orientation: 'landscape',
          unit: 'px',
          format: [imageWidth, imageHeight]
        });

        pdf.addImage(dataUrl, 'JPEG', 0, 0, imageWidth, imageHeight, undefined, 'FAST');
        pdf.save(`${processName}-flowchart-${new Date().toISOString().split('T')[0]}.pdf`);

        queueSaveMessage('✓ PDF exported');
      };
    } catch (error) {
      console.error('PDF export error:', error);
      queueSaveMessage('✗ PDF export failed');
    }
  }, [processName, nodes, queueSaveMessage]);

  // Handle export to SVG — native vector shapes (no <foreignObject>) so the
  // file opens in Visio, Illustrator, Inkscape and PowerPoint.
  const handleExportSVG = useCallback(async () => {
    // Selection is hidden only for the capture (so red outlines / delete
    // buttons don't end up in the file) and restored afterwards.
    const selectedNodeIds = new Set((nodesRef.current || []).filter((n) => n.selected).map((n) => n.id));
    const selectedEdgeIds = new Set((edgesRef.current || []).filter((e) => e.selected).map((e) => e.id));
    const hadSelection = selectedNodeIds.size > 0 || selectedEdgeIds.size > 0;
    try {
      setSaveMessage('Generating SVG...');
      if (hadSelection) {
        setNodes((nds) => nds.map((n) => (n.selected ? { ...n, selected: false } : n)));
        setEdges((eds) => eds.map((e) => (e.selected ? { ...e, selected: false } : e)));
        // Wait for React Flow to repaint without the selection styling.
        await new Promise((resolve) => requestAnimationFrame(() => requestAnimationFrame(resolve)));
      }
      const svg = buildFlowchartSvg({
        container: reactFlowWrapper.current,
        nodes: nodesRef.current,
        edges: edgesRef.current,
        title: processName ? `${processName} flowchart` : 'Flowchart',
      });
      downloadSvg(svg, `${processName}-flowchart-${new Date().toISOString().split('T')[0]}.svg`);
      queueSaveMessage('✓ SVG exported');
    } catch (error) {
      console.error('SVG export error:', error);
      queueSaveMessage('✗ SVG export failed');
    } finally {
      if (hadSelection) {
        setNodes((nds) => nds.map((n) => (selectedNodeIds.has(n.id) ? { ...n, selected: true } : n)));
        setEdges((eds) => eds.map((e) => (selectedEdgeIds.has(e.id) ? { ...e, selected: true } : e)));
      }
    }
  }, [processName, setNodes, setEdges, queueSaveMessage]);

  // Helper to update risk analysis step status
  const updateRiskStep = useCallback((stepIndex, status) => {
    setRiskAnalysisSteps(steps =>
      steps.map((step, idx) =>
        idx === stepIndex ? { ...step, status } : step
      )
    );
  }, []);

  // Reset risk analysis steps
  const resetRiskAnalysisSteps = useCallback(() => {
    setRiskAnalysisSteps([
      { name: 'Collecting process steps', status: 'pending' },
      { name: 'Analyzing SOX control points', status: 'pending' },
      { name: 'Identifying potential risks', status: 'pending' },
      { name: 'Evaluating risk levels', status: 'pending' },
      { name: 'Generating recommendations', status: 'pending' }
    ]);
    setRiskAnalysisProgress(0);
  }, []);

  // Handle identify risks - call AI to analyze process for SOX risks
  const handleIdentifyRisks = useCallback(async () => {
    if (!checkRisksModuleAccess()) return;
    try {
      setIsIdentifyingRisks(true);
      resetRiskAnalysisSteps();

      // Step 1: Collecting process steps
      updateRiskStep(0, 'processing');
      setRiskAnalysisProgress(10);

      // Build the request with process nodes
      const processNodes = nodes
        .filter(n => n.type !== 'swimlane')
        .map(n => ({
          id: n.id,
          nodeType: n.data.nodeType || n.type,
          nodeDescription: n.data.nodeDescription || '',
          actor: n.data.actor || '',
          system: n.data.system || '',
          soxControl: n.data.soxControl || '',
          document: n.data.document || ''
        }));

      updateRiskStep(0, 'completed');
      setRiskAnalysisProgress(20);

      // Step 2: Analyzing SOX control points
      updateRiskStep(1, 'processing');
      setRiskAnalysisProgress(30);

      // Small delay to show step progression
      await new Promise(resolve => setTimeout(resolve, 500));
      updateRiskStep(1, 'completed');
      setRiskAnalysisProgress(40);

      // Step 3: Identifying potential risks (API call)
      updateRiskStep(2, 'processing');
      setRiskAnalysisProgress(50);

      const response = await axiosInstance.post('/api/identify-process-risks', {
        processId,
        processName: processName,
        processDescription: '',
        flowchartVersionId: flowchartVersionId || undefined,
        versionNumber:
          selectedVersionNumber == null || selectedVersionNumber === ''
            ? undefined
            : Number(selectedVersionNumber),
        nodes: processNodes,
        ...(selectedAuditYearId ? { audit_year_id: selectedAuditYearId } : {}),
      });

      updateRiskStep(2, 'completed');
      setRiskAnalysisProgress(70);

      // Step 4: Evaluating risk levels
      updateRiskStep(3, 'processing');
      setRiskAnalysisProgress(80);
      await new Promise(resolve => setTimeout(resolve, 300));
      updateRiskStep(3, 'completed');
      setRiskAnalysisProgress(90);

      // Step 5: Generating recommendations
      updateRiskStep(4, 'processing');
      setRiskAnalysisProgress(95);
      await new Promise(resolve => setTimeout(resolve, 300));
      updateRiskStep(4, 'completed');
      setRiskAnalysisProgress(100);

      if (response.data.success) {
        const rawRisks = Array.isArray(response.data.risks) ? response.data.risks : [];
        const normalizedRisks = rawRisks.map((risk) => (risk && typeof risk === 'object' ? { ...risk } : risk));
        const mergedRisks = mergeRisksByRecordId(identifiedRisks, normalizedRisks);
        // Identify-risks already applied action=delete on the server (PRC soft-detach).
        // Drop those from FE state/save payload so update-flowchart does not recreate
        // the just-deleted process_risk_controls rows.
        const risksForUiAndSave = mergedRisks.filter((risk) => {
          const action = String(risk?.action || 'add').trim().toLowerCase();
          return action !== 'delete';
        });
        // Published fork is only needed when identify changed mappings (add/delete).
        // All no_change (or empty) → stay on published; do not call update-flowchart.
        const identifyRequiresNewDraft = normalizedRisks.some((risk) => {
          const action = String(risk?.action || 'add').trim().toLowerCase();
          return action === 'add' || action === 'delete';
        });

        if (isFlowchartPublished) {
          setIdentifiedRisks(risksForUiAndSave);
          if (identifyRequiresNewDraft) {
            // Single explicit save — forks a new draft and applies add/delete there.
            // Pass mergedRisks INCLUDING action=delete so the new draft can soft-detach
            // those cloned PRC rows after fork; UI state omits deletes.
            pendingAutoSaveRef.current = false;
            autoSaveInFlightRef.current = true;
            lastSavedFlowchartRef.current = null;
            try {
              const saveSucceeded = await persistFlowchart({
                manual: false,
                identifiedRisksOverride: mergedRisks,
              });
              if (!saveSucceeded) {
                queueSaveMessage('✗ Failed to save new draft with identified risks');
              }
            } finally {
              autoSaveInFlightRef.current = false;
              pendingAutoSaveRef.current = false;
            }
          }
        } else {
          // Draft: Go already persisted add/delete/no_change. Refresh from server;
          // do not mark dirty or autosave with delete rows still in FE risk state.
          identifiedRisksRef.current = risksForUiAndSave;
          setIdentifiedRisks(risksForUiAndSave);
          const parsedVersionNumber =
            selectedVersionNumber == null || selectedVersionNumber === ''
              ? null
              : Number(selectedVersionNumber);
          await onRefreshForVersionNumber?.(
            Number.isFinite(parsedVersionNumber) ? parsedVersionNumber : null
          );
          // Draft identify with mapping changes: commit undo step (past = pre-identify).
          if (identifyRequiresNewDraft) {
            flowchartUndoRecordCanvasNowRef.current?.(
              nodesRef.current,
              edgesRef.current,
              risksForUiAndSave
            );
          }
        }
        // Toast count = unique remaining risks after deletes (matches what GET/UI shows),
        // not AI riskCount which includes action=delete rows.
        const riskCount = (() => {
          const ids = new Set();
          for (const risk of risksForUiAndSave) {
            const id = getRiskRecordId(risk);
            if (id) ids.add(id);
          }
          return ids.size;
        })();

        // Small delay before closing modal so user sees 100%
        await new Promise(resolve => setTimeout(resolve, 500));

        if (!identifyRequiresNewDraft) {
          queueSaveMessage('✓ No change in risks', 3000);
        } else {
          queueSaveMessage(`✓ Found ${riskCount} risk${riskCount !== 1 ? 's' : ''}`, 3000);
        }
      } else {
        queueSaveMessage('✗ Risk analysis failed');
      }
    } catch (error) {
      console.error('Risk identification error:', error);
      queueSaveMessage(`✗ ${resolve403ErrorMessage(error, 'Risk analysis failed')}`);
    } finally {
      setIsIdentifyingRisks(false);
    }
  }, [identifiedRisks, isFlowchartPublished, nodes, persistFlowchart, processId, processName, flowchartVersionId, selectedVersionNumber, updateRiskStep, resetRiskAnalysisSteps, queueSaveMessage, onRefreshForVersionNumber, checkRisksModuleAccess]);

  // Handle clicking on a risk indicator
  const handleRiskClick = useCallback((nodeId, fallbackRisks = []) => {
    const nodeRisks = identifiedRisks.filter(r => r.nodeId === nodeId);
    const risksToShow = nodeRisks.length > 0 ? nodeRisks : (Array.isArray(fallbackRisks) ? fallbackRisks : []);
    if (risksToShow.length > 0) {
      setSelectedRisk({ nodeId, risks: risksToShow });
      setIsRiskModalOpen(true);
    }
  }, [identifiedRisks]);
  handleRiskClickRef.current = handleRiskClick;

  const handleUpdateIdentifiedRisk = useCallback(async (risk, values) => {
    if (!checkRisksModuleAccess()) {
      throw new Error('Missing Risks module access');
    }
    const recordId = getRiskRecordId(risk);
    if (!recordId) {
      toast.error('This risk cannot be updated because it does not have an identifier.');
      throw new Error('Missing risk identifier');
    }

    const previousValues = normalizeFormForPatch(buildIdentifiedRiskFormValues(risk), riskManualEditSchema);
    const nextValues = normalizeFormForPatch(values, riskManualEditSchema);
    const changedPatch = buildChangedPatch(previousValues, nextValues);
    if (Object.keys(changedPatch).length === 0) {
      return;
    }

    setSavingRiskId(recordId);
    try {
      const response = await updateRiskMutation.mutateAsync({ riskId: recordId, payload: changedPatch });
      const responseData = response?.data ?? response;
      const updatedRisk = mergeIdentifiedRiskRecord(risk, changedPatch, responseData);

      let nextIdentifiedRisks = [];
      setIdentifiedRisks((prev) => {
        nextIdentifiedRisks = prev.map((entry) => {
          if (getRiskRecordId(entry) !== recordId) return entry;
          // Keep each existing link's node assignment while applying updated risk fields.
          const mergedEntry = mergeIdentifiedRiskRecord(entry, changedPatch, responseData);
          return {
            ...mergedEntry,
            nodeId: entry.nodeId ?? mergedEntry.nodeId,
          };
        });
        return nextIdentifiedRisks;
      });
      setSelectedRisk((prev) => {
        if (!prev) return prev;
        const selectedNodeId = prev.nodeId;
        return {
          ...prev,
          risks: nextIdentifiedRisks.filter((entry) => entry.nodeId === selectedNodeId),
        };
      });
    } finally {
      setSavingRiskId((current) => (current === recordId ? null : current));
    }
  }, [updateRiskMutation, checkRisksModuleAccess]);

  const handleRiskDeleteClick = useCallback((risk) => {
    if (!checkRisksModuleAccess()) return;
    setRiskDeleteConfirmation({ risk, nodeId: selectedRisk?.nodeId ?? risk?.nodeId ?? null });
  }, [selectedRisk, checkRisksModuleAccess]);

  const cancelRiskDelete = useCallback(() => {
    setRiskDeleteConfirmation(null);
  }, []);

  const confirmRiskDelete = useCallback(async () => {
    if (!checkRisksModuleAccess()) return;
    const risk = riskDeleteConfirmation?.risk;
    if (!risk) return;
    await deleteIdentifiedRiskMutation.mutateAsync(risk);
  }, [deleteIdentifiedRiskMutation, riskDeleteConfirmation, checkRisksModuleAccess]);

  // Update nodes with risk data when identifiedRisks changes.
  // Use identifiedRisks as source of truth (do NOT merge with stale node.data.risks),
  // otherwise badge count stays high after action=delete / re-identify while popup is lower.
  // If risk content already matches, keep the same nodes array — a no-op rewrite after
  // Ctrl+Z/Y would schedule a new undo commit and clear redo.
  useEffect(() => {
    if (isLayouting) return; // Wait for layout to complete first

    setNodes((nds) => {
      let changed = false;
      const next = nds.map((node) => {
        const nodeRisks = identifiedRisks.filter((r) => r.nodeId === node.id);
        const prevRisks = Array.isArray(node.data?.risks) ? node.data.risks : [];
        if (nodeRisks.length > 0) {
          if (identifiedRisksListsEqual(prevRisks, nodeRisks)) {
            return node;
          }
          changed = true;
          return {
            ...node,
            data: {
              ...node.data,
              risks: nodeRisks,
              onRiskClick: () => handleRiskClickRef.current?.(node.id, nodeRisks),
              onHexagonControlClick: (control) => handleHexagonControlClickRef.current?.(
                node.id,
                node.data?.nodeDescription,
                control,
                nodeRisks
              ),
              onHexagonControlsOverflowClick: (controls) => handleHexagonControlsOverflowClickRef.current?.(
                node.id,
                node.data?.nodeDescription,
                controls,
                nodeRisks
              ),
            }
          };
        }
        // Clear risks if none found for this node
        if (node.data.risks) {
          changed = true;
          const { risks, onRiskClick, onHexagonControlClick, onHexagonControlsOverflowClick, ...restData } = node.data;
          return { ...node, data: restData };
        }
        return node;
      });
      return changed ? next : nds;
    });
  }, [identifiedRisks, setNodes, isLayouting]);

  // Handle clicking a mapped-control (teal) badge — existing behavior.
  const handleControlClick = useCallback((nodeId, nodeDescription, control, risks) => {
    setSelectedControlIndicator({
      source: 'mapping',
      nodeId,
      nodeDescription,
      control,
      risks,
      sourceRisks: Array.isArray(control?.riskIndices)
        ? control.riskIndices.map((riskIdx) => risks?.[riskIdx]).filter(Boolean)
        : [],
    });
    setIsControlModalOpen(true);
  }, []);

  // Handle clicking a hexagon (risk-attached) control — one control per popup.
  const handleHexagonControlClick = useCallback((nodeId, nodeDescription, control, risks) => {
    if (!control || control.__legacyLabel) return;
    setSelectedControlIndicator({
      source: 'hexagon',
      nodeId,
      nodeDescription,
      control,
      risks: Array.isArray(risks) ? risks : [],
      sourceRisks: findSourceRisksForControl(risks, control),
    });
    setIsControlModalOpen(true);
  }, []);
  handleHexagonControlClickRef.current = handleHexagonControlClick;

  // +N chip: list the overflow hexagon controls; picking one opens the same
  // single-control popup used by an individual hexagon click.
  const handleHexagonControlsOverflowClick = useCallback((nodeId, nodeDescription, controls, risks) => {
    const list = Array.isArray(controls)
      ? controls.filter((c) => c && !c.__legacyLabel)
      : [];
    if (list.length === 0) return;
    setSelectedControlIndicator({
      source: 'hexagon-overflow',
      nodeId,
      nodeDescription,
      controls: list,
      risks: Array.isArray(risks) ? risks : [],
    });
    setIsControlModalOpen(true);
  }, []);
  handleHexagonControlsOverflowClickRef.current = handleHexagonControlsOverflowClick;

  // Update nodes with control mapping data when mappings or risks change.
  // Keep the same nodes array when mappedControls content is unchanged — a no-op
  // rewrite after Ctrl+Z/Y would schedule a commit and clear redo (Identify Risks).
  useEffect(() => {
    if (isLayouting) return;

    setNodes((nds) => {
      let changed = false;
      const next = nds.map((node) => {
        if (node.type === 'swimlane') return node;

        const nodeRisks = node.data.risks || [];
        const nodeControls = [];
        nodeRisks.forEach((risk, riskIdx) => {
          const key = getRiskMappingKey(node.id, risk);
          if (!key) return;
          const mapping = riskControlMappings[key];
          if (mapping) {
            const existing = nodeControls.find(c => c.controlId === mapping.controlId);
            if (existing) {
              existing.riskIndices.push(riskIdx);
            } else {
              nodeControls.push({ ...mapping, riskIndices: [riskIdx] });
            }
          }
        });

        const prevControls = Array.isArray(node.data?.mappedControls) ? node.data.mappedControls : [];
        const nextControls = nodeControls.length > 0 ? nodeControls : undefined;
        const prevSerialized = prevControls.length > 0 ? JSON.stringify(prevControls) : '';
        const nextSerialized = nextControls ? JSON.stringify(nextControls) : '';
        if (prevSerialized === nextSerialized) {
          return node;
        }

        changed = true;
        if (nodeControls.length > 0 || node.data.mappedControls) {
          return {
            ...node,
            data: {
              ...node.data,
              mappedControls: nextControls,
              onControlClick: nodeControls.length > 0
                ? (control) => handleControlClick(node.id, node.data.nodeDescription, control, nodeRisks)
                : undefined
            }
          };
        }
        return node;
      });
      return changed ? next : nds;
    });
  }, [riskControlMappings, identifiedRisks, setNodes, isLayouting, handleControlClick]);

  // Handle node selection change
  const onSelectionChange = useCallback(({ nodes: selectedNodes, edges: selectedEdges }) => {
    setSelectedNodes(selectedNodes);
    setSelectedEdges(selectedEdges);
  }, []);

  // Handle delete button click - use functional updates to avoid stale closure
  const handleDeleteClick = useCallback((nodeId) => {
    // Start and End are structural anchors of the flow — never deletable.
    const target = (nodesRef.current || []).find((n) => n.id === nodeId);
    if (target && (target.data?.nodeType === 'start' || target.data?.nodeType === 'end')) {
      toast.error('Start and End steps cannot be deleted.');
      return;
    }
    setNodes((currentNodes) => {
      setEdges((currentEdges) => {
        const node = currentNodes.find(n => n.id === nodeId);
        if (!node || node.type === 'swimlane') {
          return currentEdges;
        }
        if (node.data?.nodeType === 'start' || node.data?.nodeType === 'end') {
          return currentEdges;
        }

        const connectedEdges = currentEdges.filter(e => e.source === nodeId || e.target === nodeId);

        setDeleteConfirmation({
          nodeId,
          nodeTitle: node.data.nodeDescription || 'Untitled Node',
          edgeCount: connectedEdges.length
        });

        return currentEdges;
      });
      return currentNodes;
    });
  }, [setNodes, setEdges]);
  handleDeleteClickRef.current = handleDeleteClick;

  // Confirm delete
  const confirmDelete = useCallback(() => {
    if (!deleteConfirmation) return;
    hasUserMutatedFlowchartRef.current = true;

    // Remove node and connected edges
    setNodes((nds) => nds.filter(n => n.id !== deleteConfirmation.nodeId));
    setEdges((eds) => eds.filter(e =>
      e.source !== deleteConfirmation.nodeId && e.target !== deleteConfirmation.nodeId
    ));

    queueSaveMessage(`✓ Deleted "${deleteConfirmation.nodeTitle}"`);

    setDeleteConfirmation(null);
    setSelectedNodes([]);
  }, [deleteConfirmation, setNodes, setEdges, queueSaveMessage]);

  // Handle edge deletion
  const handleEdgeDelete = useCallback((edgeId) => {
    hasUserMutatedFlowchartRef.current = true;
    setEdges((eds) => eds.filter(e => e.id !== edgeId));
    queueSaveMessage('✓ Edge deleted');
  }, [setEdges, queueSaveMessage]);
  handleEdgeDeleteRef.current = handleEdgeDelete;
  const handleEdgesChange = useCallback((changes) => {
    if (Array.isArray(changes) && changes.some((change) => change?.type === 'remove')) {
      hasUserMutatedFlowchartRef.current = true;
    }
    onEdgesChange(changes);
  }, [onEdgesChange]);


  // Handle edge edit - use functional state to avoid stale closure
  const handleEdgeEdit = useCallback((edgeId) => {
    openEdgeEditModal(edgeId);
  }, [openEdgeEditModal]);
  handleEdgeEditRef.current = handleEdgeEdit;

  const refreshFlowchartDataSilently = useCallback(async () => {
    if (!cycleId || !processId) return false;
    if (flowchartUndoSuppressGetRef.current) return false;
    try {
      const queryParams = selectedVersionNumber == null
        ? undefined
        : { versionNumber: selectedVersionNumber };
      const response = await axiosInstance.get(
        API_ENDPOINTS.getFlowchart(cycleId, processId),
        queryParams ? { params: queryParams } : {}
      );
      const {
        flowchartVersionId: versionId,
        versionStatus,
        versionNumber,
      } = response.data || {};
      if (versionId && versionId !== flowchartVersionId) {
        setFlowchartVersionId(versionId);
      }
      if (versionStatus && String(versionStatus) !== String(flowchartVersionStatus || '')) {
        setFlowchartVersionStatus(versionStatus);
      }
      if (versionNumber != null && Number(versionNumber) !== Number(flowchartVersionNumber)) {
        setFlowchartVersionNumber(versionNumber);
      }
      if (!response.data?.success || !response.data?.flowchartData) return false;

      const rawFlowchart = response.data.flowchartData;
      const liveEdgesBeforeGet = (edgesRef.current || []).length;
      const incomingConnectors = Array.isArray(rawFlowchart?.connectors)
        ? rawFlowchart.connectors.length
        : null;
      // Incomplete GET (missing nodes/connectors arrays) must not wipe connectors
      // or leave only swimlane actors — common under slower remote/dev races.
      if (isIncompleteFlowchartPayload(rawFlowchart)) {
        console.warn('[connector-wipe-debug] silentGET SKIP incomplete', {
          path: 'silentGET',
          liveEdges: liveEdgesBeforeGet,
          incomingConnectors,
          incomplete: true,
        });
        return false;
      }

      const transformed = transformDataToFlow(rawFlowchart);
      const liveProcessCount = (nodesRef.current || []).filter((n) => n.type !== 'swimlane').length;
      // Never replace a populated canvas with an empty process graph (lanes-only bug).
      if (transformed.nodes.length === 0 && liveProcessCount > 0) {
        console.warn('[connector-wipe-debug] silentGET SKIP empty nodes', {
          path: 'silentGET',
          liveEdges: liveEdgesBeforeGet,
          liveProcessCount,
          incomingConnectors,
          transformedNodes: transformed.nodes.length,
          transformedEdges: transformed.edges.length,
        });
        return false;
      }

      const refreshedRisks = Array.isArray(transformed.savedRisks) ? transformed.savedRisks : [];
      identifiedRisksRef.current = refreshedRisks;
      setIdentifiedRisks(refreshedRisks);
      const serverFullFlowchart = sanitizeFlowchartForSave(rawFlowchart);
      lastSavedFlowchartRef.current = serverFullFlowchart;
      hasUnsavedChangesRef.current = false;
      const refreshedEdges = transformed.edges.map((edge) => ({
        ...edge,
        data: {
          ...edge.data,
          onDelete: () => handleEdgeDelete(edge.id),
          onEdit: () => handleEdgeEdit(edge.id),
          onWaypointsChange: (wps) => handleWaypointsChange(edge.id, wps),
        },
      }));
      console.log('[connector-wipe-debug] silentGET setEdges', {
        path: 'silentGET',
        liveEdges: liveEdgesBeforeGet,
        incomingConnectors,
        outgoingEdges: refreshedEdges.length,
        wouldWipe: liveEdgesBeforeGet > 0 && refreshedEdges.length === 0,
      });

      setNodes((currentNodes) => {
        const swimlaneNodes = currentNodes.filter((node) => node.type === 'swimlane');
        const currentProcessNodes = currentNodes.filter((node) => node.type !== 'swimlane');
        const currentProcessNodeMap = new Map(
          currentProcessNodes.map((node) => [node.id, node])
        );

        // Fallback matcher for cases where backend rewrites node IDs after save/update.
        const getNodeSignature = (node) => {
          const d = node?.data || {};
          return [
            d.nodeType || '',
            (d.nodeDescription || '').trim(),
            (d.actor || '').trim(),
            (d.system || '').trim(),
            (d.soxControl || '').trim(),
            (d.document || '').trim(),
          ].join('||');
        };

        const currentNodesBySignature = new Map();
        currentProcessNodes.forEach((node) => {
          const signature = getNodeSignature(node);
          const bucket = currentNodesBySignature.get(signature) || [];
          bucket.push(node);
          currentNodesBySignature.set(signature, bucket);
        });

        const takeSignatureMatch = (node) => {
          const signature = getNodeSignature(node);
          const bucket = currentNodesBySignature.get(signature);
          if (!bucket || bucket.length === 0) return null;
          return bucket.shift() || null;
        };

        const refreshedProcessNodes = transformed.nodes.map((node) => {
          const existingById = currentProcessNodeMap.get(node.id);
          const matchedNode = existingById || takeSignatureMatch(node);
          const previousActor = String(matchedNode?.data?.actor || '').trim();
          const nextActor = String(node?.data?.actor || '').trim();
          const actorChanged = Boolean(matchedNode) && previousActor !== nextActor;
          const targetSwimlane = actorChanged && nextActor
            ? swimlaneBoundaries.find((boundary) =>
              boundary.actors.some((actor) => String(actor || '').trim() === nextActor)
            )
            : null;
          const targetLaneHeightForPlacement = targetSwimlane
            ? (targetSwimlane.yEnd - targetSwimlane.yStart)
            : SWIMLANE_HEIGHT;
          const resolvedPosition = actorChanged
            ? {
              x: typeof matchedNode?.position?.x === 'number'
                ? matchedNode.position.x
                : (typeof node?.position?.x === 'number' ? node.position.x : 0),
              y: targetSwimlane
                ? targetSwimlane.yStart + (targetLaneHeightForPlacement - NODE_HEIGHT) / 2
                : (typeof node?.position?.y === 'number'
                  ? node.position.y
                  : (matchedNode?.position || node.position)?.y),
            }
            : (matchedNode?.position || node.position);
          return {
            ...node,
            position: resolvedPosition,
            data: {
              ...(matchedNode?.data || {}),
              ...node.data,
            },
          };
        });

        return [...swimlaneNodes, ...refreshedProcessNodes];
      });
      setEdges(refreshedEdges);
      return true;
    } catch (error) {
      console.error('Error refreshing flowchart data:', error);
      return false;
    }
  }, [cycleId, processId, selectedVersionNumber, flowchartVersionId, flowchartVersionStatus, flowchartVersionNumber, setNodes, setEdges, setFlowchartVersionId, setFlowchartVersionStatus, setFlowchartVersionNumber, setIdentifiedRisks, handleEdgeDelete, handleEdgeEdit, swimlaneBoundaries]);
  refreshFlowchartDataRef.current = refreshFlowchartDataSilently;

  // During undo pause, full silent GET is suppressed (protects Z/Y canvas). Apply
  // attach/detach to identifiedRisks locally so badges show and flush save keeps them.
  const applyCatalogRiskLinkLocally = useCallback((detail) => {
    const action = detail?.action;
    const risk = detail?.risk;
    const nodeId = detail?.nodeId;
    if (!risk?.id || !nodeId || (action !== 'attach' && action !== 'detach')) return;

    const recordId = String(risk.id).trim();
    if (!recordId) return;

    if (action === 'detach') {
      setIdentifiedRisks((prev) => {
        const next = (Array.isArray(prev) ? prev : []).filter((entry) => !(
          getRiskRecordId(entry) === recordId && String(entry?.nodeId ?? '') === String(nodeId)
        ));
        identifiedRisksRef.current = next;
        return next;
      });
      return;
    }

    const displayCode = pickDisplayRiskCode(risk);
    const linkedEntry = {
      id: recordId,
      riskId: recordId,
      ...(displayCode ? { risk_id: displayCode } : {}),
      clientRiskId: risk.clientRiskId ?? risk.client_risk_id ?? null,
      client_risk_id: risk.clientRiskId ?? risk.client_risk_id ?? null,
      riskDescription: risk.riskDescription ?? risk.risk_description ?? '',
      riskLevel: risk.riskLevel ?? risk.risk_level ?? null,
      riskCategory: risk.riskCategory ?? risk.risk_category ?? null,
      source: risk.source ?? null,
      nodeId,
    };

    setIdentifiedRisks((prev) => {
      const list = Array.isArray(prev) ? prev : [];
      const alreadyLinked = list.some((entry) => (
        getRiskRecordId(entry) === recordId && String(entry?.nodeId ?? '') === String(nodeId)
      ));
      if (alreadyLinked) {
        identifiedRisksRef.current = list;
        return list;
      }
      const next = [...list, linkedEntry];
      identifiedRisksRef.current = next;
      return next;
    });
  }, []);

  const handleCatalogRiskAttached = useCallback(async (detail) => {
    if (flowchartUndoSuppressGetRef.current) {
      // Pause window: no full GET (would wipe undo canvas). Local risk stamp + absorb.
      flowchartUndoBeginLiveAbsorbRef.current?.(500);
      applyCatalogRiskLinkLocally(detail);
    } else {
      await refreshFlowchartDataRef.current?.();
    }
    await fetchRiskControlMappings();
    // Same stack as Identify / canvas: attach|detach becomes one undo step (draft only;
    // recordCanvasNow no-ops when undo controller disabled on published).
    flowchartUndoBeginLiveAbsorbRef.current?.(400);
    flowchartUndoRecordCanvasNowRef.current?.(
      nodesRef.current,
      edgesRef.current,
      identifiedRisksRef.current
    );
  }, [applyCatalogRiskLinkLocally, fetchRiskControlMappings]);

  const updateNodePositions = useCallback(async (nodesPositions, options = {}) => {
    const { positionOnlySync = false } = options;
    if (!flowchartVersionId || !Array.isArray(nodesPositions) || nodesPositions.length === 0) return false;
    if (nodePositionPatchInFlightRef.current) {
      nodesPositions.forEach((item) => {
        if (!item?.nodeId || !item?.position) return;
        pendingNodePositionPatchesRef.current.set(item.nodeId, item);
      });
      if (positionOnlySync) {
        pendingNodePositionPatchIncludesPositionOnlyRef.current = true;
      } else {
        pendingNodePositionPatchIncludesNonPositionOnlyRef.current = true;
      }
      return true;
    }

    nodePositionPatchInFlightRef.current = true;
    try {
      const response = await axiosInstance.patch(
        API_ENDPOINTS.updateFlowchartNodePositions(flowchartVersionId),
        { nodesPositions }
      );
      if (!response?.data?.success) return false;
      // Position-only PATCH already matches the live canvas. A full silent refresh
      // would replace nodes/edges from the server and wipe unsaved local work
      // (e.g. a just-added swimlane) — connectors then disappear. Only re-hydrate
      // when this path is not a pure position sync.
      if (positionOnlySync) {
        const currentFullFlowchart = sanitizeFlowchartForSave(convertToJSON());
        lastSavedFlowchartRef.current = currentFullFlowchart;
        hasUnsavedChangesRef.current = false;
        pendingAutoSaveRef.current = false;
      } else {
        await refreshFlowchartDataSilently();
      }
      return true;
    } catch (error) {
      console.error('Error updating node positions:', error);
      return false;
    } finally {
      nodePositionPatchInFlightRef.current = false;
      if (pendingNodePositionPatchesRef.current.size > 0) {
        const queuedPositions = Array.from(pendingNodePositionPatchesRef.current.values());
        const queuedPositionOnlySync =
          pendingNodePositionPatchIncludesPositionOnlyRef.current &&
          !pendingNodePositionPatchIncludesNonPositionOnlyRef.current;
        pendingNodePositionPatchesRef.current.clear();
        pendingNodePositionPatchIncludesPositionOnlyRef.current = false;
        pendingNodePositionPatchIncludesNonPositionOnlyRef.current = false;
        updateNodePositionsRef.current?.(queuedPositions, { positionOnlySync: queuedPositionOnlySync });
      }
      if (positionOnlySync) {
        positionOnlySyncInFlightRef.current = false;
      }
    }
  }, [flowchartVersionId, refreshFlowchartDataSilently, convertToJSON]);

  updateNodePositionsRef.current = updateNodePositions;

  // One-time sync after dashboard generation flow: send only changed/missing positions.
  useEffect(() => {
    if (!syncInitialPositions) return;
    if (!flowchartVersionId) return;
    if (isLayouting) return;
    if (initialPositionSyncDoneRef.current) return;

    const nodesNeedingPositionSync = (nodesRef.current || [])
      .filter((node) => node.type !== 'swimlane')
      .map((node) => {
        const frontendPosition = {
          x: typeof node.position?.x === 'number' ? node.position.x : 0,
          y: typeof node.position?.y === 'number' ? node.position.y : 0,
        };
        const backendPositionRaw = backendPositionByNodeId.get(node.id);
        const backendMissing =
          !backendPositionRaw ||
          typeof backendPositionRaw.x !== 'number' ||
          typeof backendPositionRaw.y !== 'number';
        const backendPosition = backendMissing
          ? null
          : { x: backendPositionRaw.x, y: backendPositionRaw.y };
        const isDifferent = !backendMissing && !deepEqual(backendPosition, frontendPosition);
        if (!backendMissing && !isDifferent) return null;
        return {
          nodeId: node.id,
          position: frontendPosition,
        };
      })
      .filter(Boolean);

    initialPositionSyncDoneRef.current = true;
    if (nodesNeedingPositionSync.length === 0) return;
    updateNodePositionsRef.current?.(nodesNeedingPositionSync);
  }, [syncInitialPositions, flowchartVersionId, isLayouting, nodes, backendPositionByNodeId]);

  const finalizeConnectorCreation = useCallback(async (nextLabel, nextIsBidirectional) => {
    if (!editingEdge) return;
    hasUserMutatedFlowchartRef.current = true;
    const isCreatingConnector = editingEdge?.data?.isTempConnector === true;
    if (isCreatingConnector) {
      // Always persist full flowchart for newly added connectors.
      // This avoids comparable-snapshot short-circuits on connector creation.
      lastSavedFlowchartRef.current = null;
    }

    const edgeMarkers = getEdgeMarkers(nextIsBidirectional);
    const updatedEdgesSnapshot = (edgesRef.current || []).map((edge) => {
      if (edge.id !== editingEdge.id) return edge;
      // Keep decision-node Yes/No anchors in sync with the label so the
      // in-session render matches what transformDataToFlow derives from the
      // label after a reload (handles themselves are never persisted).
      let sourceHandle = edge.sourceHandle || undefined;
      const sourceFlowNode = (nodesRef.current || []).find((node) => node.id === edge.source);
      if (sourceFlowNode?.type === 'decision') {
        const normalizedLabel = nextLabel.toLowerCase();
        sourceHandle =
          normalizedLabel === 'yes' ? 'right' : normalizedLabel === 'no' ? 'bottom' : undefined;
      }
      return {
        ...edge,
        sourceHandle,
        ...edgeMarkers,
        data: {
          ...edge.data,
          label: nextLabel,
          isBidirectional: nextIsBidirectional,
          isTempConnector: false,
        },
      };
    });

    setEdges(updatedEdgesSnapshot);

    setIsEdgeEditModalOpen(false);
    setEditingEdge(null);
    setEdgeLabelError('');

    const saveSucceeded = await persistFlowchart({ manual: false, edgesOverride: updatedEdgesSnapshot });
    if (saveSucceeded) {
      // persistFlowchart already hydrates (same-version refresh, or
      // onRefreshForVersionNumber on published→draft). A second silent refresh
      // can still use the old selectedVersionNumber and reload published edges,
      // hiding the newly added connector until hard refresh / version switch.
      queueSaveMessage(isCreatingConnector ? '✓ Connector created' : '✓ Connector updated');
    } else {
      queueSaveMessage('✗ Connector save failed');
    }
    setIsPendingConnectorConfirmation(false);
  }, [editingEdge, persistFlowchart, refreshFlowchartDataSilently, setEdges, queueSaveMessage]);

  // Save edge label
  const handleSaveEdgeLabel = useCallback(async () => {
    if (!editingEdge) return;
    const trimmedLabel = edgeEditForm.label.trim();
    if (trimmedLabel.length > CONNECTOR_LABEL_MAX_LENGTH) {
      setEdgeLabelError(`Label must be at most ${CONNECTOR_LABEL_MAX_LENGTH} characters`);
      return;
    }
    if (trimmedLabel !== edgeEditForm.label) {
      setEdgeEditForm((prev) => ({ ...prev, label: trimmedLabel }));
    }
    await finalizeConnectorCreation(trimmedLabel, edgeEditForm.isBidirectional === true);
  }, [editingEdge, edgeEditForm, finalizeConnectorCreation]);

  // Cancel edge edit
  const handleCancelEdgeEdit = useCallback(() => {
    if (editingEdge?.data?.isTempConnector) {
      setEdges((eds) => eds.filter((edge) => edge.id !== editingEdge.id));
      setIsPendingConnectorConfirmation(false);
    }
    setIsEdgeEditModalOpen(false);
    setEditingEdge(null);
    setEdgeLabelError('');
  }, [editingEdge, setEdges]);

  // Open swimlane edit modal
  const handleSwimlaneEditClick = useCallback((oldActor, label) => {
    setEditingSwimlane({ oldActor, label });
    setSwimlaneEditForm({ newActor: oldActor });
    setSwimlaneEditError('');
    setIsSwimlaneEditModalOpen(true);
  }, []);

  // Save swimlane edit - updates all nodes with the old actor to the new actor
  const handleSaveSwimlaneEdit = useCallback(() => {
    if (!editingSwimlane) return;
    hasUserMutatedFlowchartRef.current = true;

    const oldActor = editingSwimlane.oldActor;
    const newActor = swimlaneEditForm.newActor.trim();

    if (!newActor) {
      setSwimlaneEditError('Swimlane name cannot be empty');
      return;
    }

    if (newActor.length > SWIMLANE_NAME_MAX_LENGTH) {
      setSwimlaneEditError(`Swimlane name must be at most ${SWIMLANE_NAME_MAX_LENGTH} characters`);
      return;
    }

    if (oldActor === newActor) {
      setIsSwimlaneEditModalOpen(false);
      setEditingSwimlane(null);
      setSwimlaneEditError('');
      return;
    }

    // Rename the actor in the lane order in place so the renamed lane keeps its
    // current vertical position instead of being treated as a brand-new actor
    // (which buildLaneOrder would otherwise append at the bottom).
    swimlaneOrderRef.current = (swimlaneOrderRef.current || []).map((actor) =>
      actor === oldActor ? newActor : actor
    );

    setSwimlaneHeights((prev) => {
      if (!prev || !(oldActor in prev)) return prev;
      const next = { ...prev };
      next[newActor] = next[oldActor];
      delete next[oldActor];
      return next;
    });
    if (dirtyActorsRef.current.has(oldActor)) {
      dirtyActorsRef.current.delete(oldActor);
      dirtyActorsRef.current.add(newActor);
    }

    // Update all nodes that have the old actor
    setNodes((nds) =>
      nds.map((node) => {
        if (node.data.actor === oldActor) {
          return {
            ...node,
            data: {
              ...node.data,
              actor: newActor,
            },
          };
        }
        return node;
      })
    );

    // Trigger re-layout to regenerate swimlanes
    setLayoutTrigger(prev => prev + 1);

    queueSaveMessage(`✓ Swimlane "${oldActor}" renamed to "${newActor}"`, 3000);
    setIsSwimlaneEditModalOpen(false);
    setEditingSwimlane(null);
    setSwimlaneEditError('');
  }, [editingSwimlane, swimlaneEditForm, setNodes, queueSaveMessage]);

  // Cancel swimlane edit
  const handleCancelSwimlaneEdit = useCallback(() => {
    setIsSwimlaneEditModalOpen(false);
    setEditingSwimlane(null);
    setSwimlaneEditError('');
    setSwimlaneDeleteConfirmation(null);
  }, []);

  const getSwimlaneOrderIndex = useCallback((actor) => {
    const order = swimlaneOrderRef.current || [];
    return order.findIndex((entry) => entry === actor);
  }, []);

  // Move swimlane above/below by swapping lane order, then re-layout and persist Y.
  const handleMoveSwimlane = useCallback((direction) => {
    if (!editingSwimlane?.oldActor) return;
    const actor = editingSwimlane.oldActor;
    const order = [...(swimlaneOrderRef.current || [])];
    const index = order.indexOf(actor);
    if (index < 0) {
      setSwimlaneEditError('Could not find this swimlane in the current layout.');
      return;
    }
    const swapIndex = direction === 'up' ? index - 1 : index + 1;
    if (swapIndex < 0 || swapIndex >= order.length) {
      setSwimlaneEditError(
        direction === 'up'
          ? 'Swimlane is already at the top.'
          : 'Swimlane is already at the bottom.'
      );
      return;
    }

    hasUserMutatedFlowchartRef.current = true;
    setSwimlaneEditError('');
    const next = [...order];
    [next[index], next[swapIndex]] = [next[swapIndex], next[index]];
    swimlaneOrderRef.current = next;
    pendingSwimlaneReorderPersistRef.current = true;
    setLayoutTrigger((prev) => prev + 1);
    queueSaveMessage(
      direction === 'up'
        ? `✓ Moved swimlane "${actor}" up`
        : `✓ Moved swimlane "${actor}" down`,
      2500
    );
  }, [editingSwimlane, queueSaveMessage]);

  const handleDeleteSwimlaneClick = useCallback(() => {
    if (!editingSwimlane?.oldActor) return;
    const actor = editingSwimlane.oldActor;
    const processNodes = (nodesRef.current || []).filter(
      (node) => node.type !== 'swimlane' && String(node.data?.actor || '').trim() === actor
    );
    const hasStartOrEnd = processNodes.some(
      (node) => node.data?.nodeType === 'start' || node.data?.nodeType === 'end'
    );
    if (hasStartOrEnd) {
      setSwimlaneEditError(
        'Cannot delete a swimlane that contains Start or End steps. Move those steps to another swimlane first.'
      );
      return;
    }

    const nodeIds = new Set(processNodes.map((node) => node.id));
    const edgeCount = (edgesRef.current || []).filter(
      (edge) => nodeIds.has(edge.source) || nodeIds.has(edge.target)
    ).length;

    setSwimlaneEditError('');
    setSwimlaneDeleteConfirmation({
      actor,
      label: editingSwimlane.label || actor,
      nodeCount: processNodes.length,
      edgeCount,
    });
  }, [editingSwimlane]);

  const cancelSwimlaneDelete = useCallback(() => {
    setSwimlaneDeleteConfirmation(null);
  }, []);

  const confirmSwimlaneDelete = useCallback(() => {
    if (!swimlaneDeleteConfirmation?.actor) return;
    const actor = swimlaneDeleteConfirmation.actor;
    hasUserMutatedFlowchartRef.current = true;

    const processNodes = (nodesRef.current || []).filter(
      (node) => node.type !== 'swimlane' && String(node.data?.actor || '').trim() === actor
    );
    const nodeIds = new Set(processNodes.map((node) => node.id));

    setNodes((nds) =>
      nds.filter(
        (node) => !(node.type !== 'swimlane' && String(node.data?.actor || '').trim() === actor)
      )
    );
    setEdges((eds) =>
      eds.filter((edge) => !nodeIds.has(edge.source) && !nodeIds.has(edge.target))
    );

    swimlaneOrderRef.current = (swimlaneOrderRef.current || []).filter((entry) => entry !== actor);
    setSwimlaneHeights((prev) => {
      if (!prev || !(actor in prev)) return prev;
      const next = { ...prev };
      delete next[actor];
      return next;
    });
    dirtyActorsRef.current.delete(actor);

    setIdentifiedRisks((prev) =>
      Array.isArray(prev) ? prev.filter((risk) => !nodeIds.has(risk?.nodeId)) : prev
    );

    setLayoutTrigger((prev) => prev + 1);
    queueSaveMessage(`✓ Deleted swimlane "${swimlaneDeleteConfirmation.label}"`, 3000);
    setSwimlaneDeleteConfirmation(null);
    setIsSwimlaneEditModalOpen(false);
    setEditingSwimlane(null);
    setSwimlaneEditError('');
  }, [swimlaneDeleteConfirmation, setNodes, setEdges, queueSaveMessage]);

  // Open new swimlane modal
  const handleNewSwimlaneClick = useCallback(() => {
    setNewSwimlaneForm({
      actorName: '',
      placement: 'bottom',
      relativeTo: '',
    });
    setNewSwimlaneError('');
    setIsNewSwimlaneModalOpen(true);
  }, []);

  /** Build/Actions dropdown items — same handler is reused by both the collapsed (toolbar) and expanded (canvas) menus. */
  const handleActionsMenuAddNode = useCallback(() => {
    handleAddNode();
    setIsActionsMenuOpen(false);
  }, [handleAddNode]);

  const handleActionsMenuAddSwimlane = useCallback(() => {
    handleNewSwimlaneClick();
    setIsActionsMenuOpen(false);
  }, [handleNewSwimlaneClick]);

  const handleActionsMenuComplete = useCallback(() => {
    if (typeof onComplete === 'function') onComplete();
    setIsActionsMenuOpen(false);
  }, [onComplete]);


  // Create new swimlane - adds a placeholder node with the new actor
  const handleCreateNewSwimlane = useCallback(async () => {
    hasUserMutatedFlowchartRef.current = true;
    const actorName = newSwimlaneForm.actorName.trim();
    const placement = newSwimlaneForm.placement || 'bottom';
    const relativeTo = (newSwimlaneForm.relativeTo || '').trim();

    if (!actorName) {
      setNewSwimlaneError('Swimlane name cannot be empty');
      return;
    }

    if (actorName.length > SWIMLANE_NAME_MAX_LENGTH) {
      setNewSwimlaneError(`Swimlane name must be at most ${SWIMLANE_NAME_MAX_LENGTH} characters`);
      return;
    }

    // Check if swimlane already exists
    const existingActors = [...new Set(nodes.map(n => n.data.actor).filter(a => a && a.trim() !== ''))];
    if (existingActors.includes(actorName)) {
      setNewSwimlaneError(`Swimlane "${actorName}" already exists`);
      return;
    }

    const orderedActors = (swimlaneOrderRef.current || []).length > 0
      ? [...swimlaneOrderRef.current]
      : (Array.isArray(swimlaneBoundaries)
        ? swimlaneBoundaries
          .map((boundary) => String(boundary?.actors?.[0] || boundary?.label || '').trim())
          .filter(Boolean)
        : []);

    if ((placement === 'before' || placement === 'after')) {
      if (!relativeTo) {
        setNewSwimlaneError('Select an existing swimlane for placement');
        return;
      }
      if (!orderedActors.includes(relativeTo)) {
        setNewSwimlaneError(`Swimlane "${relativeTo}" was not found`);
        return;
      }
    }

    // Default remains bottom. Optional before/after inserts into the in-session
    // lane order so the next layout places the new lane in that slot.
    let nextOrder = [...orderedActors];
    if (placement === 'before' || placement === 'after') {
      const anchorIndex = nextOrder.indexOf(relativeTo);
      if (anchorIndex >= 0) {
        const insertAt = placement === 'before' ? anchorIndex : anchorIndex + 1;
        nextOrder = [
          ...nextOrder.slice(0, insertAt),
          actorName,
          ...nextOrder.slice(insertAt),
        ];
      } else {
        nextOrder = [...nextOrder, actorName];
      }
    } else if (!nextOrder.includes(actorName)) {
      nextOrder = [...nextOrder, actorName];
    }
    swimlaneOrderRef.current = nextOrder;

    // Placeholder Y: bottom = after last lane (existing behavior); before/after
    // uses the referenced lane boundary so the first paint lands near the slot.
    const boundaries = Array.isArray(swimlaneBoundaries) ? swimlaneBoundaries : [];
    const lastBoundary = boundaries.length > 0 ? boundaries[boundaries.length - 1] : null;
    const relativeBoundary = boundaries.find((boundary) => {
      const actor = String(boundary?.actors?.[0] || boundary?.label || '').trim();
      return actor === relativeTo;
    });

    let newLaneYStart = lastBoundary ? lastBoundary.yEnd : 0;
    if (placement === 'before' && relativeBoundary && Number.isFinite(relativeBoundary.yStart)) {
      newLaneYStart = relativeBoundary.yStart;
    } else if (placement === 'after' && relativeBoundary && Number.isFinite(relativeBoundary.yEnd)) {
      newLaneYStart = relativeBoundary.yEnd;
    }
    const newLaneCenterY = newLaneYStart + (SWIMLANE_HEIGHT - NODE_HEIGHT) / 2;

    // Create a placeholder node for the new swimlane
    const newNodeId = `swimlane-placeholder-${Date.now()}`;
    const newNode = {
      id: newNodeId,
      type: 'rectangle',
      // Place directly in the newly created lane to avoid transient wrong-lane rendering.
      position: { x: 100, y: newLaneCenterY },
      data: {
        nodeDescription: `${actorName} - Placeholder`,
        actor: actorName,
        system: '',
        soxControl: '',
        document: '',
        nodeType: 'process',
        isMapped: false,
      },
      style: {
        background: 'transparent',
        border: 'none',
        padding: '0',
        width: NODE_WIDTH,
        height: NODE_HEIGHT,
      },
    };

    // Add the new node
    setNodes((nds) => [...nds, newNode]);

    // Trigger re-layout to show new swimlane
    setLayoutTrigger(prev => prev + 1);

    queueSaveMessage(`✓ New swimlane "${actorName}" created`, 3000);
    setIsNewSwimlaneModalOpen(false);
    setNewSwimlaneError('');
  }, [newSwimlaneForm, nodes, setNodes, queueSaveMessage, swimlaneBoundaries]);

  // Cancel new swimlane
  const handleCancelNewSwimlane = useCallback(() => {
    setIsNewSwimlaneModalOpen(false);
    setNewSwimlaneError('');
  }, []);

  // Cancel delete
  const cancelDelete = useCallback(() => {
    setDeleteConfirmation(null);
  }, []);

  const isFlowchartDraft = useMemo(
    () => (selectedVersionOption ? isSelectedVersionDraft : normalizedFlowchartVersionStatus === 'draft'),
    [selectedVersionOption, isSelectedVersionDraft, normalizedFlowchartVersionStatus]
  );

  // Version number only — flowchartVersionId can change on save/refresh and was
  // clearing the undo stack (and pairing badly with layout-wait baseline).
  const flowchartUndoVersionKey =
    selectedVersionNumber != null && selectedVersionNumber !== ''
      ? String(selectedVersionNumber)
      : 'draft';

  const applyFlowchartUndoSnapshot = useCallback((snapshot) => {
    if (!snapshot) return;
    const { nodes: nextNodes, edges: nextEdges, heightUpdates } = reattachCanvasHandlers(snapshot, {
      onSwimlaneEdit: handleSwimlaneEditClick,
      onSwimlaneResize: handleSwimlaneResize,
      onNodeDelete: (nodeId) => handleDeleteClickRef.current?.(nodeId),
      onNodeEdit: (nodeId) => handleEditNodeRef.current?.(nodeId),
      onRiskClick: (nodeId, risks) => handleRiskClickRef.current?.(nodeId, risks),
      onHexagonControlClick: (nodeId, nodeDescription, control, risks) => (
        handleHexagonControlClickRef.current?.(nodeId, nodeDescription, control, risks)
      ),
      onHexagonControlsOverflowClick: (nodeId, nodeDescription, controls, risks) => (
        handleHexagonControlsOverflowClickRef.current?.(nodeId, nodeDescription, controls, risks)
      ),
      onEdgeDelete: (edgeId) => handleEdgeDeleteRef.current?.(edgeId),
      onEdgeEdit: (edgeId) => handleEdgeEditRef.current?.(edgeId),
      onEdgeWaypointsChange: (edgeId, wps) => handleWaypointsChange(edgeId, wps),
    });
    if (heightUpdates && Object.keys(heightUpdates).length > 0) {
      // Mark dirty only when height changed so swimlane-layout can catch up on flush.
      const prevHeights = swimlaneHeightsRef.current || {};
      let heightDirty = false;
      Object.entries(heightUpdates).forEach(([actor, h]) => {
        if (prevHeights[actor] !== h) {
          dirtyActorsRef.current.add(actor);
          heightDirty = true;
        }
      });
      if (heightDirty && swimlaneSaveTimerRef.current) {
        clearTimeout(swimlaneSaveTimerRef.current);
        swimlaneSaveTimerRef.current = null;
      }
      swimlaneHeightsRef.current = { ...prevHeights, ...heightUpdates };
      setSwimlaneHeights((prev) => ({ ...prev, ...heightUpdates }));
    }
    // Keep swimlaneOrderRef aligned with restored lane Y (rename/re-layout).
    const restoredLaneOrder = nextNodes
      .filter((node) => node.type === 'swimlane')
      .slice()
      .sort((a, b) => (a.position?.y ?? 0) - (b.position?.y ?? 0))
      .map((node) => String(node.data?.actor || '').trim())
      .filter(Boolean);
    if (restoredLaneOrder.length > 0) {
      swimlaneOrderRef.current = restoredLaneOrder;
    }
    // Save payload uses keySystems Set — restore it with the snapshot or flush keeps key.
    const restoredKeySystems = buildKeySystemsSetFromNodes(nextNodes);
    setKeySystems((prev) => (
      keySystemsSetsEqual(prev, restoredKeySystems) ? prev : restoredKeySystems
    ));
    const liveEdgesBeforeUndo = (edgesRef.current || []).length;
    console.log('[connector-wipe-debug] undoRestore setEdges', {
      path: 'undoRestore',
      liveEdges: liveEdgesBeforeUndo,
      outgoingEdges: (nextEdges || []).length,
      wouldWipe: liveEdgesBeforeUndo > 0 && (nextEdges || []).length === 0,
    });
    // Restore identifiedRisks only when the list changes — same-risk Z/Y must not
    // retrigger risks→nodes sync (that cleared Ctrl+Y previously).
    let restoredRisks = null;
    if (Object.prototype.hasOwnProperty.call(snapshot, 'identifiedRisks')) {
      restoredRisks = Array.isArray(snapshot.identifiedRisks) ? snapshot.identifiedRisks : [];
      if (!identifiedRisksListsEqual(identifiedRisksRef.current, restoredRisks)) {
        identifiedRisksRef.current = restoredRisks;
        setIdentifiedRisks(restoredRisks);
      } else {
        identifiedRisksRef.current = restoredRisks;
      }
    }
    // Stamp risks onto nodes to match identifiedRisks SoT before setNodes so the
    // risks→nodes effect is a no-op and does not clear Ctrl+Y after Identify→Z.
    let nodesToApply = nextNodes;
    if (restoredRisks) {
      nodesToApply = nextNodes.map((node) => {
        if (node.type === 'swimlane') return node;
        const nodeRisks = restoredRisks.filter((r) => r.nodeId === node.id);
        const prevRisks = Array.isArray(node.data?.risks) ? node.data.risks : [];
        if (nodeRisks.length > 0) {
          if (identifiedRisksListsEqual(prevRisks, nodeRisks)) {
            return node;
          }
          return {
            ...node,
            data: {
              ...node.data,
              risks: nodeRisks,
              onRiskClick: () => handleRiskClickRef.current?.(node.id, nodeRisks),
              onHexagonControlClick: (control) => handleHexagonControlClickRef.current?.(
                node.id,
                node.data?.nodeDescription,
                control,
                nodeRisks
              ),
              onHexagonControlsOverflowClick: (controls) => handleHexagonControlsOverflowClickRef.current?.(
                node.id,
                node.data?.nodeDescription,
                controls,
                nodeRisks
              ),
            },
          };
        }
        if (node.data?.risks) {
          const { risks, onRiskClick, onHexagonControlClick, onHexagonControlsOverflowClick, ...restData } = node.data;
          return { ...node, data: restData };
        }
        return node;
      });
    }
    setNodes(nodesToApply);
    setEdges(nextEdges);
    nodesRef.current = nodesToApply;
    edgesRef.current = nextEdges;
  }, [setNodes, setEdges, handleWaypointsChange, handleSwimlaneEditClick, handleSwimlaneResize]);

  const handleFlowchartUndoPersistFlush = useCallback(async () => {
    hasUserMutatedFlowchartRef.current = true;
    hasUnsavedChangesRef.current = true;
    lastSavedFlowchartRef.current = null;
    await flushSwimlaneLayoutSave();
    // Await persist so publish (and Save) can wait for the flush to finish.
    const saveSucceeded = await persistFlowchartRef.current?.({ manual: false });
    return saveSucceeded === true;
  }, [flushSwimlaneLayoutSave]);

  const handleAfterFlowchartUndoRedo = useCallback(() => {
    hasUserMutatedFlowchartRef.current = true;
    hasUnsavedChangesRef.current = true;
  }, []);

  const flowchartUndoGateRefs = useMemo(() => ({
    persistPausedRef: flowchartUndoPersistPausedRef,
    suppressGetRefreshRef: flowchartUndoSuppressGetRef,
    ignorePersistResultRef: flowchartUndoIgnorePersistResultRef,
  }), []);

  const {
    persistPaused: flowchartUndoPersistPaused,
    persistEndsAt: flowchartUndoPersistEndsAt,
    notifyPersistCaughtUp: flowchartUndoNotifyPersistCaughtUp,
    requestFlushNow: flowchartUndoRequestFlushNow,
    recordCanvasNow: flowchartUndoRecordCanvasNow,
    beginLiveSnapshotAbsorb: flowchartUndoBeginLiveSnapshotAbsorb,
  } = useFlowchartUndoController({
    enabled: isFlowchartDraft,
    versionKey: flowchartUndoVersionKey,
    isLayouting,
    getIsUserMutated: () => hasUserMutatedFlowchartRef.current,
    getIdentifiedRisks: () => identifiedRisksRef.current,
    nodes,
    edges,
    applySnapshot: applyFlowchartUndoSnapshot,
    onAfterUndoRedo: handleAfterFlowchartUndoRedo,
    onRequestPersistFlush: handleFlowchartUndoPersistFlush,
    gateRefs: flowchartUndoGateRefs,
  });

  const [flowchartUndoAutosaveSecondsLeft, setFlowchartUndoAutosaveSecondsLeft] = useState(null);
  useEffect(() => {
    if (!flowchartUndoPersistEndsAt) {
      setFlowchartUndoAutosaveSecondsLeft(null);
      return undefined;
    }
    const tick = () => {
      const left = Math.max(0, Math.ceil((flowchartUndoPersistEndsAt - Date.now()) / 1000));
      setFlowchartUndoAutosaveSecondsLeft(left);
    };
    tick();
    const id = setInterval(tick, 250);
    return () => clearInterval(id);
  }, [flowchartUndoPersistEndsAt]);

  useEffect(() => {
    flowchartUndoNotifyPersistCaughtUpRef.current = flowchartUndoNotifyPersistCaughtUp;
  }, [flowchartUndoNotifyPersistCaughtUp]);

  useEffect(() => {
    flowchartUndoRequestFlushNowRef.current = flowchartUndoRequestFlushNow;
  }, [flowchartUndoRequestFlushNow]);

  useEffect(() => {
    flowchartUndoRecordCanvasNowRef.current = flowchartUndoRecordCanvasNow;
  }, [flowchartUndoRecordCanvasNow]);

  useEffect(() => {
    flowchartUndoBeginLiveAbsorbRef.current = flowchartUndoBeginLiveSnapshotAbsorb;
  }, [flowchartUndoBeginLiveSnapshotAbsorb]);

  // Same as Identify Risks during Z/Y pause: All Risks cannot stay open.
  useEffect(() => {
    if (flowchartUndoPersistPaused) {
      setIsCatalogRisksPanelOpen(false);
    }
  }, [flowchartUndoPersistPaused]);

  useEffect(() => {
    const flushIfNeeded = () => {
      if (flowchartUndoPersistPausedRef.current) {
        flowchartUndoRequestFlushNow();
      }
    };
    window.addEventListener('beforeunload', flushIfNeeded);
    return () => {
      window.removeEventListener('beforeunload', flushIfNeeded);
      flushIfNeeded();
    };
  }, [flowchartUndoRequestFlushNow]);

  // Keyboard shortcut for delete - handle both nodes and edges
  useEffect(() => {
    const handleKeyDown = (event) => {
      if ((event.key === 'Delete' || event.key === 'Backspace')) {
        // Don't delete if user is typing in an input
        if (event.target.tagName === 'INPUT' || event.target.tagName === 'TEXTAREA') return;

        // Delete nodes
        if (selectedNodes.length > 0) {
          const selectedNode = selectedNodes[0];
          if (selectedNode.type !== 'swimlane') {
            handleDeleteClick(selectedNode.id);
          }
        }
        // Delete edges
        else if (selectedEdges.length > 0) {
          const selectedEdge = selectedEdges[0];
          handleEdgeDelete(selectedEdge.id);
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [selectedNodes, selectedEdges, handleDeleteClick, handleEdgeDelete]);

  return (
    <TooltipProvider>
      <div
        className={`${isFlowchartExpanded ? 'fixed inset-0 z-[70]' : 'h-screen'} flex flex-col`}
        style={{ backgroundColor: '#f9fafb' }}
      >
        {!isFlowchartExpanded && (
          <div className="border-b border-gray-200 bg-white px-5 py-3">
            <div className="flex flex-wrap items-center gap-3">
              <button
                onClick={onBack}
                className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-gray-200 bg-white text-gray-500 transition-colors hover:bg-gray-50 hover:text-gray-800"
                title="Back"
              >
                <ChevronLeft className="h-4 w-4" />
              </button>

              {processesWithFlowcharts.length > 0 && (
                <div className="relative min-w-[210px] max-w-[320px]" ref={processMenuRef}>
                  <Tooltip content="Select Process" side="bottom">
                    <button
                      type="button"
                      onClick={() => {
                        setIsVersionMenuOpen(false);
                        setIsStatusMenuOpen(false);
                        setIsProcessMenuOpen((prev) => !prev);
                      }}
                      className="inline-flex h-10 w-full cursor-pointer items-center justify-between rounded-xl border border-slate-200 bg-gradient-to-b from-white to-slate-50 px-3.5 text-sm font-medium text-slate-700 shadow-sm transition-all duration-150 hover:border-slate-300 hover:shadow focus:border-blue-500 focus:outline-none focus:ring-4 focus:ring-blue-100"
                      aria-haspopup="listbox"
                      aria-expanded={isProcessMenuOpen}
                    >
                      <span className="truncate pr-2">{selectedProcessLabel}</span>
                      <ChevronDown className={`h-4 w-4 text-slate-400 transition-transform ${isProcessMenuOpen ? 'rotate-180' : ''}`} />
                    </button>
                  </Tooltip>

                  {isProcessMenuOpen && (
                    <div className="absolute left-0 top-11 z-50 w-full overflow-hidden rounded-xl border border-blue-100 bg-white py-1.5 shadow-2xl ring-1 ring-blue-100/70">
                      <div className="max-h-80 overflow-auto px-1">
                        {processesWithFlowcharts.map((process) => {
                          const isSelected = String(process.process_id) === String(processId);
                          return (
                            <button
                              key={process.process_id}
                              type="button"
                              role="option"
                              aria-selected={isSelected}
                              onClick={() => {
                                setIsProcessMenuOpen(false);
                                if (!isSelected) {
                                  handleProcessChange(process.process_id);
                                }
                              }}
                              className={`flex w-full cursor-pointer items-center rounded-lg px-3 py-2 text-left text-sm transition-colors ${isSelected
                                  ? 'bg-blue-600 font-semibold text-white'
                                  : 'font-medium text-slate-700 hover:bg-blue-50'
                                }`}
                            >
                              <span className="truncate">{process.name}</span>
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              )}

              <div className="relative min-w-[150px]" ref={statusMenuRef}>
                <Tooltip content="Select the Process Status" side="bottom">
                  <button
                    type="button"
                    onClick={() => {
                      if (isStatusUpdating) return;
                      setIsProcessMenuOpen(false);
                      setIsVersionMenuOpen(false);
                      setIsStatusMenuOpen((prev) => !prev);
                    }}
                    disabled={isStatusUpdating}
                    className="inline-flex h-10 w-full cursor-pointer items-center justify-between rounded-xl border border-slate-200 bg-gradient-to-b from-white to-slate-50 px-3.5 text-sm font-medium text-slate-700 shadow-sm transition-all duration-150 hover:border-slate-300 hover:shadow focus:border-blue-500 focus:outline-none focus:ring-4 focus:ring-blue-100 disabled:cursor-not-allowed disabled:opacity-70"
                    aria-haspopup="listbox"
                    aria-expanded={isStatusMenuOpen}
                  >
                    <span className="truncate pr-2">
                      {isStatusUpdating ? 'Updating status...' : selectedStatusLabel}
                    </span>
                    <ChevronDown className={`h-4 w-4 text-slate-400 transition-transform ${isStatusMenuOpen ? 'rotate-180' : ''}`} />
                  </button>
                </Tooltip>

                {isStatusMenuOpen && (
                  <div className="absolute left-0 top-11 z-50 w-full overflow-hidden rounded-xl border border-blue-100 bg-white py-1.5 shadow-2xl ring-1 ring-blue-100/70">
                    <div className="max-h-80 overflow-auto px-1">
                      {SOX_DASHBOARD_STATUS_OPTIONS.map((option) => {
                        const isSelected = option.value === processStatus;
                        return (
                          <button
                            key={option.value}
                            type="button"
                            role="option"
                            aria-selected={isSelected}
                            onClick={async () => {
                              setIsStatusMenuOpen(false);
                              if (!isSelected) {
                                await handleProcessStatusChange(option.value);
                              }
                            }}
                            className={`flex w-full cursor-pointer items-center rounded-lg px-3 py-2 text-left text-sm transition-colors ${isSelected
                                ? 'bg-blue-600 font-semibold text-white'
                                : 'font-medium text-slate-700 hover:bg-blue-50'
                              }`}
                          >
                            <span className="truncate">{option.label}</span>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>

              <div className="relative min-w-[180px]" ref={versionMenuRef}>
                <Tooltip content="Select the Process Version" side="bottom">
                  <button
                    type="button"
                    onClick={() => {
                      if (isVersionChanging) return;
                      setIsProcessMenuOpen(false);
                      setIsStatusMenuOpen(false);
                      setIsVersionMenuOpen((prev) => !prev);
                    }}
                    disabled={isVersionChanging}
                    className="inline-flex h-10 w-full cursor-pointer items-center justify-between rounded-xl border border-slate-200 bg-gradient-to-b from-slate-50 to-white px-3.5 text-sm font-medium text-slate-700 shadow-sm transition-all duration-150 hover:border-slate-300 hover:shadow focus:border-blue-500 focus:outline-none focus:ring-4 focus:ring-blue-100 disabled:cursor-not-allowed disabled:opacity-70"
                    aria-haspopup="listbox"
                    aria-expanded={isVersionMenuOpen}
                  >
                    <span className="truncate pr-2">{isVersionChanging ? 'Loading version...' : selectedVersionLabel}</span>
                    <ChevronDown className={`h-4 w-4 text-slate-400 transition-transform ${isVersionMenuOpen ? 'rotate-180' : ''}`} />
                  </button>
                </Tooltip>

                {isVersionMenuOpen && (
                  <div className="absolute left-0 top-11 z-50 w-full overflow-hidden rounded-xl border border-blue-100 bg-white py-1.5 shadow-2xl ring-1 ring-blue-100/70">
                    <div className="max-h-80 overflow-auto px-1" ref={versionMenuListRef}>
                      {dropdownOptions.map((option) => {
                        const isSelected = option.value === selectedVersionNumber;
                        const isActiveVersion = option.isActive || option.status === 'active';
                        return (
                          <button
                            key={`${option.label}-${option.value == null ? 'null' : option.value}`}
                            type="button"
                            role="option"
                            aria-selected={isSelected}
                            data-version-selected={isSelected ? 'true' : 'false'}
                            data-version-active={isActiveVersion ? 'true' : 'false'}
                            onClick={async () => {
                              const nextVersionNumber = option.value == null ? null : Number(option.value);
                              if (nextVersionNumber === selectedVersionNumber) {
                                setIsVersionMenuOpen(false);
                                return;
                              }
                              setIsVersionChanging(true);
                              try {
                                await onVersionNumberChange(nextVersionNumber);
                                setIsVersionMenuOpen(false);
                              } finally {
                                setIsVersionChanging(false);
                              }
                            }}
                            className={`flex w-full cursor-pointer items-center rounded-lg px-3 py-2 text-left text-sm transition-colors ${isSelected
                                ? 'bg-blue-600 font-semibold text-white'
                                : 'font-medium text-slate-700 hover:bg-blue-50'
                              }`}
                          >
                            <span className="truncate">{option.label}</span>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>

              <div className="mx-1 h-8 w-px bg-slate-300/80" aria-hidden="true" />

              <div className="ml-auto flex flex-wrap items-center gap-2">
                {isFlowchartDraft && flowchartUndoPersistPaused && (
                  <>
                    <span className="text-xs font-medium text-amber-700" title="Autosave is paused after Undo/Redo">
                      Unsaved changes
                      {flowchartUndoAutosaveSecondsLeft != null
                        ? ` · Autosave in ${flowchartUndoAutosaveSecondsLeft}s`
                        : ''}
                    </span>
                    <button
                      type="button"
                      disabled={isAutoSaving}
                      onClick={() => {
                        void flowchartUndoRequestFlushNow();
                      }}
                      className={`inline-flex h-10 items-center rounded-lg border px-3 text-sm font-medium shadow-sm transition-colors focus:outline-none focus:ring-2 focus:ring-offset-1 ${isAutoSaving
                          ? 'cursor-not-allowed border-gray-200 bg-gray-100 text-gray-400'
                          : 'border-amber-300 bg-amber-50 text-amber-800 hover:bg-amber-100 focus:ring-amber-200'
                        }`}
                      title="Save now without waiting for autosave"
                    >
                      {isAutoSaving ? 'Saving...' : 'Save'}
                    </button>
                  </>
                )}

                {isFlowchartDraft && lastFlowchartSavedAt && (
                  <span className="text-xs font-medium text-slate-500" title="Last successful flowchart save for this version">
                    Last saved {new Date(lastFlowchartSavedAt).toLocaleTimeString()}
                  </span>
                )}

                {saveMessage && (
                  <span className={`text-xs font-medium ${saveMessage.includes('✓') ? 'text-green-600' :
                      saveMessage.includes('✗') || saveMessage.toLowerCase().includes('error') ? 'text-red-600' :
                        'text-gray-600'
                    }`}>
                    {saveMessage}
                  </span>
                )}

                {!saveMessage && isAutoSaving && !flowchartUndoPersistPaused && (
                  <span className="text-xs font-medium text-blue-600">Autosaving...</span>
                )}

                {!isFlowchartExpanded && (
                  <div className="relative" ref={actionsMenuRef}>
                    <button
                      onClick={() => setIsActionsMenuOpen((prev) => !prev)}
                      className="inline-flex h-10 items-center gap-1.5 rounded-lg border border-blue-200 bg-blue-50 px-3 text-sm font-medium text-blue-700 transition-colors hover:bg-blue-100 focus:outline-none focus:ring-2 focus:ring-blue-200"
                      aria-haspopup="menu"
                      aria-expanded={isActionsMenuOpen}
                    >
                      <span>Build</span>
                      <ChevronDown className={`h-4 w-4 transition-transform ${isActionsMenuOpen ? 'rotate-180' : ''}`} />
                    </button>

                    {isActionsMenuOpen && (
                      <div className="absolute right-0 top-11 z-50 w-60 overflow-hidden rounded-xl border border-slate-200 bg-white p-1.5 shadow-2xl ring-1 ring-slate-200/70 backdrop-blur-sm">
                        <button
                          onClick={handleActionsMenuAddNode}
                          className="flex w-full cursor-pointer items-center gap-2 rounded-lg px-2.5 py-2 text-left text-sm font-medium text-slate-700 transition-all duration-150 hover:bg-blue-50"
                          role="menuitem"
                        >
                          <Plus className="h-4 w-4 text-blue-600" />
                          <span>Add Node</span>
                        </button>

                        <button
                          onClick={handleActionsMenuAddSwimlane}
                          className="flex w-full cursor-pointer items-center gap-2 rounded-lg px-2.5 py-2 text-left text-sm font-medium text-slate-700 transition-all duration-150 hover:bg-blue-50"
                          role="menuitem"
                        >
                          <Plus className="h-4 w-4 text-blue-600" />
                          <span>Add Lane</span>
                        </button>

                        {onComplete && (
                          <button
                            onClick={handleActionsMenuComplete}
                            className="mt-1 flex w-full cursor-pointer items-center gap-2 rounded-lg border-t border-slate-100 px-2.5 py-2 text-left text-sm font-medium text-slate-700 transition-all duration-150 hover:bg-blue-50"
                            role="menuitem"
                          >
                            <Check className="h-4 w-4 text-blue-700" />
                            <span>Complete Setup</span>
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                )}

                {!isFlowchartExpanded && (
                  <Tooltip
                    content={
                      isIdentifyingRisks
                        ? 'Analyzing risks...'
                        : flowchartUndoPersistPaused
                          ? 'Unavailable during undo/redo'
                          : 'Identify Risks'
                    }
                    side="left"
                  >
                    <button
                      type="button"
                      onClick={handleIdentifyRisks}
                      disabled={isIdentifyingRisks || flowchartUndoPersistPaused}
                      aria-label="Identify Risks"
                      className={`inline-flex h-10 items-center gap-1.5 rounded-lg border px-3 text-sm font-medium shadow-sm transition-colors focus:outline-none focus:ring-2 focus:ring-offset-1 ${isIdentifyingRisks || flowchartUndoPersistPaused
                          ? 'cursor-not-allowed border-gray-200 bg-gray-100 text-gray-400 focus:ring-gray-200'
                          : identifiedRisks.length > 0
                            ? 'border-green-200 bg-green-50 text-green-700 hover:bg-green-100 focus:ring-green-200'
                            : 'border-red-200 bg-red-50 text-red-700 hover:bg-red-100 focus:ring-red-200'
                        }`}
                    >
                      {identifiedRisks.length > 0 ? (
                        <CheckCircle className="h-4 w-4" />
                      ) : (
                        <AlertCircle className="h-4 w-4" />
                      )}
                      <span>{isIdentifyingRisks ? 'Analyzing...' : 'Identify Risks'}</span>
                    </button>
                  </Tooltip>
                )}

              </div>
            </div>
          </div>
        )}

        {/* Main Content - Page Background */}
        <div
          className="flex-1 relative min-h-0 overflow-hidden"
          style={{ backgroundColor: '#f3f4f6', padding: isFlowchartExpanded ? '0px' : '24px' }}
        >
          {/* Canvas Container */}
          <div
            className="flex h-full min-h-0 w-full"
            style={{
              backgroundColor: '#ffffff',
              borderRadius: isFlowchartExpanded ? '0px' : '12px',
              boxShadow: isFlowchartExpanded ? 'none' : '0 10px 15px -3px rgba(0, 0, 0, 0.1)',
              border: isFlowchartExpanded ? 'none' : '1px solid #e5e7eb',
              overflow: 'hidden',
              position: 'relative'
            }}
          >
            <div className="relative flex min-h-0 min-w-0 flex-1 flex-col">
            {/* Flowchart Canvas */}
            <div className="relative h-full min-h-0 w-full flex-1">
              <div className="absolute right-3 top-3 z-20 flex items-center gap-2">
                <Tooltip
                  content={
                    flowchartUndoPersistPaused
                      ? 'Unavailable during undo/redo'
                      : isCatalogRisksPanelOpen
                        ? 'Close all risks'
                        : 'All risks'
                  }
                  side="left"
                >
                  <button
                    type="button"
                    onClick={toggleCatalogRisksPanel}
                    disabled={flowchartUndoPersistPaused}
                    aria-label="All risks"
                    className={`inline-flex h-8 items-center gap-1.5 rounded-full border px-3 text-xs font-medium shadow-sm transition-colors focus:outline-none focus:ring-2 focus:ring-offset-1 ${flowchartUndoPersistPaused
                        ? 'cursor-not-allowed border-gray-200 bg-gray-100 text-gray-400 focus:ring-gray-200'
                        : isCatalogRisksPanelOpen
                          ? 'border-blue-200 bg-blue-50 text-blue-700 hover:bg-blue-100 focus:ring-blue-200'
                          : 'border-slate-200 bg-white text-slate-600 hover:border-blue-300 hover:text-blue-600 focus:ring-slate-200'
                      }`}
                  >
                    <span>All risks</span>
                  </button>
                </Tooltip>
                <button
                  type="button"
                  onClick={toggleSubprocessLinksPanel}
                  className={`inline-flex h-8 items-center gap-1.5 rounded-full border px-3 text-xs font-medium shadow-sm transition-colors focus:outline-none focus:ring-2 focus:ring-offset-1 ${isSubprocessLinksPanelOpen
                      ? 'border-blue-200 bg-blue-50 text-blue-700 hover:bg-blue-100 focus:ring-blue-200'
                      : 'border-slate-200 bg-white text-slate-600 hover:border-blue-300 hover:text-blue-600 focus:ring-slate-200'
                    }`}
                >
                  <span>All subprocesses</span>
                </button>
                {isFlowchartExpanded && (
                  <div className="relative" ref={actionsMenuRef}>
                    <button
                      onClick={() => setIsActionsMenuOpen((prev) => !prev)}
                      className="inline-flex h-8 items-center gap-1.5 rounded-full border border-blue-200 bg-blue-50 px-3 text-xs font-medium text-blue-700 shadow-sm transition-colors hover:bg-blue-100 focus:outline-none focus:ring-2 focus:ring-blue-200"
                      aria-haspopup="menu"
                      aria-expanded={isActionsMenuOpen}
                    >
                      <span>Build</span>
                      <ChevronDown className={`h-4 w-4 transition-transform ${isActionsMenuOpen ? 'rotate-180' : ''}`} />
                    </button>

                    {isActionsMenuOpen && (
                      <div className="absolute right-0 top-10 z-50 w-60 overflow-hidden rounded-xl border border-slate-200 bg-white p-1.5 shadow-2xl ring-1 ring-slate-200/70 backdrop-blur-sm">
                        <button
                          onClick={handleActionsMenuAddNode}
                          className="flex w-full cursor-pointer items-center gap-2 rounded-lg px-2.5 py-2 text-left text-sm font-medium text-slate-700 transition-all duration-150 hover:bg-blue-50"
                          role="menuitem"
                        >
                          <Plus className="h-4 w-4 text-blue-600" />
                          <span>Add Node</span>
                        </button>

                        <button
                          onClick={handleActionsMenuAddSwimlane}
                          className="flex w-full cursor-pointer items-center gap-2 rounded-lg px-2.5 py-2 text-left text-sm font-medium text-slate-700 transition-all duration-150 hover:bg-blue-50"
                          role="menuitem"
                        >
                          <Plus className="h-4 w-4 text-blue-600" />
                          <span>Add Lane</span>
                        </button>

                        {onComplete && (
                          <button
                            onClick={handleActionsMenuComplete}
                            className="mt-1 flex w-full cursor-pointer items-center gap-2 rounded-lg border-t border-slate-100 px-2.5 py-2 text-left text-sm font-medium text-slate-700 transition-all duration-150 hover:bg-blue-50"
                            role="menuitem"
                          >
                            <Check className="h-4 w-4 text-blue-700" />
                            <span>Complete Setup</span>
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                )}

                {isFlowchartExpanded && (
                  <Tooltip
                    content={
                      isIdentifyingRisks
                        ? 'Analyzing risks...'
                        : flowchartUndoPersistPaused
                          ? 'Unavailable during undo/redo'
                          : 'Identify Risks'
                    }
                    side="left"
                  >
                    <button
                      type="button"
                      onClick={handleIdentifyRisks}
                      disabled={isIdentifyingRisks || flowchartUndoPersistPaused}
                      aria-label="Identify Risks"
                      className={`inline-flex h-8 items-center gap-1.5 rounded-full border px-3 text-xs font-medium shadow-sm transition-colors focus:outline-none focus:ring-2 focus:ring-offset-1 ${isIdentifyingRisks || flowchartUndoPersistPaused
                          ? 'cursor-not-allowed border-gray-200 bg-gray-100 text-gray-400 focus:ring-gray-200'
                          : identifiedRisks.length > 0
                            ? 'border-green-200 bg-green-50 text-green-700 hover:bg-green-100 focus:ring-green-200'
                            : 'border-red-200 bg-red-50 text-red-700 hover:bg-red-100 focus:ring-red-200'
                        }`}
                    >
                      {identifiedRisks.length > 0 ? (
                        <CheckCircle className="h-4 w-4" />
                      ) : (
                        <AlertCircle className="h-4 w-4" />
                      )}
                      <span>{isIdentifyingRisks ? 'Analyzing...' : 'Identify Risks'}</span>
                    </button>
                  </Tooltip>
                )}

                <Tooltip content={isMiniMapVisible ? 'Hide Mini Map' : 'Show Mini Map'} side="left">
                  <button
                    type="button"
                    onClick={() => setIsMiniMapVisible((prev) => !prev)}
                    aria-label={isMiniMapVisible ? 'Hide mini map' : 'Show mini map'}
                    className="inline-flex h-8 w-8 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-600 shadow-sm transition-colors hover:border-blue-300 hover:text-blue-600"
                  >
                    {isMiniMapVisible ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
                  </button>
                </Tooltip>

                <Tooltip content="Export PDF" side="left">
                  <button
                    type="button"
                    onClick={handleExportPDF}
                    aria-label="Export PDF"
                    className="inline-flex h-8 w-8 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-600 shadow-sm transition-colors hover:border-blue-300 hover:text-blue-600"
                  >
                    <FileDown className="h-4 w-4" />
                  </button>
                </Tooltip>

                <Tooltip content="Export SVG" side="left">
                  <button
                    type="button"
                    onClick={handleExportSVG}
                    aria-label="Export SVG"
                    className="inline-flex h-8 w-8 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-600 shadow-sm transition-colors hover:border-blue-300 hover:text-blue-600"
                  >
                    <FileImage className="h-4 w-4" />
                  </button>
                </Tooltip>

                {!isFlowchartVersionActive ? (
                  <button
                    type="button"
                    onClick={handleSetActive}
                    disabled={isSettingActive || isVersionChanging || !selectedFlowchartVersionId || isFlowchartDraft}
                    className={`inline-flex h-8 items-center gap-1 rounded-full border px-2.5 text-xs font-semibold transition-colors ${isSettingActive || isVersionChanging || !selectedFlowchartVersionId || isFlowchartDraft
                        ? 'cursor-not-allowed border-gray-200 bg-gray-100 text-gray-500'
                        : 'border-blue-200 bg-blue-50 text-blue-700 hover:bg-blue-100'
                      }`}
                    title={
                      !selectedFlowchartVersionId
                        ? 'Flowchart version unavailable'
                        : isFlowchartDraft
                          ? 'Draft version cannot be marked active'
                          : 'Mark selected version as active'
                    }
                  >
                    <Shield className="h-3.5 w-3.5" />
                    <span>{isSettingActive ? 'Setting...' : 'Mark as Active'}</span>
                  </button>
                ) : (
                  <span className="inline-flex h-8 items-center rounded-full border border-emerald-200 bg-emerald-50 px-2.5 text-xs font-semibold text-emerald-700">
                    Active
                  </span>
                )}

                {isFlowchartPublished ? (
                  <span className="inline-flex h-8 items-center rounded-full border border-emerald-200 bg-emerald-50 px-2.5 text-xs font-semibold text-emerald-700">
                    Published
                  </span>
                ) : isFlowchartDraft ? (
                  <button
                    type="button"
                    onClick={handlePublish}
                    disabled={isPublishing || !flowchartVersionId}
                    className={`inline-flex h-8 items-center gap-1 rounded-full border px-2.5 text-xs font-semibold transition-colors ${isPublishing || !flowchartVersionId
                        ? 'cursor-not-allowed border-gray-200 bg-gray-100 text-gray-500'
                        : 'border-gray-300 bg-white text-gray-800 hover:bg-gray-50'
                      }`}
                    title={!flowchartVersionId ? 'Flowchart version unavailable' : 'Publish flowchart version'}
                  >
                    <Check className="h-3.5 w-3.5" />
                    <span>{isPublishing ? 'Publishing...' : 'Publish'}</span>
                  </button>
                ) : (
                  <span className="inline-flex h-8 items-center rounded-full border border-amber-200 bg-amber-50 px-2.5 text-xs font-semibold text-amber-700">
                    Draft
                  </span>
                )}

                <Tooltip content={isFlowchartExpanded ? 'Exit Full Width View' : 'Expand to Full Width'} side="left">
                  <button
                    type="button"
                    onClick={() => setIsFlowchartExpanded((prev) => !prev)}
                    aria-label={isFlowchartExpanded ? 'Exit full width view' : 'Expand to full width'}
                    className="inline-flex h-8 w-8 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-600 shadow-sm transition-colors hover:border-blue-300 hover:text-blue-600"
                  >
                    {isFlowchartExpanded ? <Minimize2 className="h-4 w-4" /> : <Maximize2 className="h-4 w-4" />}
                  </button>
                </Tooltip>
              </div>
              <div ref={reactFlowWrapper} style={{ width: '100%', height: '100%' }}>
                <WaypointActionsContext.Provider value={handleWaypointsChange}>
                <SwimlaneDragHighlightContext.Provider value={swimlaneDragHighlight}>
                <ReactFlow
                  nodes={nodes}
                  edges={edges}
                  onNodesChange={handleNodesChange}
                  onEdgesChange={handleEdgesChange}
                  onConnect={onConnect}
                  isValidConnection={(connection) => isConnectionAllowed(connection, edgesRef.current)}
                  onInit={(inst) => { rfInstanceRef.current = inst; }}
                  onNodeDrag={onNodeDrag}
                  onNodeDragStop={onNodeDragStop}
                  onEdgeDoubleClick={handleEdgeDoubleClickAddWaypoint}
                  onNodeDoubleClick={onNodeDoubleClick}
                  onSelectionChange={onSelectionChange}
                  onMoveEnd={onMoveEnd}
                  nodeTypes={nodeTypes}
                  edgeTypes={edgeTypes}
                  nodesDraggable={true}
                  nodesConnectable={true}
                  elementsSelectable={true}
                  deleteKeyCode={null}
                  fitView
                  attributionPosition="bottom-left"
                  panOnScroll={true}
                  panOnScrollMode="free"
                  zoomOnScroll={false}
                  panOnDrag={true}
                  selectionOnDrag={false}
                  zoomOnDoubleClick={false}
                  connectionMode={ConnectionMode.Loose}
                  connectionLineType="smoothstep"
                  connectionRadius={38}
                >
                  <Background color="#d1d5db" gap={20} variant="dots" />
                  <FlowchartSubprocessLinkOverlay
                    links={subprocessLinks}
                    nodes={nodes}
                    processUuid={processData?.id}
                  />
                  <Controls position="bottom-right" />
                  {isMiniMapVisible && (
                    <MiniMap
                      position="bottom-left"
                      nodeColor="#9ca3af"
                      maskColor="rgba(0, 0, 0, 0.1)"
                      style={{ backgroundColor: '#f9fafb' }}
                    />
                  )}
                </ReactFlow>
                </SwimlaneDragHighlightContext.Provider>
                </WaypointActionsContext.Provider>
              </div>

              {isLayouting && (
                <div className="absolute inset-0 flex items-center justify-center bg-gray-50/80 z-20">
                  <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
                    <p className="text-gray-600">Generating flowchart...</p>
                  </div>
                </div>
              )}
            </div>
            <FlowchartLegend />
            </div>

            <FlowchartCatalogRisksSidePanel
              isOpen={isCatalogRisksPanelOpen}
              onClose={() => setIsCatalogRisksPanelOpen(false)}
              processUuid={processUuid}
              flowchartVersionId={flowchartVersionId}
              cycleId={cycleId}
              processId={processId}
              versionOptions={versionOptions}
              flowchartNodes={catalogPanelFlowchartNodes}
              onRiskAttached={handleCatalogRiskAttached}
              auditYearId={selectedAuditYearId}
            />
            <FlowchartSubprocessLinksSidePanel
              isOpen={isSubprocessLinksPanelOpen}
              onClose={() => setIsSubprocessLinksPanelOpen(false)}
              cycleId={cycleId}
              processUuid={processUuid}
              processDisplayId={processId}
              flowchartVersionId={flowchartVersionId}
              currentProcessNodes={subprocessPanelFlowchartNodes}
              existingLinks={subprocessLinks}
              onLinksChanged={setSubprocessLinks}
            />
          </div>

          {/* Delete Confirmation Modal */}
          {deleteConfirmation && (
            <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
              <div className="bg-white rounded-lg shadow-2xl w-full max-w-md p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Delete Node?</h3>

                <div className="space-y-3 mb-6">
                  <p className="text-gray-700">
                    Are you sure you want to delete <strong>"{deleteConfirmation.nodeTitle}"</strong>?
                  </p>
                  {deleteConfirmation.edgeCount > 0 && (
                    <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                      <p className="text-sm text-yellow-800">
                        <AlertCircle className="inline h-4 w-4 mr-1" />
                        This will also remove {deleteConfirmation.edgeCount} connected edge{deleteConfirmation.edgeCount > 1 ? 's' : ''}.
                      </p>
                    </div>
                  )}
                </div>

                <div className="flex justify-end space-x-3">
                  <button
                    onClick={cancelDelete}
                    className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={confirmDelete}
                    className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                  >
                    Delete
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Risk Analysis Loading Modal */}
          {isIdentifyingRisks && (
            <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
              <div className="bg-white rounded-lg shadow-2xl w-full max-w-md p-6">
                <div className="flex items-center gap-3 mb-6 p-4 bg-red-50 rounded-lg">
                  <Loader className="w-5 h-5 text-red-600 animate-spin" />
                  <span className="text-red-900 font-medium">Analyzing Process Risks</span>
                </div>

                <div className="mb-6">
                  <div className="flex justify-between text-sm mb-2">
                    <span className="text-gray-600">Analysis Progress</span>
                    <span className="text-gray-900 font-medium">{riskAnalysisProgress}%</span>
                  </div>
                  <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-red-600 transition-all duration-300"
                      style={{ width: `${riskAnalysisProgress}%` }}
                    />
                  </div>
                </div>

                <div className="space-y-3">
                  {riskAnalysisSteps.map((step, index) => (
                    <div key={index} className="flex items-center gap-3">
                      <div className={`w-7 h-7 rounded-full flex items-center justify-center ${step.status === 'completed' ? 'bg-green-100' :
                          step.status === 'processing' ? 'bg-red-100' :
                            'bg-gray-100'
                        }`}>
                        {step.status === 'completed' ? (
                          <CheckCircle className="w-4 h-4 text-green-600" />
                        ) : step.status === 'processing' ? (
                          <Loader className="w-4 h-4 text-red-600 animate-spin" />
                        ) : (
                          <div className="w-2 h-2 bg-gray-400 rounded-full" />
                        )}
                      </div>
                      <div className="flex-1">
                        <p className={`text-sm font-medium ${step.status === 'completed' ? 'text-green-600' :
                            step.status === 'processing' ? 'text-red-600' :
                              'text-gray-400'
                          }`}>
                          {step.name}
                        </p>
                        {step.status === 'completed' && (
                          <p className="text-xs text-gray-500">Complete</p>
                        )}
                        {step.status === 'processing' && (
                          <p className="text-xs text-gray-500">Processing...</p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Risk Details Modal */}
          {isRiskModalOpen && selectedRisk && (
            <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
              <div className="bg-white rounded-lg shadow-2xl w-full max-w-lg max-h-[80vh] flex flex-col overflow-hidden">
                <div className="flex items-center justify-between p-6 pb-4 border-b border-gray-200 bg-white shrink-0">
                  <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                    <AlertCircle className="h-5 w-5 text-red-600" />
                    Identified Risks ({selectedRisk.risks.length})
                  </h3>
                  <button
                    onClick={() => {
                      setIsRiskModalOpen(false);
                      setSelectedRisk(null);
                    }}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>

                <div className="flex-1 overflow-y-auto p-6 pt-4 space-y-4">
                  {selectedRisk.risks.map((risk, index) => {
                    const cardKey = getRiskRecordId(risk) || getRiskDisplayIdentifier(risk) || index;
                    const recordId = getRiskRecordId(risk);
                    const mappingKey = getRiskMappingKey(selectedRisk.nodeId, risk);
                    const mapping = riskControlMappings[mappingKey];

                    return (
                      <div key={cardKey} className="space-y-3">
                        <IdentifiedRiskEditableCard
                          risk={risk}
                          onUpdateRisk={handleUpdateIdentifiedRisk}
                          onDeleteRisk={handleRiskDeleteClick}
                          isSaving={!!recordId && savingRiskId === recordId}
                        />

                        {mapping ? (
                          <div className="bg-teal-50 rounded p-3 border border-teal-200">
                            <p className="text-xs font-semibold text-teal-700 mb-1 flex items-center gap-1">
                              <Shield className="h-3 w-3" /> Mapped Control
                            </p>
                            <div className="flex items-center gap-2">
                              <span className="text-sm font-medium text-teal-900">{mapping.controlDisplayId}</span>
                              {mapping.isAIRecommendation && (
                                <span className="text-xs bg-purple-100 text-purple-700 px-1.5 py-0.5 rounded">AI</span>
                              )}
                            </div>
                            <p className="text-sm text-teal-800 mt-1">{mapping.controlTitle}</p>
                          </div>
                        ) : null}
                      </div>
                    );
                  })}
                </div>

              </div>
            </div>
          )}

          <DeleteModal
            isOpen={!!riskDeleteConfirmation}
            title="Delete Risk?"
            message={`Are you sure you want to delete this risk ${getRiskDisplayIdentifier(riskDeleteConfirmation?.risk) || getRiskRecordId(riskDeleteConfirmation?.risk) || 'Risk'}?`}
            onClose={cancelRiskDelete}
            onConfirm={confirmRiskDelete}
            confirmLabel="Delete"
            cancelLabel="Cancel"
            isConfirming={deleteIdentifiedRiskMutation.isPending}
          />

          {/* Control Details Modal */}
          {isControlModalOpen && selectedControlIndicator && (() => {
            const isHexagonOverflowSource = selectedControlIndicator.source === 'hexagon-overflow';
            const isHexagonSource = selectedControlIndicator.source === 'hexagon';
            const control = selectedControlIndicator.control || {};
            const isKey = isHexagonSource
              ? isKeyControlSignificance(control)
              : String(control.significance || '').trim().toLowerCase() === 'key';
            const clientControlId = isHexagonSource
              ? getControlDisplayLabel(control)
              : (control.controlDisplayId || getControlDisplayLabel(control) || '');
            const controlWording = String(
              control.control_wording_text
              ?? control.controlWordingText
              ?? ''
            ).trim();
            const sourceRisks = Array.isArray(selectedControlIndicator.sourceRisks)
              ? selectedControlIndicator.sourceRisks
              : [];
            const panelTone = isKey
              ? {
                shell: 'border-orange-300 bg-orange-50',
                header: 'border-orange-200 bg-orange-100',
                icon: 'text-orange-700',
                title: 'text-orange-950',
                label: 'text-orange-800',
                body: 'text-orange-950',
                riskCard: 'border-orange-200 bg-white',
              }
              : {
                shell: 'border-purple-300 bg-purple-50',
                header: 'border-purple-200 bg-purple-100',
                icon: 'text-purple-700',
                title: 'text-purple-950',
                label: 'text-purple-800',
                body: 'text-purple-950',
                riskCard: 'border-purple-200 bg-white',
              };

            if (isHexagonOverflowSource) {
              const overflowControls = Array.isArray(selectedControlIndicator.controls)
                ? selectedControlIndicator.controls
                : [];
              return (
                <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                  <div className="rounded-lg shadow-2xl w-full max-w-lg max-h-[80vh] flex flex-col overflow-hidden border border-purple-300 bg-purple-50">
                    <div className="flex items-center justify-between p-6 pb-4 border-b shrink-0 border-purple-200 bg-purple-100">
                      <h3 className="text-lg font-semibold flex items-center gap-2 text-purple-950">
                        <Shield className="h-5 w-5 text-purple-700" />
                        Controls ({overflowControls.length})
                      </h3>
                      <button
                        onClick={() => {
                          setIsControlModalOpen(false);
                          setSelectedControlIndicator(null);
                        }}
                        className="text-gray-500 hover:text-gray-700"
                      >
                        <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </div>
                    <div className="flex-1 overflow-y-auto p-4 space-y-2">
                      {overflowControls.map((entry, index) => {
                        const label = getControlDisplayLabel(entry) || `Control ${index + 1}`;
                        const entryKey = String(entry.id ?? entry.control_id ?? entry.controlId ?? `${label}-${index}`);
                        const entryIsKey = isKeyControlSignificance(entry);
                        return (
                          <button
                            key={entryKey}
                            type="button"
                            onClick={() => {
                              handleHexagonControlClick(
                                selectedControlIndicator.nodeId,
                                selectedControlIndicator.nodeDescription,
                                entry,
                                selectedControlIndicator.risks
                              );
                            }}
                            className={`w-full text-left border rounded-lg p-3 hover:shadow-sm transition-shadow ${
                              entryIsKey
                                ? 'border-orange-200 bg-orange-50 hover:bg-orange-100'
                                : 'border-purple-200 bg-white hover:bg-purple-50'
                            }`}
                          >
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className={`text-sm font-semibold ${entryIsKey ? 'text-orange-950' : 'text-purple-950'}`}>
                                {label}
                              </span>
                              {entryIsKey && (
                                <span className="text-[10px] font-bold uppercase tracking-wide text-orange-800 bg-orange-200 px-1.5 py-0.5 rounded">
                                  Key
                                </span>
                              )}
                            </div>
                            {String(entry.control_wording_text ?? entry.controlWordingText ?? '').trim() ? (
                              <p className={`text-xs mt-1 line-clamp-2 ${entryIsKey ? 'text-orange-900' : 'text-purple-900'}`}>
                                {String(entry.control_wording_text ?? entry.controlWordingText ?? '').trim()}
                              </p>
                            ) : null}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                </div>
              );
            }

            if (isHexagonSource) {
              return (
                <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                  <div className={`rounded-lg shadow-2xl w-full max-w-lg max-h-[80vh] flex flex-col overflow-hidden border ${panelTone.shell}`}>
                    <div className={`flex items-center justify-between p-6 pb-4 border-b shrink-0 ${panelTone.header}`}>
                      <h3 className={`text-lg font-semibold flex items-center gap-2 ${panelTone.title}`}>
                        <Shield className={`h-5 w-5 ${panelTone.icon}`} />
                        {isKey ? 'Key Control' : 'Control'}
                        {clientControlId ? `: ${clientControlId}` : ''}
                      </h3>
                      <button
                        onClick={() => {
                          setIsControlModalOpen(false);
                          setSelectedControlIndicator(null);
                        }}
                        className="text-gray-500 hover:text-gray-700"
                      >
                        <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </div>

                    <div className="flex-1 overflow-y-auto p-6 pt-4 space-y-4">
                      <div>
                        <label className={`text-xs font-medium uppercase ${panelTone.label}`}>Control ID</label>
                        <p className={`text-sm mt-1 font-medium ${panelTone.body}`}>
                          {clientControlId || 'N/A'}
                        </p>
                      </div>

                      <div>
                        <label className={`text-xs font-medium uppercase ${panelTone.label}`}>Control Wording</label>
                        <p className={`text-sm mt-1 whitespace-pre-wrap ${panelTone.body}`}>
                          {controlWording || 'N/A'}
                        </p>
                      </div>

                      <div>
                        <label className={`text-xs font-medium uppercase mb-2 block ${panelTone.label}`}>
                          From Risk{sourceRisks.length === 1 ? '' : 's'}
                        </label>
                        {sourceRisks.length > 0 ? (
                          <div className="space-y-2">
                            {sourceRisks.map((risk, index) => {
                              const riskKey = getRiskRecordId(risk) || getRiskDisplayIdentifier(risk) || index;
                              const riskIdLabel = getRiskDisplayIdentifier(risk) || getRiskRecordId(risk) || 'Risk';
                              const riskLevel = normalizeRiskLevelValue(risk) || risk?.riskLevel || risk?.risk_level || '';
                              const riskDescription = String(
                                risk?.riskDescription ?? risk?.risk_description ?? ''
                              ).trim();
                              return (
                                <div key={riskKey} className={`border rounded-lg p-3 ${panelTone.riskCard}`}>
                                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                                    <span className={`text-xs font-semibold ${panelTone.body}`}>{riskIdLabel}</span>
                                    {riskLevel ? (
                                      <span className={`px-2 py-0.5 rounded text-xs font-semibold ${
                                        riskLevel === 'High' ? 'bg-red-600 text-white'
                                          : riskLevel === 'Medium' ? 'bg-amber-500 text-white'
                                            : 'bg-yellow-500 text-white'
                                      }`}>
                                        {riskLevel}
                                      </span>
                                    ) : null}
                                    <span className="text-xs text-gray-500">{getRiskCategoryLabel(risk.riskCategory ?? risk.risk_category)}</span>
                                  </div>
                                  {riskDescription ? (
                                    <p className="text-sm text-gray-800">{riskDescription}</p>
                                  ) : null}
                                </div>
                              );
                            })}
                          </div>
                        ) : (
                          <p className={`text-sm ${panelTone.body}`}>No linked risk found for this control.</p>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              );
            }

            // Existing mapped-control (teal badge) popup — unchanged fields.
            return (
              <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                <div className="bg-white rounded-lg shadow-2xl w-full max-w-lg p-6 max-h-[80vh] overflow-y-auto">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                      <Shield className="h-5 w-5 text-teal-600" />
                      Control: {selectedControlIndicator.control.controlDisplayId}
                    </h3>
                    <button
                      onClick={() => {
                        setIsControlModalOpen(false);
                        setSelectedControlIndicator(null);
                      }}
                      className="text-gray-400 hover:text-gray-600"
                    >
                      <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>

                  <div className="flex gap-4 mb-4">
                    <div>
                      <label className="text-xs font-medium text-gray-500 uppercase">Type</label>
                      <p className="text-sm text-gray-900 mt-1 capitalize">{selectedControlIndicator.control.controlType || 'N/A'}</p>
                    </div>
                    <div>
                      <label className="text-xs font-medium text-gray-500 uppercase">Significance</label>
                      <p className="text-sm text-gray-900 mt-1 capitalize">{selectedControlIndicator.control.significance || 'N/A'}</p>
                    </div>
                  </div>

                  <div className="space-y-4">
                    <div>
                      <label className="text-xs font-medium text-gray-500 uppercase">Control Title</label>
                      <p className="text-sm text-gray-900 mt-1">{selectedControlIndicator.control.controlTitle}</p>
                    </div>

                    <div>
                      <label className="text-xs font-medium text-gray-500 uppercase mb-2 block">
                        Associated Risk:
                      </label>
                      <div className="space-y-2">
                        {(selectedControlIndicator.control.riskIndices || []).map(riskIdx => {
                          const risk = selectedControlIndicator.risks[riskIdx];
                          if (!risk) return null;
                          return (
                            <div key={riskIdx} className="border rounded-lg p-3 bg-gray-50">
                              <div className="flex items-center gap-2 mb-1">
                                <span className={`px-2 py-0.5 rounded text-xs font-semibold ${risk.riskLevel === 'High' ? 'bg-red-600 text-white' :
                                    risk.riskLevel === 'Medium' ? 'bg-amber-500 text-white' :
                                      'bg-yellow-500 text-white'
                                  }`}>{risk.riskLevel}</span>
                                <span className="text-xs text-gray-500">{getRiskCategoryLabel(risk.riskCategory)}</span>
                              </div>
                              <p className="text-sm text-gray-800">{risk.riskDescription}</p>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>

                </div>
              </div>
            );
          })()}

          {/* Edit/Add Node Modal */}
          {isEditModalOpen && (
            <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
              <div className="bg-white rounded-lg shadow-2xl w-full max-w-md p-6 max-h-[90vh] overflow-y-auto">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">
                  {isAddingNode ? 'Add Node' : 'Edit Node'}
                </h3>

                <div className="space-y-4">
                  {/* Node Type */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Node Type *</label>
                    <select
                      value={editForm.nodeType}
                      onChange={(e) => {
                        setEditForm({ ...editForm, nodeType: e.target.value });
                        setNodeFormErrors((prev) => ({ ...prev, nodeType: '' }));
                      }}
                      className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${nodeFormErrors.nodeType ? 'border-red-500' : 'border-gray-300'
                        }`}
                    >
                      <option value="start">Start (Oval)</option>
                      <option value="process">Process (Rectangle)</option>
                      <option value="decision">Decision (Rhombus)</option>
                      <option value="end">End (Oval)</option>
                    </select>
                    {nodeFormErrors.nodeType && (
                      <p className="text-sm text-red-600 mt-1">{nodeFormErrors.nodeType}</p>
                    )}
                  </div>

                  {/* Node Description */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Description *</label>
                    <textarea
                      value={editForm.nodeDescription}
                      onChange={(e) => {
                        setEditForm({ ...editForm, nodeDescription: e.target.value });
                        setNodeFormErrors((prev) => ({ ...prev, nodeDescription: '' }));
                      }}
                      rows={3}
                      className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${nodeFormErrors.nodeDescription ? 'border-red-500' : 'border-gray-300'
                        }`}
                      placeholder="Enter node description"
                    />
                    {nodeFormErrors.nodeDescription && (
                      <p className="text-sm text-red-600 mt-1">{nodeFormErrors.nodeDescription}</p>
                    )}
                  </div>

                  {/* Actor - Dropdown with existing actors */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Actor (Swimlane) *</label>
                    {!isAddingNewActor ? (
                      <div className="relative">
                        <select
                          value={editForm.actor}
                          onChange={(e) => {
                            if (e.target.value === '__NEW__') {
                              setIsAddingNewActor(true);
                              setEditForm({ ...editForm, actor: '' });
                            } else {
                              setEditForm({ ...editForm, actor: e.target.value });
                            }
                            setNodeFormErrors((prev) => ({ ...prev, actor: '' }));
                          }}
                          className={`w-full px-3 py-2 pr-10 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white appearance-none cursor-pointer ${nodeFormErrors.actor ? 'border-red-500' : 'border-gray-300'
                            }`}
                        >
                          <option value="">-- Select Actor or Create New --</option>
                          {[...new Set(nodes.map(n => n.data.actor).filter(a => a && a.trim() !== ''))].map(actor => (
                            <option key={actor} value={actor}>{actor}</option>
                          ))}
                          <option value="__NEW__">+ Add New Actor...</option>
                        </select>
                        <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
                      </div>
                    ) : (
                      <div className="space-y-2">
                        <input
                          type="text"
                          value={editForm.actor}
                          placeholder="Enter new actor name"
                          autoFocus
                          onChange={(e) => setEditForm({ ...editForm, actor: e.target.value })}
                          className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${nodeFormErrors.actor ? 'border-red-500' : 'border-blue-300'
                            }`}
                        />
                        <button
                          type="button"
                          onClick={() => {
                            setIsAddingNewActor(false);
                            setEditForm({ ...editForm, actor: '' });
                          }}
                          className="text-sm text-gray-600 hover:text-gray-800 underline"
                        >
                          ← Back to select existing
                        </button>
                      </div>
                    )}

                    <p className="text-xs text-gray-500 mt-1">
                      Select existing actor or add new to create swimlane
                    </p>
                    {nodeFormErrors.actor && (
                      <p className="text-sm text-red-600 mt-1">{nodeFormErrors.actor}</p>
                    )}
                  </div>

                  {/* System (multi-select with add-new modal) */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">System</label>
                    <SystemMultiSelect
                      value={editForm.system}
                      error={!!nodeFormErrors.system}
                      onChange={(next) => {
                        setEditForm({ ...editForm, system: next });
                        setNodeFormErrors((prev) => ({ ...prev, system: '' }));
                      }}
                      onSystemCreated={registerSystemInCatalog}
                    />
                    {nodeFormErrors.system && (
                      <p className="text-sm text-red-600 mt-1">{nodeFormErrors.system}</p>
                    )}

                    {/* Key System checkboxes — one per selected system so multi-select cases work cleanly */}
                    {editForm.system && editForm.system.trim() !== '' && (
                      <div className="mt-2 space-y-1">
                        {editForm.system
                          .split(/[,/]/)
                          .map((s) => s.trim())
                          .filter(Boolean)
                          .map((sysName) => (
                            <label key={sysName} className="flex items-center space-x-2 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={keySystems.has(sysName)}
                                onChange={(e) => {
                                  handleKeySystemToggle(sysName, e.target.checked);
                                }}
                                className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                              />
                              <span className="text-sm text-gray-700">
                                Key System{' '}
                                <span className="text-gray-500">(applies to all &quot;{sysName}&quot; nodes)</span>
                              </span>
                            </label>
                          ))}
                      </div>
                    )}
                  </div>

                  {/* Key Control — same visibility as before (when soxControl already exists on the node).
                      SOX Control text input was removed from this form only. */}
                  {editForm.soxControl && editForm.soxControl.trim() !== '' && (
                    <div>
                      <label className="flex items-center space-x-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={editForm.keyControl || false}
                          onChange={(e) => setEditForm({ ...editForm, keyControl: e.target.checked })}
                          className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                        />
                        <span className="text-sm text-gray-700">
                          Key Control
                        </span>
                      </label>
                    </div>
                  )}

                  {/* Documents — attach from catalog; each linked doc is one box (view / pencil-edit) */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Document</label>
                    <DocumentCatalogSelect
                      excludeIds={(editForm.documents || []).map((d) => d.id).filter(Boolean)}
                      onSelect={(picked) => {
                        if (!picked?.id) return;
                        setEditForm((prev) => {
                          const existing = Array.isArray(prev.documents) ? prev.documents : [];
                          if (existing.some((d) => d.id === picked.id)) return prev;
                          return {
                            ...prev,
                            documents: [
                              ...existing,
                              {
                                id: picked.id,
                                name: picked.name || '',
                                docType: picked.docType === 'report' ? 'report' : null,
                                systems: Array.isArray(picked.systems) ? picked.systems : [],
                              },
                            ],
                          };
                        });
                        setEditingDocumentKey(picked.id);
                      }}
                    />
                    {!Array.isArray(editForm.documents) || editForm.documents.length === 0 ? (
                      <p className="text-sm text-gray-400 px-1 py-2">No documents linked</p>
                    ) : (
                      <div className="space-y-2">
                        {editForm.documents.map((doc, index) => {
                          const docKey = doc.id || `doc-${index}`;
                          const isReport = doc.docType === 'report';
                          const isEditingDoc = editingDocumentKey === docKey;
                          const systemNames = Array.isArray(doc.systems)
                            ? doc.systems.map((s) => (typeof s?.name === 'string' ? s.name.trim() : '')).filter(Boolean)
                            : [];

                          if (!isEditingDoc) {
                            return (
                              <div
                                key={docKey}
                                className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-2.5"
                              >
                                <div className="flex items-start justify-between gap-2">
                                  <div className="min-w-0 flex-1 space-y-1.5">
                                    <p className="text-sm font-medium text-gray-900 truncate">
                                      {doc.name || 'Untitled'}
                                    </p>
                                    <div className="flex flex-wrap items-center gap-1.5">
                                      <span
                                        className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                                          isReport
                                            ? 'bg-amber-50 text-amber-900 border border-amber-200'
                                            : 'bg-green-50 text-green-800 border border-green-200'
                                        }`}
                                      >
                                        Report: {isReport ? 'Yes' : 'No'}
                                      </span>
                                    </div>
                                    <div className="flex flex-wrap items-center gap-1">
                                      <span className="text-[11px] font-medium uppercase tracking-wide text-gray-400">
                                        Systems
                                      </span>
                                      {systemNames.length === 0 ? (
                                        <span className="text-xs text-gray-400">None</span>
                                      ) : (
                                        systemNames.map((name) => (
                                          <span
                                            key={name}
                                            className="inline-flex items-center px-2 py-0.5 text-xs font-medium bg-blue-50 text-blue-700 border border-blue-200 rounded"
                                          >
                                            {name}
                                          </span>
                                        ))
                                      )}
                                    </div>
                                  </div>
                                  <button
                                    type="button"
                                    onClick={() => setEditingDocumentKey(docKey)}
                                    className="shrink-0 p-1.5 text-gray-500 rounded-md hover:text-blue-600 hover:bg-blue-50 transition-colors"
                                    aria-label={`Edit ${doc.name || 'document'}`}
                                    title="Edit document"
                                  >
                                    <Edit2 className="h-4 w-4" />
                                  </button>
                                </div>
                              </div>
                            );
                          }

                          return (
                            <div
                              key={docKey}
                              className="rounded-lg border border-blue-200 bg-white px-3 py-2.5 space-y-2 shadow-sm"
                            >
                              <div className="flex items-center justify-between gap-2">
                                <span
                                  className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded ${
                                    isReport
                                      ? 'bg-amber-50 text-amber-900 border border-amber-200'
                                      : 'bg-green-50 text-green-800 border border-green-200'
                                  }`}
                                >
                                  {doc.name || 'Untitled'}
                                  <span
                                    role="button"
                                    tabIndex={0}
                                    onClick={() => {
                                      setEditForm((prev) => ({
                                        ...prev,
                                        documents: (prev.documents || []).filter((d, i) =>
                                          doc.id ? d.id !== doc.id : i !== index
                                        ),
                                      }));
                                      setEditingDocumentKey(null);
                                    }}
                                    onKeyDown={(e) => {
                                      if (e.key === 'Enter' || e.key === ' ') {
                                        e.preventDefault();
                                        setEditForm((prev) => ({
                                          ...prev,
                                          documents: (prev.documents || []).filter((d, i) =>
                                            doc.id ? d.id !== doc.id : i !== index
                                          ),
                                        }));
                                        setEditingDocumentKey(null);
                                      }
                                    }}
                                    className={`rounded p-0.5 inline-flex cursor-pointer ${
                                      isReport ? 'hover:bg-amber-100' : 'hover:bg-green-100'
                                    }`}
                                    aria-label={`Remove ${doc.name || 'document'}`}
                                  >
                                    <X className="h-3 w-3" />
                                  </span>
                                </span>
                                <button
                                  type="button"
                                  onClick={() => setEditingDocumentKey(null)}
                                  className="shrink-0 inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-blue-700 bg-blue-50 border border-blue-200 rounded hover:bg-blue-100"
                                  aria-label="Done editing document"
                                >
                                  <Check className="h-3.5 w-3.5" />
                                  Done
                                </button>
                              </div>

                              <label className="flex items-center space-x-2 cursor-pointer">
                                <input
                                  type="checkbox"
                                  checked={isReport}
                                  onChange={(e) => {
                                    const checked = e.target.checked;
                                    if (
                                      !confirmDocumentCatalogChange(
                                        doc.id,
                                        doc.name,
                                        checked ? 'mark as report' : 'unmark as report'
                                      )
                                    ) {
                                      return;
                                    }
                                    const nextDocType = checked ? 'report' : null;
                                    const nextSystems = Array.isArray(doc.systems) ? doc.systems : [];
                                    setEditForm((prev) => ({
                                      ...prev,
                                      documents: (prev.documents || []).map((d, i) => {
                                        const match = doc.id ? d.id === doc.id : i === index;
                                        if (!match) return d;
                                        return {
                                          ...d,
                                          docType: nextDocType,
                                        };
                                      }),
                                    }));
                                    if (doc.id) {
                                      applyDocumentCatalogChange(doc.id, {
                                        docType: nextDocType,
                                        systems: nextSystems,
                                      });
                                    }
                                  }}
                                  className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                                />
                                <span className="text-sm text-gray-700">
                                  Report{' '}
                                  <span className="text-gray-500">
                                    (applies to all nodes with this document)
                                  </span>
                                </span>
                              </label>

                              <div>
                                <label className="block text-xs text-gray-500 mb-1">
                                  Systems{' '}
                                  <span className="text-gray-400">
                                    (optional — applies to all nodes with this document)
                                  </span>
                                </label>
                                <SystemMultiSelect
                                  value={documentSystemsText(doc.systems)}
                                  onChange={(next) => {
                                    if (
                                      !confirmDocumentCatalogChange(
                                        doc.id,
                                        doc.name,
                                        'update system links'
                                      )
                                    ) {
                                      return;
                                    }
                                    const nextSystems = syncDocumentSystemsFromText(
                                      next,
                                      doc.systems || [],
                                      systemsCatalogByLowerName
                                    );
                                    const nextDocType = doc.docType === 'report' ? 'report' : null;
                                    setEditForm((prev) => ({
                                      ...prev,
                                      documents: (prev.documents || []).map((d, i) => {
                                        const match = doc.id ? d.id === doc.id : i === index;
                                        if (!match) return d;
                                        return {
                                          ...d,
                                          systems: nextSystems,
                                        };
                                      }),
                                    }));
                                    if (doc.id) {
                                      applyDocumentCatalogChange(doc.id, {
                                        docType: nextDocType,
                                        systems: nextSystems,
                                      });
                                    }
                                  }}
                                  onSystemCreated={registerSystemInCatalog}
                                />
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </div>

                {/* Buttons */}
                <div className="flex justify-end space-x-3 mt-6">
                  <button
                    onClick={handleCancelEdit}
                    className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSaveNode}
                    className="px-4 py-2 rounded-lg transition-colors bg-blue-600 text-white hover:bg-blue-700"
                  >
                    Save Changes
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Edge Edit Modal */}
          {isEdgeEditModalOpen && (
            <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
              <div className="relative bg-white rounded-lg shadow-2xl w-full max-w-md p-6">
                <button
                  onClick={handleCancelEdgeEdit}
                  className="absolute top-4 right-4 p-1 text-gray-500 rounded-md hover:text-gray-700 hover:bg-gray-100 transition-colors"
                  aria-label="Close connector confirmation"
                >
                  <X size={18} />
                </button>

                <h3 className="text-lg font-semibold text-gray-900 mb-4">
                  {editingEdge?.data?.isTempConnector ? 'Add Connector' : 'Edit Connector'}
                </h3>

                <div className="space-y-4">
                  {/* Label */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Label</label>
                    <input
                      type="text"
                      value={edgeEditForm.label}
                      onChange={(e) => {
                        setEdgeEditForm((prev) => ({ ...prev, label: e.target.value }));
                        setEdgeLabelError('');
                      }}
                      maxLength={CONNECTOR_LABEL_MAX_LENGTH}
                      className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${edgeLabelError ? 'border-red-500' : 'border-gray-300'
                        }`}
                      placeholder="Enter edge label (optional)"
                      autoFocus
                    />
                    {edgeLabelError && (
                      <p className="text-sm text-red-600 mt-1">{edgeLabelError}</p>
                    )}
                  </div>

                  <label className="flex items-center gap-2 cursor-pointer select-none">
                    <input
                      type="checkbox"
                      checked={edgeEditForm.isBidirectional === true}
                      onChange={(e) => {
                        setEdgeEditForm((prev) => ({ ...prev, isBidirectional: e.target.checked }));
                      }}
                      className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                    />
                    <span className="text-sm text-gray-700">Is bidirectional</span>
                  </label>
                </div>

                {/* Buttons */}
                <div className="flex justify-end space-x-3 mt-6">
                  <button
                    onClick={handleSaveEdgeLabel}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    Save Connector
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Swimlane Edit Modal */}
          {isSwimlaneEditModalOpen && (
            <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
              <div className="bg-white rounded-lg shadow-2xl w-full max-w-md p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Edit Swimlane</h3>

                <div className="space-y-4">
                  {/* Current Name */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Current Name</label>
                    <div className="px-3 py-2 bg-gray-100 rounded-lg text-gray-700">
                      {editingSwimlane?.label}
                    </div>
                  </div>

                  {/* New Name */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">New Name</label>
                    <input
                      type="text"
                      value={swimlaneEditForm.newActor}
                      onChange={(e) => {
                        setSwimlaneEditForm({ newActor: e.target.value });
                        setSwimlaneEditError('');
                      }}
                      maxLength={SWIMLANE_NAME_MAX_LENGTH}
                      className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${swimlaneEditError ? 'border-red-500' : 'border-gray-300'
                        }`}
                      placeholder="Enter new swimlane name"
                      autoFocus
                    />
                    {swimlaneEditError && (
                      <p className="text-sm text-red-600 mt-1">{swimlaneEditError}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Position</label>
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={() => handleMoveSwimlane('up')}
                        disabled={getSwimlaneOrderIndex(editingSwimlane?.oldActor) <= 0}
                        className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <ArrowUp className="h-4 w-4" />
                        Move up
                      </button>
                      <button
                        type="button"
                        onClick={() => handleMoveSwimlane('down')}
                        disabled={
                          getSwimlaneOrderIndex(editingSwimlane?.oldActor) < 0 ||
                          getSwimlaneOrderIndex(editingSwimlane?.oldActor) >=
                            Math.max(0, (swimlaneOrderRef.current || []).length - 1)
                        }
                        className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <ArrowDown className="h-4 w-4" />
                        Move down
                      </button>
                    </div>
                  </div>

                  <div className="text-sm text-gray-600 bg-blue-50 p-3 rounded-lg">
                    <strong>Note:</strong> Renaming updates the actor for all nodes in this swimlane.
                    Moving changes the vertical order. Deleting removes this swimlane and its nodes/connectors from this flowchart version.
                  </div>
                </div>

                {/* Buttons */}
                <div className="flex flex-wrap items-center justify-between gap-3 mt-6">
                  <button
                    type="button"
                    disabled
                    onClick={handleDeleteSwimlaneClick}
                    className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-red-700 bg-white border border-red-300 rounded-lg opacity-50 cursor-not-allowed"
                  >
                    <Trash2 className="h-4 w-4" />
                    Delete swimlane
                  </button>
                  <div className="flex items-center gap-3">
                    <button
                      onClick={handleCancelSwimlaneEdit}
                      className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleSaveSwimlaneEdit}
                      className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                    >
                      Save
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Swimlane Delete Confirmation Modal */}
          {swimlaneDeleteConfirmation && (
            <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
              <div className="bg-white rounded-lg shadow-2xl w-full max-w-md p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Delete Swimlane?</h3>
                <div className="space-y-3 mb-6">
                  <p className="text-gray-700">
                    Are you sure you want to delete swimlane{' '}
                    <strong>"{swimlaneDeleteConfirmation.label}"</strong> from this flowchart version?
                  </p>
                  {(swimlaneDeleteConfirmation.nodeCount > 0 || swimlaneDeleteConfirmation.edgeCount > 0) && (
                    <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                      <p className="text-sm text-yellow-800">
                        <AlertCircle className="inline h-4 w-4 mr-1" />
                        This will remove {swimlaneDeleteConfirmation.nodeCount} node
                        {swimlaneDeleteConfirmation.nodeCount === 1 ? '' : 's'}
                        {swimlaneDeleteConfirmation.edgeCount > 0
                          ? ` and ${swimlaneDeleteConfirmation.edgeCount} connected connector${
                              swimlaneDeleteConfirmation.edgeCount === 1 ? '' : 's'
                            }`
                          : ''}{' '}
                        from this version (soft-deleted on save).
                      </p>
                    </div>
                  )}
                </div>
                <div className="flex justify-end space-x-3">
                  <button
                    type="button"
                    onClick={cancelSwimlaneDelete}
                    className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={confirmSwimlaneDelete}
                    className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                  >
                    Delete
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* New Swimlane Modal */}
          {isNewSwimlaneModalOpen && (
            <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
              <div className="bg-white rounded-lg shadow-2xl w-full max-w-md p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Create New Swimlane</h3>

                <div className="space-y-4">
                  {/* Swimlane Name */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Swimlane Name (Actor)</label>
                    <input
                      type="text"
                      value={newSwimlaneForm.actorName}
                      onChange={(e) => {
                        setNewSwimlaneForm((prev) => ({ ...prev, actorName: e.target.value }));
                        setNewSwimlaneError('');
                      }}
                      maxLength={SWIMLANE_NAME_MAX_LENGTH}
                      className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 ${newSwimlaneError ? 'border-red-500' : 'border-gray-300'
                        }`}
                      placeholder="Enter swimlane name (e.g., Finance Team)"
                      autoFocus
                    />
                    {newSwimlaneError && (
                      <p className="text-sm text-red-600 mt-1">{newSwimlaneError}</p>
                    )}
                  </div>

                  {/* Optional placement — default stays "at the bottom" */}
                  {((swimlaneOrderRef.current || []).length > 0
                    || (Array.isArray(swimlaneBoundaries) && swimlaneBoundaries.length > 0)) && (
                    <div className="space-y-3">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Place swimlane</label>
                        <select
                          value={newSwimlaneForm.placement || 'bottom'}
                          onChange={(e) => {
                            const nextPlacement = e.target.value;
                            setNewSwimlaneForm((prev) => ({
                              ...prev,
                              placement: nextPlacement,
                              relativeTo: nextPlacement === 'bottom' ? '' : prev.relativeTo,
                            }));
                            setNewSwimlaneError('');
                          }}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                        >
                          <option value="bottom">At the bottom (default)</option>
                          <option value="before">Before an existing swimlane</option>
                          <option value="after">After an existing swimlane</option>
                        </select>
                      </div>

                      {(newSwimlaneForm.placement === 'before' || newSwimlaneForm.placement === 'after') && (
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            {newSwimlaneForm.placement === 'before' ? 'Before swimlane' : 'After swimlane'}
                          </label>
                          <select
                            value={newSwimlaneForm.relativeTo || ''}
                            onChange={(e) => {
                              setNewSwimlaneForm((prev) => ({ ...prev, relativeTo: e.target.value }));
                              setNewSwimlaneError('');
                            }}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                          >
                            <option value="">Select swimlane</option>
                            {(
                              (swimlaneOrderRef.current || []).length > 0
                                ? swimlaneOrderRef.current
                                : (swimlaneBoundaries || [])
                                  .map((boundary) => String(boundary?.actors?.[0] || boundary?.label || '').trim())
                                  .filter(Boolean)
                            ).map((actor) => (
                              <option key={actor} value={actor}>{actor}</option>
                            ))}
                          </select>
                        </div>
                      )}
                    </div>
                  )}

                  <div className="text-sm text-gray-600 bg-purple-50 p-3 rounded-lg">
                    <strong>Note:</strong> A placeholder node will be created in the new swimlane. You can edit or delete it later.
                  </div>
                </div>

                {/* Buttons */}
                <div className="flex justify-end space-x-3 mt-6">
                  <button
                    onClick={handleCancelNewSwimlane}
                    className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleCreateNewSwimlane}
                    className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
                  >
                    Create
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
      <ModuleAccessDeniedDialog {...risksModuleAccessDialogProps} />
    </TooltipProvider>
  );
}

export default FlowchartViewer;
